import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { ComponentType, MouseEvent, Dispatch, SetStateAction } from 'react';
import {
  RefreshCw,
  Search,
  Download,
  Plus,
  Settings,
  ExternalLink,
  Linkedin,
  Facebook,
  Instagram,
  Youtube,
  Twitter,
} from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import {
  Header,
  DataDrawer,
  CrunchbaseContent,
  LinkedIn360Content,
  Glassdoor360Content,
  G2MultiContent,
  TrustpilotOnlyContent,
  InstagramContent,
  FacebookContent,
  YouTubeContent,
  TikTokContent,
  XContent,
  ThreadsContent,
} from '@/components';
import { deductCredits, getCredits } from '@/services/credits';
import { loadCompaniesServerSide } from '@/services/contacts';
import { FEATURE_FLAGS } from '@/services/api';
import { getPepCompanyProfile, getPepProfile, savePepCompanyProfile, type PepProfile } from '@/services/pepProfile';
import { useResearchStore } from '@/stores/researchStore';
import { API_CONFIG } from '@/services/api';
import { startCrunchbaseScrape, pollCrunchbaseResults, startLinkedIn360Scrape, pollLinkedIn360Results, startGlassdoorScrape, pollGlassdoorResults, startG2Scrape, pollG2Results, startTrustpilotScrape, pollTrustpilotResults } from '@/services/scraping';
import { researchContact, searchLeads, syncExistingContactsToCrm } from '@/services/research';
import { getAccountSettings, setGhlSendAsLocation } from '@/services/account';
import type { Lead } from '@/types/research';
import { List, type RowComponentProps } from 'react-window';

const RESEARCH_API_URL = API_CONFIG.RESEARCH_API;
const LIST_ENTITY_TYPE = 'company';
const DEFAULT_LIST_STORAGE_KEY = 'profitibull_default_company_list';
const PEPAI_CAMPAIGN_STORAGE_KEY = 'profitibull_pepai_campaign_id';
const SEND_TO_CRM_STORAGE_KEY = 'profitibull_mydata_companies_send_to_crm';

const ECOM_ORCHESTRATOR_API = API_CONFIG.ECOM_ORCHESTRATOR_API;

type Contact = {
  searchResultId: string;
  contactSearchId?: number;
  uiMatchId?: string;
  fullName?: string;
  Name?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  Title?: string;
  company?: string;
  Company?: string;
  email?: string;
  Email?: string;
  email1?: string;
  email2?: string;
  email3?: string;
  personalEmail?: string;
  PersonalEmail?: string;
  personalEmail1?: string;
  contactPhone1?: string;
  contactMobilePhone1?: string;
  companyPhone1?: string;
  linkedIn?: string;
  LIProfileUrl?: string;
  CompanyLIProfileUrl?: string;
  researchedAt?: string;
  lists?: string[] | string;
  ContactLocation?: string | Record<string, unknown> | Array<unknown>;
  CompanyLocation?: string | Record<string, unknown> | Array<unknown>;
  CompanyIndustry?: string | Record<string, unknown> | Array<unknown>;
  Industry?: string | Record<string, unknown> | Array<unknown>;
  Website?: string;
  CompanyDomain?: string;
};

type CompanyRow = {
  companyName: string;
  contactsCount: number;
  lastResearchedAt?: string;
  sampleDomain?: string;
  location?: string;
  industry?: string;
  contacts: Contact[];
  fallbackLinkedIn?: string;
};

const decodeAmp = (value: string) => value.replace(/&amp;/g, '&');

const COMPANY_PEP_SYSTEM_DEFAULT = {
  brandVoice: 'Strategic, direct, insight-led. Keep language specific and grounded in observable signals.',
  icpPersona: 'Revenue leaders and GTM operators in B2B organizations.',
  offersProducts: 'Simply Scrapable: signal collection, enrichment, and actionable GTM intelligence.',
  languageDos: 'Be concise. Reference real signals. Connect message to measurable business impact.',
  languageDonts: 'No generic hype, no fabricated claims, no overpromising outcomes.',
  channelConstraints: 'Email-first. Keep copy compliant, professional, and personalized.',
  sequencePolicy: 'Generate adaptive next-best actions based on latest signals, not fixed scripts.',
  touchpointRules: 'Produce first 5 recommended touchpoints with channel, action, angle, and wait_days.',
  approvalMode: 'manual',
};

type CompanyEntity = {
  domain?: string;
  website_url?: string;
  website_ds?: string[];
  website_scraped_at?: string;
  linkedin_url?: string;
  linkedin_profile_ds?: string[];
  linkedin_posts_ds?: string[];
  linkedin_jobs_ds?: string[];
  linkedin_employees_ds?: string[];
  linkedin_scraped_at?: string;
  crunchbase_ds?: string[];
  glassdoor_scraped_at?: string;
  glassdoor_overview_ds?: string[];
  glassdoor_reviews_ds?: string[];
  glassdoor_salaries_ds?: string[];
  glassdoor_interviews_ds?: string[];
  glassdoor_benefits_ds?: string[];
  instagram_ds?: string[];
  instagram_scraped_at?: string;
  facebook_ds?: string[];
  facebook_scraped_at?: string;
  youtube_ds?: string[];
  youtube_scraped_at?: string;
  tiktok_ds?: string[];
  tiktok_scraped_at?: string;
  x_ds?: string[];
  x_scraped_at?: string;
  threads_ds?: string[];
  threads_scraped_at?: string;
  g2_ds?: string[];
  g2_scraped_at?: string;
  trustpilot_ds?: string[];
  trustpilot_scraped_at?: string;
  crunchbase_scraped_at?: string;
  glassdoor_url?: string;
  g2_url?: string;
  trustpilot_url?: string;
  instagram_url?: string;
  facebook_url?: string;
  youtube_url?: string;
  tiktok_url?: string;
  x_url?: string;
  threads_url?: string;
  podcast_url?: string;
  socials_last_scraped_at?: string;
  custom_kpi?: {
    indicator?: number;
    criteria?: Record<string, { score: number; reasoning: string }>;
    description?: string;
    description_website?: string;
    employees?: number;
    founded?: number;
    traffic?: number;
    revenue_annual?: number;
    funding_stage?: string;
  };
  custom_kpi_scraped_at?: string;
};

type SocialPlatform =
  | 'linkedin'
  | 'glassdoor'
  | 'g2'
  | 'trustpilot'
  | 'instagram'
  | 'facebook'
  | 'youtube'
  | 'tiktok'
  | 'x'
  | 'threads';

const socialLabels: Record<SocialPlatform, string> = {
  linkedin: 'LinkedIn 360',
  glassdoor: 'Glassdoor 360',
  g2: 'G2 Page',
  trustpilot: 'Trustpilot Page',
  instagram: 'Instagram Page',
  facebook: 'Facebook Page',
  youtube: 'YouTube Page',
  tiktok: 'TikTok Page',
  x: 'X Page',
  threads: 'Threads Page',
};

const socialField: Record<SocialPlatform, keyof CompanyEntity> = {
  linkedin: 'linkedin_url',
  glassdoor: 'glassdoor_url',
  g2: 'g2_url',
  trustpilot: 'trustpilot_url',
  instagram: 'instagram_url',
  facebook: 'facebook_url',
  youtube: 'youtube_url',
  tiktok: 'tiktok_url',
  x: 'x_url',
  threads: 'threads_url',
};

const normalizeWebsiteUrl = (input: string): string => {
  const trimmed = (input || '').trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const normalizePlatformUrl = (input: string): string => {
  const trimmed = (input || '').trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const isPlatformUrl = (platform: 'facebook' | 'instagram' | 'youtube' | 'tiktok' | 'x' | 'threads', value: string) => {
  if (!value) return false;
  const v = value.toLowerCase();
  if (platform === 'facebook') return v.includes('facebook.com');
  if (platform === 'instagram') return v.includes('instagram.com');
  if (platform === 'youtube') return v.includes('youtube.com') || v.includes('youtu.be');
  if (platform === 'tiktok') return v.includes('tiktok.com');
  if (platform === 'x') return v.includes('x.com') || v.includes('twitter.com');
  if (platform === 'threads') return v.includes('threads.net');
  return false;
};

const extractWebsiteSocialLinks = (data: unknown): Partial<Record<SocialPlatform, string>> => {
  const collectPages = (value: unknown): any[] => {
    if (Array.isArray(value)) return value;
    if (!value || typeof value !== 'object') return [];
    const obj = value as Record<string, unknown>;
    const directKeys = ['pages', 'results', 'items'];
    for (const key of directKeys) {
      if (Array.isArray(obj[key])) return obj[key] as any[];
    }
    if (Array.isArray(obj.data)) return obj.data as any[];
    if (obj.data && typeof obj.data === 'object') {
      const dataObj = obj.data as Record<string, unknown>;
      for (const key of directKeys) {
        if (Array.isArray(dataObj[key])) return dataObj[key] as any[];
      }
    }
    if (Array.isArray(obj.payload)) return obj.payload as any[];
    if (obj.payload && typeof obj.payload === 'object') {
      const payloadObj = obj.payload as Record<string, unknown>;
      for (const key of directKeys) {
        if (Array.isArray(payloadObj[key])) return payloadObj[key] as any[];
      }
    }
    return [obj];
  };

  const pages = collectPages(data);
  const urls = new Set<string>();

  const normalizeUrl = (value: string) => {
    const trimmed = value.trim().replace(/[),.;]+$/g, '');
    if (!trimmed) return '';
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  };
  const addUrl = (value: unknown) => {
    if (typeof value !== 'string') return;
    const normalized = normalizeUrl(value);
    if (normalized) urls.add(normalized);
  };
  const walk = (value: unknown) => {
    if (typeof value === 'string') {
      const decoded = value.replace(/\\\//g, '/');
      const httpMatches = decoded.match(/https?:\/\/[^\s"'<>]+/gi) || [];
      const bareMatches =
        decoded.match(
          /\b(?:www\.)?(?:linkedin\.com|facebook\.com|instagram\.com|tiktok\.com|youtube\.com|youtu\.be|x\.com|twitter\.com|threads\.net|podcasts\.apple\.com|open\.spotify\.com|podbean\.com|anchor\.fm)\/[^\s"'<>]+/gi
        ) || [];
      [...httpMatches, ...bareMatches].forEach(addUrl);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }
    if (value && typeof value === 'object') {
      Object.values(value as Record<string, unknown>).forEach(walk);
    }
  };
  const addMany = (value: unknown) => {
    if (Array.isArray(value)) {
      value.forEach(addUrl);
      return;
    }
    addUrl(value);
  };

  for (const page of pages as any[]) {
    const meta = page?.metadata || {};
    const jsonLd = Array.isArray(meta?.jsonLd) ? meta.jsonLd : [];
    const graph = jsonLd
      .flatMap((node: any) => (node && node['@graph'] ? node['@graph'] : node))
      .filter(Boolean);
    for (const node of graph) {
      const sameAs = Array.isArray((node as any)?.sameAs) ? (node as any).sameAs : [];
      sameAs.forEach(addUrl);
      const brand = (node as any)?.brand;
      if (brand && typeof brand === 'object') {
        const brandSameAs = Array.isArray((brand as any)?.sameAs) ? (brand as any).sameAs : [];
        brandSameAs.forEach(addUrl);
      }
    }
    walk(page);
    walk(meta?.links);
    // Last-resort extraction for deeply nested or escaped JSON-LD fields.
    try {
      walk(JSON.stringify(page));
    } catch {
      // ignore serialization errors
    }

    if (Array.isArray(page?.links)) {
      page.links.forEach((link: any) => {
        addUrl(typeof link === 'string' ? link : link?.url || link?.href);
      });
    }
    if (Array.isArray(meta?.links)) {
      meta.links.forEach((link: any) => {
        addUrl(typeof link === 'string' ? link : link?.url || link?.href);
      });
    }
    if (Array.isArray(page?.socials)) {
      page.socials.forEach(addUrl);
    }
    if (page?.socials && typeof page.socials === 'object') {
      Object.values(page.socials).forEach((v: any) => {
        if (Array.isArray(v)) v.forEach(addUrl);
        else addUrl(v);
      });
    }

    addMany(page?.linkedins);
    addMany(page?.twitters);
    addMany(page?.instagrams);
    addMany(page?.facebooks);
    addMany(page?.youtubes);
    addMany(page?.tiktoks);
    addMany(page?.threads);
    addMany(page?.podcasts);
  }

  const findMatch = (predicate: (url: string) => boolean) => {
    for (const url of urls) {
      const lower = url.toLowerCase();
      if (predicate(lower)) return url;
    }
    return '';
  };
  const findPreferred = (preferred: (url: string) => boolean, fallback: (url: string) => boolean) => {
    const preferredHit = findMatch(preferred);
    if (preferredHit) return preferredHit;
    return findMatch(fallback);
  };

  return {
    linkedin: findMatch((u) => u.includes('linkedin.com/company/')),
    facebook: findPreferred(
      (u) => u.includes('facebook.com') && !u.includes('/posts/') && !u.includes('/photo') && !u.includes('/videos/'),
      (u) => u.includes('facebook.com')
    ),
    instagram: findPreferred(
      (u) =>
        u.includes('instagram.com') &&
        !u.includes('/p/') &&
        !u.includes('/reel/') &&
        !u.includes('/stories/') &&
        !u.includes('scontent-') &&
        !u.includes('cdninstagram'),
      (u) => u.includes('instagram.com')
    ),
    tiktok: findMatch((u) => u.includes('tiktok.com') && !u.includes('/video/')),
    youtube: findPreferred(
      (u) => u.includes('youtube.com/') && (u.includes('/channel/') || u.includes('/user/') || u.includes('/@') || u.includes('/c/')),
      (u) => u.includes('youtube.com') || u.includes('youtu.be')
    ),
    x: findPreferred(
      (u) => (u.includes('x.com') || u.includes('twitter.com')) && !u.includes('/status/'),
      (u) => u.includes('x.com') || u.includes('twitter.com')
    ),
    threads: findMatch((u) => u.includes('threads.net')),
    trustpilot: findMatch((u) => u.includes('trustpilot.com')),
    g2: findMatch((u) => u.includes('g2.com')),
    glassdoor: findMatch((u) => u.includes('glassdoor.com')),
  };
};

const companySocialIconByPlatform: Partial<Record<SocialPlatform, ComponentType<{ className?: string }>>> = {
  linkedin: Linkedin,
  facebook: Facebook,
  instagram: Instagram,
  youtube: Youtube,
  x: Twitter,
};

const companySocialImageByPlatform: Partial<Record<SocialPlatform, { src: string; alt: string }>> = {
  glassdoor: {
    src: 'https://storage.googleapis.com/msgsndr/ceLRhUdWIdfZ8UcJopVc/media/6988e2877f6dcf76f25b54f2.png',
    alt: 'Glassdoor',
  },
  trustpilot: {
    src: 'https://storage.googleapis.com/msgsndr/ceLRhUdWIdfZ8UcJopVc/media/6988e33f0a7fd16da83481cd.png',
    alt: 'Trustpilot',
  },
  g2: {
    src: 'https://storage.googleapis.com/msgsndr/ceLRhUdWIdfZ8UcJopVc/media/6988e3777f6dcfab165b7cdc.png',
    alt: 'G2',
  },
  tiktok: {
    src: 'https://storage.googleapis.com/msgsndr/ceLRhUdWIdfZ8UcJopVc/media/698a09847305aaafa7f897bc.png',
    alt: 'TikTok',
  },
  threads: {
    src: 'https://storage.googleapis.com/msgsndr/ceLRhUdWIdfZ8UcJopVc/media/698a0ab57305aab63df8de8e.png',
    alt: 'Threads',
  },
};

const renderCompanySocialButton = (
  platform: SocialPlatform,
  url: string | undefined,
  hasReport: boolean,
  onClick: () => void
) => {
  const Icon = companySocialIconByPlatform[platform];
  const image = companySocialImageByPlatform[platform];
  const isActive = Boolean(url || hasReport);
  const iconClass = `w-5 h-5 min-w-5 min-h-5 ${isActive ? 'text-blue-400' : 'text-gray-600'}`;
  const imgClass = `w-8 h-8 min-w-8 min-h-8 object-contain block ${isActive ? '' : 'grayscale opacity-40'}`;
  return (
    <button
      onClick={onClick}
      className="text-blue-400 p-0 leading-none"
      title={`${isActive ? 'View' : 'Add or find'} ${socialLabels[platform]}`}
    >
      {Icon ? <Icon className={iconClass} /> : image ? <img src={image.src} className={imgClass} alt={image.alt} /> : <ExternalLink className={iconClass} />}
    </button>
  );
};

export function MyListsCompanies() {
  const { userEmail, credits, setCredits, isAuthenticated, addScrapeJob, updateScrapeJob, isAdmin, adminToken } = useAppStore();
  const { saveResearchedContact } = useResearchStore();

  const [contacts, _setContacts] = useState<Contact[]>([]);
  const [contactsStable] = useState<Contact[]>([]);
  const [userLists, setUserLists] = useState<string[]>([]);
  const [defaultList, setDefaultList] = useState('');
  const [loading, setLoading] = useState(false);
  const [drawerRerunLoading, setDrawerRerunLoading] = useState<Record<string, boolean>>({});
  const [companySearchLoading, setCompanySearchLoading] = useState<Record<string, boolean>>({});
  const [companySearchResults, setCompanySearchResults] = useState<Record<string, { results: Lead[]; total: number }>>({});
  const [companySearchResearchLoading, setCompanySearchResearchLoading] = useState<Record<string, boolean>>({});

  const [searchTerm, setSearchTerm] = useState('');
  const [companiesFromServer, setCompaniesFromServer] = useState<CompanyRow[]>([]);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [currentListFilter, setCurrentListFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [perPage] = useState(25);
  const [sortKeyCompanies, setSortKeyCompanies] = useState<'company_asc' | 'company_desc' | 'contacts_desc' | 'contacts_asc' | 'recent_desc' | 'recent_asc'>('company_asc');

  const [createListModal, setCreateListModal] = useState(false);
  const [manageListsModal, setManageListsModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [loadingMore] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [sendToCrm, setSendToCrm] = useState(
    () => window.localStorage.getItem(SEND_TO_CRM_STORAGE_KEY) === 'true'
  );
  const [crmSyncLoading, setCrmSyncLoading] = useState(false);
  const [ghlSendAsLocationId, setGhlSendAsLocationId] = useState('');
  const [ghlTargetOptions, setGhlTargetOptions] = useState<Array<{ locationId: string; label: string }>>([]);
  const [ghlSendAsSaving, setGhlSendAsSaving] = useState(false);
  const canChooseGhlTarget = isAdmin || userEmail.toLowerCase().endsWith('@profitibull.com');
  const targetGhlLocationId = useMemo(() => {
    if (!canChooseGhlTarget) return undefined;
    const value = String(ghlSendAsLocationId || '').trim();
    if (!value) return undefined;
    return ghlTargetOptions.some((option) => option.locationId === value) ? value : undefined;
  }, [canChooseGhlTarget, ghlSendAsLocationId, ghlTargetOptions]);

  const [companyEntities, setCompanyEntities] = useState<Record<string, CompanyEntity>>({});
  const [findingCompanyUrls, setFindingCompanyUrls] = useState<Record<string, boolean>>({});
  const [scrapingWebsite, setScrapingWebsite] = useState<Record<string, boolean>>({});
  const [, setSocialSearching] = useState<Record<string, boolean>>({});
  const [editCompanyUrl, setEditCompanyUrl] = useState<{ companyName: string; current?: string } | null>(null);
  const [editCompanyUrlValue, setEditCompanyUrlValue] = useState('');
  const [editCompanyLink, setEditCompanyLink] = useState<{ companyName: string; field: string; label: string; current?: string } | null>(null);
  const [editCompanyLinkValue, setEditCompanyLinkValue] = useState('');

  const [companyDrawer, setCompanyDrawer] = useState<CompanyRow | null>(null);
  const [companyModalTab, setCompanyModalTab] = useState<'ov' | 'ppl' | 'sig' | 'li' | 'gd' | 'cb' | 'rev' | 'soc' | 'tech' | 'act'>('ov');
  const [companySocialTab, setCompanySocialTab] = useState<'ig' | 'fb' | 'yt' | 'tt' | 'x' | 'threads'>('ig');
  const [peopleDeptTab, setPeopleDeptTab] = useState<string>('Org');
  const [peopleTierOpen, setPeopleTierOpen] = useState<Record<string, boolean>>({
    'C-Level': true,
    'VPs': true,
    'Directors': true,
    'Managers': false,
  });
  const [companyModalTheme, setCompanyModalTheme] = useState<'dark' | 'light'>('dark');
  const [socialDrawer, setSocialDrawer] = useState<{ companyName: string; platform: SocialPlatform } | null>(null);
  const [websiteDrawer, setWebsiteDrawer] = useState<{ companyName: string; data: unknown; scrapedAt?: string } | null>(null);
  const [websiteActions, setWebsiteActions] = useState<{ companyName: string } | null>(null);
  const [websiteCache, setWebsiteCache] = useState<Record<string, { data: unknown; scrapedAt?: string }>>({});
  const [crunchbaseDrawer, setCrunchbaseDrawer] = useState<{ companyName: string; data: unknown; scrapedAt?: string } | null>(null);
  const [crunchbaseCache, setCrunchbaseCache] = useState<Record<string, { data: unknown; scrapedAt?: string }>>({});
  const [linkedinDrawer, setLinkedinDrawer] = useState<{ companyName: string; data: unknown; scrapedAt?: string } | null>(null);
  const [linkedinCache, setLinkedinCache] = useState<Record<string, { data: unknown; scrapedAt?: string }>>({});
  const [linkedinLoading, setLinkedinLoading] = useState<Record<string, boolean>>({});
  const [glassdoorDrawer, setGlassdoorDrawer] = useState<{ companyName: string; data: unknown; scrapedAt?: string } | null>(null);
  const [glassdoorCache, setGlassdoorCache] = useState<Record<string, { data: unknown; scrapedAt?: string }>>({});
  const [glassdoorLoading, setGlassdoorLoading] = useState<Record<string, boolean>>({});
  const [g2Drawer, setG2Drawer] = useState<{ companyName: string; data: unknown; scrapedAt?: string } | null>(null);
  const [g2Cache, setG2Cache] = useState<Record<string, { data: unknown; scrapedAt?: string }>>({});
  const [g2Loading, setG2Loading] = useState<Record<string, boolean>>({});
  const [trustpilotDrawer, setTrustpilotDrawer] = useState<{ companyName: string; data: unknown; scrapedAt?: string } | null>(null);
  const [trustpilotCache, setTrustpilotCache] = useState<Record<string, { data: unknown; scrapedAt?: string }>>({});
  const [trustpilotLoading, setTrustpilotLoading] = useState<Record<string, boolean>>({});
  const [instagramDrawer, setInstagramDrawer] = useState<{ companyName: string; data: unknown; scrapedAt?: string } | null>(null);
  const [instagramCache, setInstagramCache] = useState<Record<string, { data: unknown; scrapedAt?: string }>>({});
  const [instagramLoading, setInstagramLoading] = useState<Record<string, boolean>>({});
  const [facebookDrawer, setFacebookDrawer] = useState<{ companyName: string; data: unknown; scrapedAt?: string } | null>(null);
  const [facebookCache, setFacebookCache] = useState<Record<string, { data: unknown; scrapedAt?: string }>>({});
  const [facebookLoading, setFacebookLoading] = useState<Record<string, boolean>>({});
  const [youtubeDrawer, setYoutubeDrawer] = useState<{ companyName: string; data: unknown; scrapedAt?: string } | null>(null);
  const [youtubeCache, setYoutubeCache] = useState<Record<string, { data: unknown; scrapedAt?: string }>>({});
  const [youtubeLoading, setYoutubeLoading] = useState<Record<string, boolean>>({});
  const [tiktokDrawer, setTiktokDrawer] = useState<{ companyName: string; data: unknown; scrapedAt?: string } | null>(null);
  const [tiktokCache, setTiktokCache] = useState<Record<string, { data: unknown; scrapedAt?: string }>>({});
  const [tiktokLoading, setTiktokLoading] = useState<Record<string, boolean>>({});
  const [xDrawer, setXDrawer] = useState<{ companyName: string; data: unknown; scrapedAt?: string } | null>(null);
  const [xCache, setXCache] = useState<Record<string, { data: unknown; scrapedAt?: string }>>({});
  const [xLoading, setXLoading] = useState<Record<string, boolean>>({});
  const [threadsDrawer, setThreadsDrawer] = useState<{ companyName: string; data: unknown; scrapedAt?: string } | null>(null);
  const [threadsCache, setThreadsCache] = useState<Record<string, { data: unknown; scrapedAt?: string }>>({});
  const [threadsLoading, setThreadsLoading] = useState<Record<string, boolean>>({});
  const [crunchbaseLoading, setCrunchbaseLoading] = useState<Record<string, boolean>>({});
  const [buyIntentModal, setBuyIntentModal] = useState<{ companyName: string } | null>(null);
  const [buyIntentName, setBuyIntentName] = useState('');
  const [buyIntentEmail, setBuyIntentEmail] = useState('');
  const [buyIntentKeywords, setBuyIntentKeywords] = useState(['', '', '']);
  const [buyIntentSubmitting, setBuyIntentSubmitting] = useState(false);
  const [buyIntentStatus, setBuyIntentStatus] = useState<string | null>(null);
  const [pepPolicyProfile, setPepPolicyProfile] = useState<PepProfile | null>(null);
  const [pepPolicyLocked, setPepPolicyLocked] = useState(false);
  const [pepPolicyExpanded, setPepPolicyExpanded] = useState(false);
  const [pepCompanyPolicyProfile, setPepCompanyPolicyProfile] = useState<PepProfile | null>(null);
  const [pepCompanyPolicyDraft, setPepCompanyPolicyDraft] = useState<Partial<PepProfile>>({});
  const [pepCompanyPolicySaving, setPepCompanyPolicySaving] = useState(false);
  const goToProfileForms = () => {
    window.location.href = '/command-center';
  };


  const summarizeInstagramData = (data: unknown): {
    username?: string;
    fullName?: string;
    bio?: string;
    website?: string;
    followers?: number | string;
    following?: number | string;
    postsCount?: number | string;
    profileUrl?: string;
    avatar?: string;
    isVerified?: boolean;
    recentPosts: Array<{
      caption?: string;
      url?: string;
      likes?: number | string;
      comments?: number | string;
      date?: string;
      image?: string;
      video?: string;
    }>;
  } => {
    const payload = data && typeof data === 'object' ? (data as Record<string, any>) : undefined;
    const arrayData = Array.isArray(data) ? data : [];
    const items = Array.isArray(payload?.results)
      ? payload.results
      : arrayData.length > 0 && Array.isArray((arrayData[0] as any)?.results)
        ? (arrayData[0] as any).results
        : Array.isArray(data)
          ? data
          : data && typeof data === 'object'
            ? [data]
            : [];
    const getStr = (v: any) => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
    const first = (items[0] as Record<string, any>) || {};
    const profile = first;

    const username = getStr(profile.username || profile.userName || profile.handle);
    const fullName = getStr(profile.fullName || profile.name || profile.nickName);
    const bio = getStr(profile.biography || profile.bio);
    const website = getStr(profile.externalUrl || profile.externalUrlShimmed || profile.website);
    const followers = profile.followersCount ?? profile.followers ?? undefined;
    const following = profile.followsCount ?? profile.following ?? undefined;
    const postsCount = profile.postsCount ?? profile.posts ?? profile.igtvVideoCount ?? undefined;
    const profileUrl = getStr(profile.url || profile._discovered_profile_url || profile.inputUrl);
    const avatar = getStr(profile.profilePicUrlHD || profile.profilePicUrl || profile.avatar || profile.profilePicture);
    const isVerified = Boolean(profile.verified || profile.isVerified);

    const postSource = Array.isArray(profile.latestPosts) && profile.latestPosts.length > 0
      ? profile.latestPosts
      : Array.isArray(profile.latestIgtvVideos) && profile.latestIgtvVideos.length > 0
        ? profile.latestIgtvVideos
        : [];
    const recentPosts = postSource.slice(0, 10).map((p: any) => ({
      caption: getStr(p.caption || p.text || p.description),
      url: getStr(p.url || p.postUrl),
      likes: p.likesCount ?? p.likes ?? p.likeCount ?? undefined,
      comments: p.commentsCount ?? p.comments ?? p.commentCount ?? undefined,
      date: getStr(p.timestamp || p.date),
      image: Array.isArray(p.images) ? p.images[0] : p.displayUrl || p.imageUrl || p.thumbnail,
      video: p.videoUrl || p.video,
    }));

    return {
      username,
      fullName,
      bio,
      website,
      followers,
      following,
      postsCount,
      profileUrl,
      avatar,
      isVerified,
      recentPosts,
    };
  };

  const summarizeFacebookData = (data: unknown): {
    title?: string;
    pageUrl?: string;
    likes?: number | string;
    followers?: number | string;
    category?: string;
    phone?: string;
    website?: string;
    address?: string;
    intro?: string;
    profilePictureUrl?: string;
    coverPhotoUrl?: string;
    posts: Array<{
      date?: string;
      text?: string;
      url?: string;
      likes?: number | string;
      comments?: number | string;
      shares?: number | string;
      views?: number | string;
      image?: string;
      video?: string;
    }>;
  } => {
    const payload = (data && typeof data === 'object') ? (data as Record<string, any>) : {};
    const items = Array.isArray(data)
      ? data
      : Array.isArray(payload.items)
        ? payload.items
        : [];
    const pageArray = Array.isArray(payload.page) ? payload.page : [];
    const postsArray = Array.isArray(payload.posts) ? payload.posts : [];
    const getStr = (v: any) => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
    const pageItem =
      pageArray[0] ||
      items.find((i: any) => i && (i.pageName || i.pageUrl || i.title || i.facebookUrl));
    const postItems =
      postsArray.length > 0
        ? postsArray
        : items.filter((i: any) => i && (i.post_id || i.post_url || i.text));
    const page = (pageItem as Record<string, any>) || {};

    const title = getStr(page.pageName || page.title || page.facebookUrl);
    const pageUrl = getStr(page.pageUrl || page.facebookUrl);
    const likes = page.likes ?? undefined;
    const followers = page.followers ?? undefined;
    const category = getStr(page.category || (Array.isArray(page.categories) ? page.categories.join(', ') : ''));
    const phone = getStr(page.phone);
    const website = getStr(page.website);
    const address = getStr(page.address);
    const intro = getStr(page.intro);
    const profilePictureUrl = getStr(page.profilePictureUrl);
    const coverPhotoUrl = getStr(page.coverPhotoUrl);

    const posts = postItems.slice(0, 50).map((p: any) => ({
      date: p.create_time ? new Date(p.create_time).toISOString() : p.created_time || p.date || '',
      text: getStr(
        p.text ||
        p.message ||
        p.story ||
        p.caption ||
        p.title ||
        p.description ||
        p.postText
      ),
      url: getStr(p.post_url || p.postUrl || p.input || p.url),
      likes: p.like_count ?? p.likes ?? undefined,
      comments: p.comment_count ?? p.comments ?? undefined,
      shares: p.share_count ?? p.shares ?? undefined,
      views: p.view_count ?? p.play_count ?? undefined,
      image: Array.isArray(p.image_list) ? p.image_list[0] : undefined,
      video: Array.isArray(p.video_list) ? p.video_list[0] : undefined,
    }));

    return {
      title,
      pageUrl,
      likes,
      followers,
      category,
      phone,
      website,
      address,
      intro,
      profilePictureUrl,
      coverPhotoUrl,
      posts,
    };
  };

  const summarizeYouTubeData = (data: unknown): {
    channelName?: string;
    channelUrl?: string;
    channelAvatar?: string;
    subscribers?: number | string;
    totalViews?: number | string;
    totalVideos?: number | string;
    channelLocation?: string;
    recentVideos: Array<{
      title?: string;
      url?: string;
      date?: string;
      views?: number | string;
      likes?: number | string;
      comments?: number | string;
      thumbnail?: string;
      text?: string;
    }>;
  } => {
    const payload = data && typeof data === 'object' ? (data as Record<string, any>) : undefined;
    const items = Array.isArray(data)
      ? data
      : Array.isArray(payload?.results)
        ? payload?.results
        : Array.isArray(payload?.items)
          ? payload?.items
          : [];
    const cleaned = items.filter((item: any) => {
      const note = String(item?.note || '').toLowerCase();
      const error = String(item?.error || '');
      return error !== 'NO_RESULTS' && !note.includes('no results were collected');
    });
    const first = (cleaned[0] as Record<string, any>) || {};
    const about = first.aboutChannelInfo || {};
    const channelName = about.channelName || first.channelName;
    const channelUrl = about.channelUrl || first.channelUrl;
    const channelAvatar = about.channelAvatarUrl || first.channelAvatarUrl;
    const subscribers = about.numberOfSubscribers || first.numberOfSubscribers;
    const totalViews = about.channelTotalViews || first.channelTotalViews;
    const totalVideos = about.channelTotalVideos || first.channelTotalVideos;
    const channelLocation = about.channelLocation || first.channelLocation;
    const recentVideos = cleaned.slice(0, 50).map((v: any) => ({
      title: v.title,
      url: v.url,
      date: v.date,
      views: v.viewCount,
      likes: v.likes,
      comments: v.commentsCount,
      thumbnail: v.thumbnailUrl,
      text: v.text,
    }));
    return {
      channelName,
      channelUrl,
      channelAvatar,
      subscribers,
      totalViews,
      totalVideos,
      channelLocation,
      recentVideos,
    };
  };

  const summarizeTikTokData = (data: unknown): {
    username?: string;
    fullName?: string;
    bio?: string;
    website?: string;
    followers?: number | string;
    following?: number | string;
    videos?: number | string;
    hearts?: number | string;
    profileUrl?: string;
    avatar?: string;
    isVerified?: boolean;
    recentPosts: Array<{
      caption?: string;
      url?: string;
      likes?: number | string;
      comments?: number | string;
      shares?: number | string;
      views?: number | string;
      date?: string;
      image?: string;
      duration?: number | string;
    }>;
  } => {
    const items = Array.isArray(data)
      ? data
      : data && typeof data === 'object' && Array.isArray((data as any).items)
        ? (data as any).items
        : data && typeof data === 'object'
          ? [data]
          : [];
    const getStr = (v: any) => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
    const first = (items[0] as Record<string, any>) || {};
    const meta = first.authorMeta || first.author || first.authorInfo || {};
    const stats = first.authorStats || first.stats || first.statistics || {};

    const username = getStr(meta.name || meta.username || meta.uniqueId || meta.userName || meta.handle);
    const fullName = getStr(meta.nickName || meta.nickname || meta.name || meta.fullName || meta.displayName);
    const bio = getStr(meta.signature || meta.bio || meta.description || first.desc);
    const website = getStr(meta.bioLink || meta.website || meta.url || meta.link);
    const followers = meta.fans ?? meta.followers ?? meta.followerCount ?? stats.followerCount ?? stats.followers ?? undefined;
    const following = meta.following ?? meta.followingCount ?? stats.followingCount ?? undefined;
    const videos = meta.video ?? meta.videoCount ?? stats.videoCount ?? undefined;
    const hearts = meta.heart ?? meta.heartCount ?? meta.likeCount ?? stats.heartCount ?? stats.diggCount ?? undefined;
    const profileUrl = getStr(meta.profileUrl || meta.url || first._discovered_profile_url || (username ? `https://www.tiktok.com/@${username}` : ''));
    const avatar = getStr(meta.originalAvatarUrl || meta.avatar || meta.avatarLarger || meta.avatarThumb);
    const isVerified = Boolean(meta.verified || meta.verifiedStatus);

    const recentPosts = items.slice(0, 20).map((p: any) => {
      const postUrl =
        getStr(p.webVideoUrl || p.url || p.videoUrl || p.video?.downloadAddr) ||
        (username && p.id ? `https://www.tiktok.com/@${username}/video/${p.id}` : '');
      const cover =
        p.videoMeta?.coverUrl ||
        p.videoMeta?.originalCoverUrl ||
        p.video?.cover ||
        p.video?.originCover ||
        p.imageUrl;
      const duration =
        p.videoMeta?.duration ??
        p.video?.duration ??
        p.video?.durationInSeconds ??
        undefined;
      const statsObj = p.stats || p.statistics || {};
      return {
        caption: getStr(p.text || p.desc || p.caption),
        url: postUrl,
        likes: p.diggCount ?? statsObj.diggCount ?? p.likes ?? undefined,
        comments: p.commentCount ?? statsObj.commentCount ?? p.comments ?? undefined,
        shares: p.shareCount ?? statsObj.shareCount ?? p.shares ?? undefined,
        views: p.playCount ?? statsObj.playCount ?? p.views ?? p.playCount ?? undefined,
        date: getStr(p.createTimeISO ?? p.createTime ?? p.createTimeUnix ?? p.date ?? p.timestamp),
        image: cover,
        duration,
      };
    });

    return {
      username,
      fullName,
      bio,
      website,
      followers,
      following,
      videos,
      hearts,
      profileUrl,
      avatar,
      isVerified,
      recentPosts,
    };
  };

  const summarizeXData = (data: unknown): {
    profileName?: string;
    profileHandle?: string;
    bio?: string;
    followers?: number | string;
    following?: number | string;
    avatar?: string;
    profileUrl?: string;
    recentPosts: Array<{
      text?: string;
      url?: string;
      date?: string | number;
      likes?: number | string;
      replies?: number | string;
      reposts?: number | string;
      quotes?: number | string;
      media?: string;
    }>;
  } => {
    const items = Array.isArray(data) ? data : [];
    const getStr = (v: any) => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
    const first = (items[0] as Record<string, any>) || {};
    const author = first.author || first.user || {};

    const profileName = getStr(author.name || author.fullName || author.displayName);
    const profileHandle = getStr(author.username || author.screen_name || author.screenName || author.handle);
    const bio = getStr(author.description || author.bio);
    const followers = author.followers_count ?? author.followers ?? author.followersCount ?? undefined;
    const following = author.friends_count ?? author.following ?? author.friendsCount ?? undefined;
    const avatar = getStr(author.profile_image_url_https || author.profileImageUrl || author.avatar);
    const profileUrl = profileHandle ? `https://x.com/${profileHandle}` : getStr(author.profileUrl);

    const recentPosts = items.slice(0, 20).map((post: any) => {
      const mediaUrl = Array.isArray(post.media)
        ? post.media[0]?.media_url_https || post.media[0]?.mediaUrlHttps || post.media[0]?.mediaUrl || post.media[0]?.url
        : post.media?.media_url_https || post.media?.mediaUrl || post.media?.url || undefined;
      return {
        text: getStr(post.full_text || post.text || post.caption || post.postText),
        url: getStr(post.url || post.tweetUrl || post.post_url || post.postUrl),
        date: post.created_at ?? post.date ?? post.timestamp ?? post.createdAt,
        likes: post.favorite_count ?? post.favouriteCount ?? post.likes ?? post.favouritesCount ?? undefined,
        replies: post.reply_count ?? post.replyCount ?? post.replies ?? undefined,
        reposts: post.retweet_count ?? post.repostCount ?? post.reposts ?? undefined,
        quotes: post.quote_count ?? post.quoteCount ?? post.quotes ?? undefined,
        media: mediaUrl,
      };
    });

    return {
      profileName,
      profileHandle,
      bio,
      followers,
      following,
      avatar,
      profileUrl,
      recentPosts,
    };
  };

  const normalizeDomain = (value?: string): string => {
    const raw = (value || '').toString().trim().toLowerCase();
    if (!raw) return '';
    let working = raw.replace(/^mailto:/, '');
    if (working.includes('@')) {
      working = working.split('@').pop() || '';
    }
    working = working.replace(/^https?:\/\//, '').replace(/^www\./, '');
    working = working.split('/')[0] || '';
    working = working.split('#')[0] || '';
    working = working.split('?')[0] || '';
    if (working.includes(':')) {
      const parts = working.split(':');
      if (parts[0]) working = parts[0];
    }
    return working.trim();
  };

  const formatLocation = (value: unknown): string => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.map(formatLocation).filter(Boolean).join(' | ');
    if (typeof value === 'object') {
      const v = value as Record<string, unknown>;
      const city = typeof v.city === 'string' ? v.city : '';
      const state = (typeof v.stateAbbr === 'string' ? v.stateAbbr : '') || (typeof v.state === 'string' ? v.state : '');
      const country = (typeof v.countryAbbr === 'string' ? v.countryAbbr : '') || (typeof v.country === 'string' ? v.country : '');
      const parts = [city, state, country].filter(Boolean);
      if (parts.length) return parts.join(', ');
    }
    return '';
  };

  const startScrapeJob = (label: string, message: string) => {
    const id = crypto.randomUUID();
    addScrapeJob({
      id,
      source: 'my-lists',
      companyName: label,
      label,
      status: 'queued',
      progress: 5,
      message,
      queuedAt: new Date().toISOString(),
    });
    updateScrapeJob(id, { status: 'running', progress: 15, startedAt: new Date().toISOString() });
    return id;
  };

  const finishScrapeJob = (id: string, ok: boolean, message: string) => {
    updateScrapeJob(id, {
      status: ok ? 'succeeded' : 'failed',
      progress: 100,
      message,
      finishedAt: new Date().toISOString(),
    });
  };

  const formatIndustry = (value: unknown): string => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.map(formatIndustry).filter(Boolean).join(' | ');
    if (typeof value === 'object') {
      const v = value as Record<string, unknown>;
      if (typeof v.name === 'string') return v.name;
      if (typeof v.value === 'string') return v.value;
    }
    return '';
  };

  const openBuyIntent = (companyName: string) => {
    setBuyIntentStatus(null);
    setBuyIntentName('');
    setBuyIntentEmail(userEmail || '');
    setBuyIntentKeywords(['', '', '']);
    setBuyIntentModal({ companyName });
  };

  const submitBuyIntent = async () => {
    if (!buyIntentModal) return;
    const name = buyIntentName.trim();
    const email = buyIntentEmail.trim();
    const keywords = buyIntentKeywords.map((k) => k.trim()).filter(Boolean).slice(0, 3);
    if (!name || !email) {
      setBuyIntentStatus('Please provide your name and email.');
      return;
    }
    if (!API_CONFIG.MAKE_WEBHOOK_URL) {
      setBuyIntentStatus('Webhook URL is missing.');
      return;
    }
    setBuyIntentSubmitting(true);
    setBuyIntentStatus(null);
    try {
      const payload = {
        action: 'buy_intent_leads',
        name,
        email,
        companyName: buyIntentModal.companyName,
        keywords,
        source: 'company_modal',
      };
      const res = await fetch(API_CONFIG.MAKE_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Webhook failed: ${res.status}`);
      setBuyIntentStatus('Request sent. We will contact you within 24 hours.');
    } catch (err) {
      console.error('Buy intent webhook failed:', err);
      setBuyIntentStatus('Failed to send request. Please try again.');
    } finally {
      setBuyIntentSubmitting(false);
    }
  };

  const normalizeCrunchbaseData = (input: any) => {
    const parseMaybeJson = (val: any): any => {
      if (typeof val !== 'string') return val;
      const trimmed = val.trim();
      if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return val;
      try {
        return JSON.parse(trimmed);
      } catch {
        return val;
      }
    };

    const unwrapOnce = (val: any): any => {
      if (!val) return val;
      if (Array.isArray(val)) return val[0];
      if (val.items) return Array.isArray(val.items) ? val.items[0] : val.items;
      if (val.data) return Array.isArray(val.data) ? val.data[0] : val.data;
      if (val.output) return Array.isArray(val.output) ? val.output[0] : val.output;
      if (val.result) return Array.isArray(val.result) ? val.result[0] : val.result;
      return val;
    };

    const rawData = parseMaybeJson(input);
    let cbData = unwrapOnce(rawData);
    for (let i = 0; i < 4; i += 1) {
      const next = unwrapOnce(cbData);
      if (next === cbData) break;
      cbData = next;
    }

    const looksLikeCrunchbase = (val: any): number => {
      if (!val || typeof val !== 'object' || Array.isArray(val)) return 0;
      const keys = [
        'properties',
        'overview_fields_extended',
        'funding_rounds_summary',
        'bombora_surge_list',
        'builtwith_tech_used_list',
        'siftery_product_list',
        'product',
        'products',
        'growth_and_heat',
      ];
      return keys.reduce((score, k) => score + (k in val ? 1 : 0), 0);
    };

    if (looksLikeCrunchbase(cbData) === 0) {
      const candidates: any[] = [];
      const visit = (val: any, depth: number) => {
        if (!val || depth > 3) return;
        if (Array.isArray(val)) {
          for (const item of val) visit(item, depth + 1);
          return;
        }
        if (typeof val === 'object') {
          candidates.push(val);
          for (const k of Object.keys(val)) visit(val[k], depth + 1);
        }
      };
      visit(rawData, 0);
      candidates.sort((a, b) => looksLikeCrunchbase(b) - looksLikeCrunchbase(a));
      if (candidates[0] && looksLikeCrunchbase(candidates[0]) > 0) {
        cbData = candidates[0];
      }
    }

    const rawPayload = parseMaybeJson(
      cbData?.raw ?? cbData?.payload ?? cbData?.response ?? rawData?.raw ?? rawData?.payload ?? rawData?.response
    );
    if (looksLikeCrunchbase(rawPayload) > looksLikeCrunchbase(cbData)) {
      cbData = rawPayload;
    }

    return cbData || null;
  };

  const pick = (val: any, keys: string[], fallback: any = ''): any => {
    for (const k of keys) {
      const v = val?.[k];
      if (v !== undefined && v !== null && v !== '') return v;
    }
    return fallback;
  };

  const normalizeCompanyKey = (value: string) =>
    (value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');

  const crunchbaseMatchesCompany = (
    cb: any,
    companyName: string,
    entity?: CompanyEntity,
    fallbackLinkedIn?: string
  ) => {
    const target = normalizeCompanyKey(companyName);
    const identifierValue = cb?.properties?.identifier?.value || cb?.identifier?.value || cb?.title || cb?.name || '';
    const permalink = cb?.properties?.identifier?.permalink || cb?.identifier?.permalink || '';
    const idNorm = normalizeCompanyKey(identifierValue);
    const permNorm = normalizeCompanyKey(permalink);
    const cbWebsite =
      cb?.properties?.website?.value ||
      cb?.website ||
      cb?.homepage_url ||
      cb?.homePageUrl ||
      cb?.overview_fields_extended?.website ||
      cb?.overview_fields_extended?.website_url ||
      cb?.properties?.website_url ||
      '';
    const cbLinkedin =
      cb?.properties?.linkedin?.value ||
      cb?.properties?.linkedin_url ||
      cb?.linkedin ||
      cb?.linkedin_url ||
      cb?.linkedinUrl ||
      cb?.overview_fields_extended?.linkedin ||
      '';
    const entityWebsite = entity?.website_url || entity?.domain || '';
    const entityLinkedin = entity?.linkedin_url || fallbackLinkedIn || '';
    const cbDomain = normalizeDomain(cbWebsite);
    const entityDomain = normalizeDomain(entityWebsite);

    if (!target) return false;
    if (idNorm && (idNorm === target || idNorm.includes(target) || target.includes(idNorm))) return true;
    if (permNorm && (permNorm === target || permNorm.includes(target) || target.includes(permNorm))) return true;
    if (cbDomain && entityDomain && cbDomain === entityDomain) return true;
    if (cbLinkedin && entityLinkedin && cbLinkedin.toLowerCase() === entityLinkedin.toLowerCase()) return true;
    return false;
  };

  const pickFromContacts = (items: Contact[], keys: string[]): unknown => {
    for (const item of items) {
      for (const key of keys) {
        const value = (item as Record<string, unknown>)[key];
        if (value === null || value === undefined) continue;
        if (Array.isArray(value) && value.length === 0) continue;
        if (typeof value === 'string' && value.trim() === '') continue;
        return value;
      }
    }
    return '';
  };

  const formatFieldValue = (value?: unknown): string => {
    if (Array.isArray(value)) return value.filter(Boolean).join(', ');
    if (value && typeof value === 'object') return JSON.stringify(value);
    return value ? String(value) : '';
  };

  const companyNameFromDomain = (domain: string): string => {
    if (!domain) return '';
    const parts = domain.split('.').filter(Boolean);
    if (parts.length === 0) return '';
    let idx = Math.max(0, parts.length - 2);
    if (parts.length >= 3) {
      const tld = parts[parts.length - 1];
      const sld = parts[parts.length - 2];
      if (tld.length <= 3 && sld.length <= 3) {
        idx = Math.max(0, parts.length - 3);
      }
    }
    const label = parts[idx] || parts[0];
    const words = label.replace(/_/g, '-').split('-').filter(Boolean);
    return words.map((w) => w[0]!.toUpperCase() + w.slice(1)).join(' ');
  };

  const getContactName = (c: Contact): string =>
    (c.fullName || c.Name || `${c.firstName || ''} ${c.lastName || ''}`.trim()).trim();

  const getContactCompany = (c: Contact): string => (c.company || c.Company || '').trim();

  const getContactDomain = (c: Contact): string =>
    normalizeDomain(c.CompanyDomain || c.Website || c.email1 || c.email || c.Email || '');

  const deriveCompanyName = (c: Contact): string => {
    const company = getContactCompany(c);
    const contactName = getContactName(c).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const companyNorm = company.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    if (company && companyNorm && companyNorm !== contactName) return company;
    const domain = getContactDomain(c);
    const derived = companyNameFromDomain(domain);
    return derived || company || 'Unknown';
  };

  const contactsForUi = loadingMore ? contactsStable : contacts;

  const loadContacts = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);
    try {
      const { companies: companyRows, totalCount } = await loadCompaniesServerSide(
        userEmail,
        currentPage,
        perPage,
        sortKeyCompanies.startsWith('company') ? 'company' : sortKeyCompanies.startsWith('contacts') ? 'contacts' : 'recent',
        sortKeyCompanies.endsWith('_asc') ? 'asc' : 'desc',
        searchTerm,
      );
      setCompaniesFromServer(companyRows);
      setTotalCompanies(totalCount);
      setLoading(false);
    } catch (e) {
      console.error('Failed to load companies:', e);
      setLoading(false);
    }
  }, [userEmail, currentPage, perPage, sortKeyCompanies, searchTerm]);

  const loadUserLists = useCallback(async () => {
    if (!userEmail) return;
    try {
      const res = await fetch(`${RESEARCH_API_URL}/get-lists?userEmail=${encodeURIComponent(userEmail)}&entityType=${LIST_ENTITY_TYPE}`);
      const data = await res.json();
      if (data.success) {
        setUserLists(data.lists || []);
      }
    } catch (e) {
      console.error('Failed to load lists:', e);
    }
  }, [userEmail]);

  useEffect(() => {
    if (isAuthenticated && userEmail) {
      loadContacts();
      loadUserLists();
      getCredits(userEmail).then(setCredits);
      const savedDefault = localStorage.getItem(DEFAULT_LIST_STORAGE_KEY) || localStorage.getItem('profitibull_default_list');
      if (savedDefault) setDefaultList(savedDefault);
    }
  }, [isAuthenticated, userEmail, loadContacts, loadUserLists, setCredits]);

  useEffect(() => {
    let active = true;
    if (!userEmail || !canChooseGhlTarget) {
      setGhlTargetOptions([]);
      setGhlSendAsLocationId('');
      return () => {
        active = false;
      };
    }
    if (!adminToken) {
      setGhlTargetOptions([]);
      return () => {
        active = false;
      };
    }
    (async () => {
      try {
        const settings = await getAccountSettings({
          token: adminToken,
          email: userEmail,
        });
        if (!active) return;
        const options = (settings.ghlSendAsOptions ?? [])
          .filter((opt) => opt.active !== false)
          .map((opt) => {
            const locationId = String(opt.locationId || '').trim();
            const name = String(opt.name || '').trim();
            return {
              locationId,
              label: `${name || locationId} (${locationId})`,
            };
          })
          .filter((opt) => Boolean(opt.locationId));
        setGhlTargetOptions(options);
        const savedLocationId = String(settings.account?.ghlSendAsLocationId || '').trim();
        setGhlSendAsLocationId(
          savedLocationId && options.some((option) => option.locationId === savedLocationId)
            ? savedLocationId
            : ''
        );
      } catch {
        if (!active) return;
        setGhlTargetOptions([]);
        setGhlSendAsLocationId('');
      }
    })();
    return () => {
      active = false;
    };
  }, [adminToken, canChooseGhlTarget, userEmail]);

  useEffect(() => {
    window.localStorage.setItem(SEND_TO_CRM_STORAGE_KEY, sendToCrm ? 'true' : 'false');
  }, [sendToCrm]);

  useEffect(() => {
    let active = true;
    if (!userEmail) return;
    getPepProfile(userEmail)
      .then((res) => {
        if (!active) return;
        if (res?.success && res.profile) {
          setPepPolicyProfile(res.profile);
          setPepPolicyLocked(Boolean(res.locked));
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [userEmail]);

  useEffect(() => {
    let active = true;
    if (!userEmail || !companyDrawer?.companyName) {
      setPepCompanyPolicyProfile(null);
      setPepCompanyPolicyDraft({});
      return;
    }
    getPepCompanyProfile(userEmail, companyDrawer.companyName)
      .then((res) => {
        if (!active) return;
        const profile = res?.success && res.profile ? res.profile : null;
        setPepCompanyPolicyProfile(profile);
        setPepCompanyPolicyDraft(profile || {});
      })
      .catch(() => {
        if (!active) return;
        setPepCompanyPolicyProfile(null);
        setPepCompanyPolicyDraft({});
      });
    return () => {
      active = false;
    };
  }, [userEmail, companyDrawer?.companyName]);

  const getContactLists = (contact: Contact): string[] => {
    let lists = contact.lists || [];
    if (typeof lists === 'string') {
      try { lists = JSON.parse(lists) || []; } catch { lists = []; }
    }
    return lists as string[];
  };

  const filteredContacts = useMemo(() => {
    let filtered = contactsForUi;
    if (currentListFilter) {
      filtered = filtered.filter(c => getContactLists(c).includes(currentListFilter));
    }
    return filtered;
  }, [contactsForUi, currentListFilter]);

  const companies = useMemo(() => {
    const map = new Map<string, CompanyRow>();

    for (const c of filteredContacts) {
      const companyName = deriveCompanyName(c);
      const existing = map.get(companyName) ?? {
        companyName,
        contactsCount: 0,
        contacts: [],
      };
      existing.contactsCount += 1;
      existing.contacts.push(c);

      const researchedAt = c.researchedAt;
      if (researchedAt && (!existing.lastResearchedAt || researchedAt > existing.lastResearchedAt)) {
        existing.lastResearchedAt = researchedAt;
      }

      const domain = getContactDomain(c);
      if (domain && !existing.sampleDomain) existing.sampleDomain = domain;

      const li = c.CompanyLIProfileUrl || '';
      if (li && !existing.fallbackLinkedIn) existing.fallbackLinkedIn = li;

      const location = formatLocation(c.CompanyLocation || c.ContactLocation);
      if (location && !existing.location) existing.location = location;

      const industry = formatIndustry(c.CompanyIndustry || c.Industry);
      if (industry && !existing.industry) existing.industry = industry;

      map.set(companyName, existing);
    }

    let list = Array.from(map.values());
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(c =>
        c.companyName.toLowerCase().includes(term) ||
        (c.sampleDomain || '').toLowerCase().includes(term)
      );
    }

    const sortByCompany = (a: CompanyRow, b: CompanyRow) => a.companyName.localeCompare(b.companyName);
    const sortByCompanyDesc = (a: CompanyRow, b: CompanyRow) => b.companyName.localeCompare(a.companyName);
    const sortByContacts = (a: CompanyRow, b: CompanyRow) => (b.contactsCount ?? 0) - (a.contactsCount ?? 0);
    const sortByContactsAsc = (a: CompanyRow, b: CompanyRow) => (a.contactsCount ?? 0) - (b.contactsCount ?? 0);
    const sortByRecent = (a: CompanyRow, b: CompanyRow) =>
      new Date(b.lastResearchedAt || 0).getTime() - new Date(a.lastResearchedAt || 0).getTime();
    const sortByRecentAsc = (a: CompanyRow, b: CompanyRow) =>
      new Date(a.lastResearchedAt || 0).getTime() - new Date(b.lastResearchedAt || 0).getTime();

    switch (sortKeyCompanies) {
      case 'company_desc':
        list.sort(sortByCompanyDesc);
        break;
      case 'contacts_desc':
        list.sort(sortByContacts);
        break;
      case 'contacts_asc':
        list.sort(sortByContactsAsc);
        break;
      case 'recent_desc':
        list.sort(sortByRecent);
        break;
      case 'recent_asc':
        list.sort(sortByRecentAsc);
        break;
      case 'company_asc':
      default:
        list.sort(sortByCompany);
        break;
    }

    return list;
  }, [filteredContacts, searchTerm, sortKeyCompanies]);

  const formatDate = (value?: string) => {
    if (!value) return '-';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
  };

  const deepFindArray = (obj: any, keys: string[]): any[] | null => {
    if (!obj) return null;
    if (Array.isArray(obj)) return null;
    for (const key of keys) {
      if (Array.isArray(obj[key])) return obj[key];
    }
    for (const value of Object.values(obj)) {
      if (Array.isArray(value)) {
        if (keys.includes('any')) return value;
      } else if (value && typeof value === 'object') {
        const found = deepFindArray(value, keys);
        if (found) return found;
      }
    }
    return null;
  };

  const deepCollect = (obj: any, predicate: (key: string, value: any) => boolean, out: any[] = []) => {
    if (!obj) return out;
    if (Array.isArray(obj)) {
      obj.forEach((item) => deepCollect(item, predicate, out));
      return out;
    }
    if (typeof obj === 'object') {
      for (const [k, v] of Object.entries(obj)) {
        if (predicate(k, v)) out.push(v);
        if (v && typeof v === 'object') deepCollect(v, predicate, out);
      }
    }
    return out;
  };

  const normalizeCrunchbasePayload = (data: any): any => {
    const unwrapOnce = (val: any): any => {
      if (!val) return val;
      if (Array.isArray(val)) return val[0];
      if (val.items) return Array.isArray(val.items) ? val.items[0] : val.items;
      if (val.data) return Array.isArray(val.data) ? val.data[0] : val.data;
      if (val.output) return Array.isArray(val.output) ? val.output[0] : val.output;
      if (val.result) return Array.isArray(val.result) ? val.result[0] : val.result;
      return val;
    };
    const looksLikeCrunchbase = (val: any): number => {
      if (!val || typeof val !== 'object' || Array.isArray(val)) return 0;
      const keys = [
        'properties',
        'overview_fields_extended',
        'funding_rounds_summary',
        'key_employee_change_list',
        'partnership_announcements',
        'bombora_surge_list',
        'builtwith_tech_used_list',
        'siftery_product_list',
        'acquisitions_list',
        'product_launches',
        'awards',
      ];
      return keys.reduce((score, k) => score + (k in val ? 1 : 0), 0);
    };
    let core = unwrapOnce(data);
    for (let i = 0; i < 4; i++) {
      const next = unwrapOnce(core);
      if (next === core) break;
      core = next;
    }
    if (looksLikeCrunchbase(core) === 0 && data && typeof data === 'object') {
      const candidates: any[] = [];
      const visit = (val: any, depth: number) => {
        if (!val || depth > 3) return;
        if (Array.isArray(val)) {
          for (const item of val) visit(item, depth + 1);
          return;
        }
        if (typeof val === 'object') {
          candidates.push(val);
          for (const k of Object.keys(val)) visit((val as any)[k], depth + 1);
        }
      };
      visit(data, 0);
      candidates.sort((a, b) => looksLikeCrunchbase(b) - looksLikeCrunchbase(a));
      if (candidates[0] && looksLikeCrunchbase(candidates[0]) > 0) core = candidates[0];
    }
    return core || data;
  };

  const extractTechList = (data: any): any[] => {
    if (!data) return [];
    const core = normalizeCrunchbasePayload(data);
    const direct =
      core?.builtwith_tech_used_list ||
      core?.siftery_product_list ||
      deepFindArray(core, ['builtwith_tech_used_list', 'siftery_product_list', 'builtwith', 'tech_used_list']) ||
      [];
    const list = Array.isArray(direct) ? direct : [];
    if (list.length) return list;
    const candidates = deepCollect(core, (_k, v) => Array.isArray(v));
    for (const arr of candidates) {
      if (!Array.isArray(arr)) continue;
      const items = arr.filter((item: any) => item && typeof item === 'object');
      if (!items.length) continue;
      const looksLikeTech = items.some((item: any) =>
        item.name || item.value || item.tech || item.product || item.identifier || item.category || item.type
      );
      if (looksLikeTech) return items;
    }
    return [];
  };

  // extractBuyingSignals removed (replaced by intentSignals + activitySignals)

  const extractOpenRoles = (data: any): number | null => {
    if (!data) return null;
    const counts = deepCollect(data, (k, v) => typeof v === 'number' && /open_roles|open_positions|openings|job_openings|jobs_count|job_count/i.test(k));
    if (counts.length) return Number(counts[0]);
    const jobsArr = deepFindArray(data, ['jobs', 'job_postings', 'jobPostings', 'open_roles', 'open_positions', 'openJobs', 'jobListings', 'listings']);
    if (jobsArr && Array.isArray(jobsArr)) return jobsArr.length;
    // LinkedIn posts mentioning openings
    const posts = deepFindArray(data, ['posts']) || [];
    if (Array.isArray(posts)) {
      const jobPosts = posts.filter((p: any) => {
        const text = (p?.content || p?.text || '').toString().toLowerCase();
        return text.includes('opening') || text.includes('open role') || text.includes('hiring');
      });
      if (jobPosts.length) return jobPosts.length;
    }
    return null;
  };

  const extractTotalFunding = (data: any): string => {
    if (!data) return '';
    const values = deepCollect(
      data,
      (k, v) =>
        (typeof v === 'number' || typeof v === 'string') &&
        /total_funding|funding_total|totalFunding|total_funding_usd|funding_total_usd/i.test(k)
    ).filter(Boolean);
    const raw = values[0];
    if (raw === undefined || raw === null) return '';
    return String(raw);
  };

  const computeReviewAverage = (g2Data: any, trustpilotData: any): string => {
    const combined = [...collectRatings(g2Data), ...collectRatings(trustpilotData)];
    if (!combined.length) return '';
    const avg = combined.reduce((a, b) => a + b, 0) / combined.length;
    return avg.toFixed(2);
  };

  const extractLinkedinActivity = (data: any): { count: number; latest?: string } | null => {
    if (!data) return null;
    const posts = deepFindArray(data, ['posts']) || [];
    if (!Array.isArray(posts) || posts.length === 0) return null;
    const dates = posts
      .map((p: any) => p?.postedAt?.date || p?.postedAt?.timestamp || p?.date)
      .filter(Boolean)
      .map((d: any) => (typeof d === 'number' ? new Date(d).toISOString() : String(d)));
    const latest = dates.length ? dates.sort().slice(-1)[0] : undefined;
    return { count: posts.length, latest };
  };

  const extractGlassdoorActivity = (data: any): { rating?: number; offices?: number } | null => {
    if (!data) return null;
    const overview = Array.isArray(data?.overview) ? data.overview[0] : null;
    if (!overview) return null;
    const rating = overview?.employerReviews?.ratings?.overallRating;
    const offices = Array.isArray(overview?.officeAddresses) ? overview.officeAddresses.length : undefined;
    if (rating === undefined && offices === undefined) return null;
    return { rating, offices };
  };

  const collectRatings = (data: any): number[] => {
    if (!data) return [];
    const ratings: number[] = [];
    const items = Array.isArray(data) ? data : (data?.data || data?.items || data?.results || data?.reviews || data?.content || []);
    const list = Array.isArray(items) ? items : [];
    list.forEach((item: any) => {
      const r = item?.rating ?? item?.score ?? item?.overallRating ?? item?.overall_rating;
      if (typeof r === 'number') ratings.push(r);
    });
    return ratings;
  };

  const classifyDepartment = (titleRaw?: string): string => {
    const title = (titleRaw || '').toLowerCase();
    if (!title) return 'Other';
    if (title.includes('sales') || title.includes('account') || title.includes('business development') || title.includes('revenue')) return 'Sales';
    if (title.includes('marketing') || title.includes('growth') || title.includes('brand') || title.includes('content') || title.includes('seo')) return 'Marketing';
    if (title.includes('operations') || title.includes('ops') || title.includes('logistics') || title.includes('supply chain')) return 'Operations';
    if (title.includes('finance') || title.includes('financial') || title.includes('controller')) return 'Finance';
    if (title.includes('accounting') || title.includes('payable') || title.includes('receivable') || title.includes('cpa')) return 'Accounting';
    if (title.includes('support') || title.includes('customer success') || title.includes('customer service')) return 'Support';
    if (title.includes('legal') || title.includes('counsel') || title.includes('compliance')) return 'Legal';
    if (title.includes('engineer') || title.includes('developer') || title.includes('software') || title.includes('devops') || title.includes('architect')) return 'Engineering';
    if (title.includes('human resources') || title.includes('hr') || title.includes('talent') || title.includes('people') || title.includes('recruit')) return 'Human Resources';
    if (title.includes('it ') || title.includes('information technology') || title.includes('technology')) return 'IT';
    if (title.includes('ceo') || title.includes('chief') || title.includes('founder') || title.includes('owner') || title.includes('president')) return 'Org';
    return 'Other';
  };

  const classifySeniority = (titleRaw?: string): string => {
    const title = (titleRaw || '').toLowerCase();
    if (!title) return 'Other';
    if (title.includes('chief') || title.includes('ceo') || title.includes('cfo') || title.includes('coo') || title.includes('cto') || title.includes('cio') || title.includes('president') || title.includes('founder') || title.includes('owner')) return 'C-Level';
    if (title.includes('vp') || title.includes('vice president')) return 'VPs';
    if (title.includes('director') || title.includes('head of')) return 'Directors';
    if (title.includes('manager') || title.includes('lead')) return 'Managers';
    return 'Other';
  };

  const finalCompanies = companiesFromServer.length > 0 ? companiesFromServer : companies;
  const totalPages = Math.max(1, Math.ceil(totalCompanies > 0 ? Math.ceil(totalCompanies / perPage) : finalCompanies.length / perPage));
  const paginatedCompanies = companiesFromServer.length > 0 ? finalCompanies : companies.slice(currentPage * perPage, (currentPage + 1) * perPage);
  const paginatedCompanyNames = useMemo(() => paginatedCompanies.map((c) => c.companyName), [paginatedCompanies]);

  const fetchedCompanyNamesRef = useRef<Set<string>>(new Set());

  const loadCompanyEntities = useCallback(async (names: string[], force = false) => {
    if (!userEmail || names.length === 0) return;
    const missing = force
      ? names
      : names.filter((n) => !companyEntities[n] && !fetchedCompanyNamesRef.current.has(n));
    if (missing.length === 0) return;
    missing.forEach((n) => fetchedCompanyNamesRef.current.add(n));
    try {
      const res = await fetch(`${RESEARCH_API_URL}/company/batch-get`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyNames: missing }),
      });
      const data = await res.json();
      if (data.success && data.companies) {
        setCompanyEntities((prev) => ({ ...prev, ...data.companies }));
      }
    } catch (e) {
      missing.forEach((n) => fetchedCompanyNamesRef.current.delete(n));
      console.error('Failed to load company entities:', e);
    }
  }, [userEmail, companyEntities]);

  useEffect(() => {
    if (paginatedCompanyNames.length) loadCompanyEntities(paginatedCompanyNames);
  }, [paginatedCompanyNames, loadCompanyEntities]);

  useEffect(() => {
    if (!companyDrawer) return;
    setCompanyModalTab('ov');
    const stored = window.localStorage.getItem('solo-theme');
    setCompanyModalTheme(stored === 'light' ? 'light' : 'dark');
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setCompanyDrawer(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [companyDrawer]);

  useEffect(() => {
    const run = async () => {
      if (!companyDrawer) return;
      const entity = getCompanyEntity(companyDrawer.companyName) || {};
      const hasCrunchbaseFull = Boolean(entity.crunchbase_ds) && (!Array.isArray(entity.crunchbase_ds) || entity.crunchbase_ds.length > 0);
      if (!hasCrunchbaseFull) return;
      const cached = crunchbaseCache[companyDrawer.companyName];
      if (cached) {
        const cb = normalizeCrunchbaseData(cached.data);
        if (cb && crunchbaseMatchesCompany(cb, companyDrawer.companyName, entity, getFallbackLinkedIn(companyDrawer.companyName))) return;
      }
      const full = await fetchCrunchbaseFull(companyDrawer.companyName);
      if (full) {
        const payload = { data: full, scrapedAt: entity.crunchbase_scraped_at };
        setCrunchbaseCache((prev) => ({ ...prev, [companyDrawer.companyName]: payload }));
      }
    };
    run();
  }, [companyDrawer, companyEntities, crunchbaseCache]);

  useEffect(() => {
    const run = async () => {
      if (!companyDrawer) return;
      const companyName = companyDrawer.companyName;
      const entity = getCompanyEntity(companyName) || {};
      const hasLinkedinFull =
        (Array.isArray(entity.linkedin_profile_ds) && entity.linkedin_profile_ds.length > 0) ||
        (Array.isArray(entity.linkedin_posts_ds) && entity.linkedin_posts_ds.length > 0) ||
        (Array.isArray(entity.linkedin_jobs_ds) && entity.linkedin_jobs_ds.length > 0) ||
        (Array.isArray(entity.linkedin_employees_ds) && entity.linkedin_employees_ds.length > 0);
      const hasGlassdoorFull =
        (Array.isArray(entity.glassdoor_overview_ds) && entity.glassdoor_overview_ds.length > 0) ||
        (Array.isArray(entity.glassdoor_reviews_ds) && entity.glassdoor_reviews_ds.length > 0) ||
        (Array.isArray(entity.glassdoor_salaries_ds) && entity.glassdoor_salaries_ds.length > 0) ||
        (Array.isArray(entity.glassdoor_interviews_ds) && entity.glassdoor_interviews_ds.length > 0) ||
        (Array.isArray(entity.glassdoor_benefits_ds) && entity.glassdoor_benefits_ds.length > 0);
      const hasG2Full = Array.isArray(entity.g2_ds) && entity.g2_ds.length > 0;
      const hasTrustpilotFull = Array.isArray(entity.trustpilot_ds) && entity.trustpilot_ds.length > 0;
      const hasInstagramFull = Array.isArray(entity.instagram_ds) && entity.instagram_ds.length > 0;
      const hasFacebookFull = Array.isArray(entity.facebook_ds) && entity.facebook_ds.length > 0;
      const hasYouTubeFull = Array.isArray(entity.youtube_ds) && entity.youtube_ds.length > 0;
      const hasTikTokFull = Array.isArray(entity.tiktok_ds) && entity.tiktok_ds.length > 0;
      const hasXFull = Array.isArray(entity.x_ds) && entity.x_ds.length > 0;
      const hasThreadsFull = Array.isArray(entity.threads_ds) && entity.threads_ds.length > 0;

      const ensure = async (
        cache: Record<string, { data: unknown; scrapedAt?: string }>,
        setter: Dispatch<SetStateAction<Record<string, { data: unknown; scrapedAt?: string }>>>,
        hasFull: boolean,
        fetcher: (name: string) => Promise<unknown | null>,
        scrapedAt?: string
      ) => {
        if (cache[companyName] || !hasFull) return;
        const full = await fetcher(companyName);
        if (full) {
          setter((prev) => ({ ...prev, [companyName]: { data: full, scrapedAt } }));
        }
      };

      // Prefetch core reports so overview can display signals.
      await ensure(linkedinCache, setLinkedinCache, hasLinkedinFull, fetchLinkedinFull, entity.linkedin_scraped_at);
      await ensure(glassdoorCache, setGlassdoorCache, hasGlassdoorFull, fetchGlassdoorFull, entity.glassdoor_scraped_at);
      await ensure(g2Cache, setG2Cache, hasG2Full, fetchG2Full, entity.g2_scraped_at);
      await ensure(trustpilotCache, setTrustpilotCache, hasTrustpilotFull, fetchTrustpilotFull, entity.trustpilot_scraped_at);

      // Always prefetch social reports when present so insights are available on open.
      await ensure(instagramCache, setInstagramCache, hasInstagramFull, fetchInstagramFull, entity.instagram_scraped_at);
      await ensure(facebookCache, setFacebookCache, hasFacebookFull, fetchFacebookFull, entity.facebook_scraped_at);
      await ensure(youtubeCache, setYoutubeCache, hasYouTubeFull, fetchYouTubeFull, entity.youtube_scraped_at);
      await ensure(tiktokCache, setTiktokCache, hasTikTokFull, fetchTikTokFull, entity.tiktok_scraped_at);
      await ensure(xCache, setXCache, hasXFull, fetchXFull, entity.x_scraped_at);
      await ensure(threadsCache, setThreadsCache, hasThreadsFull, fetchThreadsFull, entity.threads_scraped_at);
    };
    run();
  }, [
    companyDrawer,
    companyModalTab,
    companySocialTab,
    companyEntities,
    linkedinCache,
    glassdoorCache,
    g2Cache,
    trustpilotCache,
    instagramCache,
    facebookCache,
    youtubeCache,
    tiktokCache,
    xCache,
    threadsCache,
  ]);

  const handleFindCompanyUrl = async (companyName: string) => {
    if (!userEmail) return;
    const jobId = startScrapeJob(`${companyName} · Find URL`, 'Searching for company website');
    setFindingCompanyUrls((prev) => ({ ...prev, [companyName]: true }));
    try {
      const res = await fetch(`${RESEARCH_API_URL}/company-url/find`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName }),
      });
      const data = await res.json();
      if (!data.success) {
        alert(data.message || 'No URL found');
        finishScrapeJob(jobId, false, 'No URL found');
        return;
      }
      await loadContacts();
      await loadCompanyEntities([companyName]);
      finishScrapeJob(jobId, true, 'Website found');
    } catch (e) {
      console.error('Find URL failed:', e);
      alert('Find URL failed');
      finishScrapeJob(jobId, false, 'Find URL failed');
    } finally {
      setFindingCompanyUrls((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const handleSaveCompanyUrl = async () => {
    if (!userEmail || !editCompanyUrl) return;
    const url = editCompanyUrlValue.trim();
    if (!url) return;
    try {
      const res = await fetch(`${RESEARCH_API_URL}/company-url/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName: editCompanyUrl.companyName, url }),
      });
      const data = await res.json();
      if (!data.success) {
        alert(data.message || 'Failed to update URL');
        return;
      }
      await loadContacts();
      await loadCompanyEntities([editCompanyUrl.companyName], true);
      setEditCompanyUrl(null);
      setEditCompanyUrlValue('');
    } catch (e) {
      console.error('Set URL failed:', e);
      alert('Set URL failed');
    }
  };

  const handleSaveCompanyLink = async () => {
    if (!userEmail || !editCompanyLink) return;
    const url = editCompanyLinkValue.trim();
    try {
      const res = await fetch(`${RESEARCH_API_URL}/company/link/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName: editCompanyLink.companyName, field: editCompanyLink.field, url }),
      });
      const data = await res.json();
      if (!data.success) {
        alert(data.message || 'Failed to update link');
        return;
      }
      await loadCompanyEntities([editCompanyLink.companyName], true);
      setEditCompanyLink(null);
      setEditCompanyLinkValue('');
    } catch (e) {
      console.error('Set link failed:', e);
      alert('Set link failed');
    }
  };

  const openSocialLinkEditor = (companyName: string, platform: SocialPlatform) => {
    const field = socialField[platform];
    if (!field) return;
    const entity = getCompanyEntity(companyName) || {};
    const current =
      (entity[field] as string | undefined) ||
      (platform === 'linkedin' ? getFallbackLinkedIn(companyName) : undefined);
    setEditCompanyLink({
      companyName,
      field,
      label: socialLabels[platform] || field,
      current,
    });
    setEditCompanyLinkValue(current || '');
  };

  const setCompanyLinkDirect = async (companyName: string, platform: SocialPlatform, url: string) => {
    if (!userEmail) return false;
    const field = socialField[platform];
    if (!field || !url) return false;
    const res = await fetch(`${RESEARCH_API_URL}/company/link/set`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userEmail, companyName, field, url }),
    });
    const data = await res.json();
    return Boolean(data?.success);
  };

  const fetchGlassdoorFull = async (companyName: string) => {
    if (!userEmail) return null;
    try {
      const res = await fetch(`${RESEARCH_API_URL}/company/glassdoor/full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName }),
      });
      const data = await res.json();
      if (!data.success || !data.data) return null;
      return data.data as { overview?: unknown[]; reviews?: unknown[]; salaries?: unknown[]; interviews?: unknown[]; benefits?: unknown[] };
    } catch (e) {
      console.error('Failed to load full Glassdoor report:', e);
      return null;
    }
  };

  const fetchCrunchbaseFull = async (companyName: string) => {
    if (!userEmail) return null;
    try {
      const res = await fetch(`${RESEARCH_API_URL}/company/crunchbase/full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName }),
      });
      const data = await res.json();
      if (!data.success || !data.data) return null;
      return data.data;
    } catch (e) {
      console.error('Failed to load full Crunchbase report:', e);
      return null;
    }
  };

  const fetchG2Full = async (companyName: string) => {
    if (!userEmail) return null;
    try {
      const res = await fetch(`${RESEARCH_API_URL}/company/g2/full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName }),
      });
      const data = await res.json();
      if (!data.success || !data.data) return null;
      return data.data as unknown[];
    } catch (e) {
      console.error('Failed to load full G2 report:', e);
      return null;
    }
  };

  const fetchTrustpilotFull = async (companyName: string) => {
    if (!userEmail) return null;
    try {
      const res = await fetch(`${RESEARCH_API_URL}/company/trustpilot/full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName }),
      });
      const data = await res.json();
      if (!data.success || !data.data) return null;
      return data.data as unknown[];
    } catch (e) {
      console.error('Failed to load full Trustpilot report:', e);
      return null;
    }
  };

  const fetchInstagramFull = async (companyName: string) => {
    if (!userEmail) return null;
    try {
      const res = await fetch(`${RESEARCH_API_URL}/company/instagram/full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName }),
      });
      const data = await res.json();
      if (!data.success || !data.data) return null;
      let payload: any = data.data;
      if (Array.isArray(payload) && payload.length === 1 && payload[0] && typeof payload[0] === 'object' && 'results' in payload[0]) {
        payload = payload[0];
      } else if (Array.isArray(payload)) {
        payload = { results: payload };
      }
      return payload as unknown;
    } catch (e) {
      console.error('Failed to load full Instagram report:', e);
      return null;
    }
  };

  const fetchFacebookFull = async (companyName: string) => {
    if (!userEmail) return null;
    try {
      const res = await fetch(`${RESEARCH_API_URL}/company/facebook/full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName }),
      });
      const data = await res.json();
      if (!data.success || !data.data) return null;
      let payload: any = data.data;
      if (Array.isArray(payload) && payload.length === 1 && payload[0] && typeof payload[0] === 'object') {
        payload = payload[0];
      } else if (Array.isArray(payload)) {
        const first = payload.find((item: any) => item && (item.page || item.posts)) ?? payload[0];
        if (first && typeof first === 'object') {
          payload = first;
        } else {
          payload = { items: payload };
        }
      }
      return payload as unknown;
    } catch (e) {
      console.error('Failed to load full Facebook report:', e);
      return null;
    }
  };

  const fetchYouTubeFull = async (companyName: string) => {
    if (!userEmail) return null;
    try {
      const res = await fetch(`${RESEARCH_API_URL}/company/youtube/full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName }),
      });
      const data = await res.json();
      if (!data.success || !data.data) return null;
      let payload: any = data.data;
      if (Array.isArray(payload) && payload.length === 1 && payload[0] && typeof payload[0] === 'object' && 'results' in payload[0]) {
        payload = (payload[0] as any).results ?? payload;
      } else if (Array.isArray(payload) && payload.some((item: any) => item && typeof item === 'object' && 'results' in item)) {
        payload = payload.flatMap((item: any) => (Array.isArray(item?.results) ? item.results : [item]));
      }
      return payload as unknown[];
    } catch (e) {
      console.error('Failed to load full YouTube report:', e);
      return null;
    }
  };

  const fetchTikTokFull = async (companyName: string) => {
    if (!userEmail) return null;
    try {
      const res = await fetch(`${RESEARCH_API_URL}/company/tiktok/full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName }),
      });
      const data = await res.json();
      if (!data.success || !data.data) return null;
      const payload = data.data;
      if (Array.isArray(payload)) return payload as unknown[];
      const results =
        (Array.isArray(payload?.results) && payload.results) ||
        (Array.isArray(payload?.items) && payload.items) ||
        (Array.isArray(payload?.data) && payload.data) ||
        (Array.isArray(payload?.data?.items) && payload.data.items) ||
        (Array.isArray(payload?.data?.results) && payload.data.results) ||
        (Array.isArray(payload?.content) && payload.content);
      if (results) return results as unknown[];
      return [payload] as unknown[];
    } catch (e) {
      console.error('Failed to load full TikTok report:', e);
      return null;
    }
  };

  const fetchXFull = async (companyName: string) => {
    if (!userEmail) return null;
    try {
      const res = await fetch(`${RESEARCH_API_URL}/company/x/full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName }),
      });
      const data = await res.json();
      if (!data.success || !data.data) return null;
      return data.data as unknown[];
    } catch (e) {
      console.error('Failed to load full X report:', e);
      return null;
    }
  };

  const buildLinkedinFullData = (payload: { profile?: unknown[]; posts?: unknown[]; jobs?: unknown[]; employees?: unknown[] }) => {
    const profile = Array.isArray(payload.profile) && payload.profile.length > 0 ? (payload.profile[0] as Record<string, unknown>) : {};
    return {
      company: [
        {
          ...(profile ?? {}),
          posts: payload.posts ?? [],
          jobs: payload.jobs ?? [],
          employees: payload.employees ?? [],
        },
      ],
      posts: payload.posts ?? [],
      jobs: payload.jobs ?? [],
    };
  };

  const fetchLinkedinFull = async (companyName: string) => {
    if (!userEmail) return null;
    try {
      const res = await fetch(`${RESEARCH_API_URL}/company/linkedin/full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName }),
      });
      const data = await res.json();
      if (!data.success || !data.data) return null;
      return buildLinkedinFullData(data.data as { profile?: unknown[]; posts?: unknown[]; jobs?: unknown[]; employees?: unknown[] });
    } catch (e) {
      console.error('Failed to load full LinkedIn report:', e);
      return null;
    }
  };

  const fetchWebsiteFull = async (companyName: string) => {
    if (!userEmail) return null;
    try {
      const res = await fetch(`${RESEARCH_API_URL}/company/website/full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName }),
      });
      const data = await res.json();
      if (!data.success || !data.data) return null;
      return data.data as unknown[];
    } catch (e) {
      console.error('Failed to load full website report:', e);
      return null;
    }
  };

  const fetchThreadsFull = async (companyName: string) => {
    if (!userEmail) return null;
    try {
      const res = await fetch(`${RESEARCH_API_URL}/company/threads/full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName }),
      });
      const data = await res.json();
      if (!data.success || !data.data) return null;
      return data.data as unknown[];
    } catch (e) {
      console.error('Failed to load full Threads report:', e);
      return null;
    }
  };

  const normalizeName = (value?: string) => (value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const normalizeUrl = (value?: string) => {
    const raw = (value || '').toString().trim().toLowerCase();
    if (!raw) return '';
    let working = raw.replace(/^https?:\/\//, '').replace(/^www\./, '');
    working = working.split('/')[0] || '';
    working = working.split('?')[0] || '';
    working = working.split('#')[0] || '';
    return working;
  };
  const getLinkedInProfileMeta = (data: { company?: unknown[] }) => {
    const profile = Array.isArray(data.company) && data.company.length > 0 ? (data.company[0] as Record<string, unknown>) : {};
    const profileName =
      (profile.company_name as string) ||
      (profile.companyName as string) ||
      (profile.name as string) ||
      (profile.company as string) ||
      '';
    const profileUrl =
      (profile.linkedinUrl as string) ||
      (profile.linkedInUrl as string) ||
      (profile.linkedin_url as string) ||
      (profile.url as string) ||
      (profile.companyUrl as string) ||
      '';
    const profileWebsite =
      (profile.website as string) ||
      (profile.websiteUrl as string) ||
      (profile.website_url as string) ||
      '';
    return { profileName, profileUrl, profileWebsite };
  };

  const handleWebsiteScrape = async (companyName: string, website?: string) => {
    if (!userEmail || !website) return;
    const jobId = startScrapeJob(`${companyName} · Scrape Site`, 'Starting website scrape…');
    setScrapingWebsite((prev) => ({ ...prev, [companyName]: true }));
    try {
      updateScrapeJob(jobId, { progress: 20, message: 'Crawling website…' });
      const res = await fetch(`${RESEARCH_API_URL}/company/website-scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail,
          companyName,
          website: normalizeWebsiteUrl(website),
        }),
      });
      const data = await res.json();
      if (!data?.success) {
        alert(data.error || 'Website scrape failed');
        finishScrapeJob(jobId, false, 'Website scrape failed');
        return;
      }
      const content = data.content || {};
      const links = data.links || {};
      const contentRunId = content.run_id;
      const contentDatasetId = content.dataset_id;
      if (!contentRunId) throw new Error('Website scrape failed');

      const { contentItems, linkItems } = await pollWebsiteResults(
        contentRunId,
        contentDatasetId,
        links.run_id,
        links.dataset_id,
        (progress, message) => updateScrapeJob(jobId, { progress: Math.min(95, Math.max(10, Math.round(progress))), message })
      );

      updateScrapeJob(jobId, { progress: 90, message: 'Saving website data…' });
      const scrapedAt = new Date().toISOString();
      const combined = [...(contentItems || []), ...(linkItems || [])];
      try {
        const saveRes = await fetch(`${RESEARCH_API_URL}/company/website/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail, companyName, data: contentItems, linksData: linkItems }),
        });
        if (!saveRes.ok) throw new Error('Failed to save website data');
        const saved = await saveRes.json();
        if (!saved.success) throw new Error(saved.error || 'Failed to save website data');
      } catch (e) {
        console.warn('Failed to persist website data:', e);
      }

      const socials = extractWebsiteSocialLinks(combined);
      setCompanyEntities((prev) => ({
        ...prev,
        [companyName]: {
          ...prev[companyName],
          ...socials,
          website_scraped_at: scrapedAt,
          socials_last_scraped_at: scrapedAt,
        },
      }));
      setWebsiteCache((prev) => ({ ...prev, [companyName]: { data: combined, scrapedAt } }));
      setWebsiteDrawer({ companyName, data: combined, scrapedAt });
      await loadCompanyEntities([companyName], true);
      finishScrapeJob(jobId, true, 'Website scrape complete');
    } catch (e) {
      console.error('Website scrape failed:', e);
      alert('Website scrape failed');
      finishScrapeJob(jobId, false, 'Website scrape failed');
    } finally {
      setScrapingWebsite((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const handleSocialSearch = async (companyName: string, platform: SocialPlatform) => {
    if (!userEmail) return;
    const key = `${companyName}::${platform}`;
    const jobId = startScrapeJob(`${companyName} · ${platform}`, 'Searching social profile');
    setSocialSearching((prev) => ({ ...prev, [key]: true }));
    try {
      const entity = getCompanyEntity(companyName);
      let websiteData = websiteCache[companyName]?.data;
      if (!websiteData && entity?.website_ds && entity.website_ds.length) {
        websiteData = await fetchWebsiteFull(companyName);
      }
      if (websiteData) {
        const socials = extractWebsiteSocialLinks(websiteData);
        const candidate = socials[platform];
        if (candidate) {
          const saved = await setCompanyLinkDirect(companyName, platform, candidate);
          if (saved) {
            await loadCompanyEntities([companyName], true);
            finishScrapeJob(jobId, true, 'Found on website');
            return;
          }
        }
      }

      const res = await fetch(`${RESEARCH_API_URL}/company/social-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail, companyName, platform }),
      });
      const data = await res.json();
      if (!data.success) {
        alert(data.error || 'No URL found');
        finishScrapeJob(jobId, false, 'Search failed');
        return;
      }
      await loadCompanyEntities([companyName]);
      finishScrapeJob(jobId, true, 'Social found');
    } catch (e) {
      console.error('Social search failed:', e);
      alert('Social search failed');
      finishScrapeJob(jobId, false, 'Search failed');
    } finally {
      setSocialSearching((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleCrunchbasePurchase = async (companyName: string) => {
    if (!userEmail) return;
    const jobId = startScrapeJob(`${companyName} · Crunchbase`, 'Fetching Crunchbase report');
    setCrunchbaseLoading((prev) => ({ ...prev, [companyName]: true }));
    try {
      const result = await startCrunchbaseScrape(companyName, userEmail, true);
      if (!result.success) throw new Error(result.error || 'Crunchbase scrape failed');
      const data = await pollCrunchbaseResults(result);
      const scrapedAt = new Date().toISOString();
      try {
        const res = await fetch(`${RESEARCH_API_URL}/company/crunchbase/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail, companyName, data }),
        });
        if (!res.ok) throw new Error('Failed to save Crunchbase data');
        const saved = await res.json();
        if (!saved.success) throw new Error(saved.error || 'Failed to save Crunchbase data');
        await loadCompanyEntities([companyName], true);
      } catch (e) {
        console.warn('Failed to persist Crunchbase data:', e);
      }
      setCrunchbaseCache((prev) => ({ ...prev, [companyName]: { data, scrapedAt } }));
      setCrunchbaseDrawer({ companyName, data, scrapedAt });
      finishScrapeJob(jobId, true, 'Crunchbase ready');
    } catch (e) {
      console.error('Crunchbase scrape failed:', e);
      alert('Crunchbase scrape failed');
      finishScrapeJob(jobId, false, 'Crunchbase failed');
    } finally {
      setCrunchbaseLoading((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const handleLinkedInScrape = async (companyName: string, linkedinUrl?: string) => {
    if (!userEmail) return;
    if (!linkedinUrl) {
      alert('Missing LinkedIn URL. Use Find (1cr) or edit to add it.');
      return;
    }
    const jobId = startScrapeJob(`${companyName} · LinkedIn`, 'Scraping LinkedIn company data');
    setLinkedinLoading((prev) => ({ ...prev, [companyName]: true }));
    try {
      const result = await startLinkedIn360Scrape(companyName, linkedinUrl, userEmail, true);
      if (!result?.success) throw new Error(result?.error || 'LinkedIn scrape failed');
      const data = await pollLinkedIn360Results(result, (progress, message) => {
        updateScrapeJob(jobId, { progress: Math.min(95, Math.max(10, Math.round(progress))), message });
      }, { requireCompanyProfile: false });
      const meta = getLinkedInProfileMeta(data);
      const requestedName = normalizeName(companyName);
      const returnedName = normalizeName(meta.profileName);
      const requestedLinkedInHost = normalizeUrl(linkedinUrl);
      const returnedLinkedInHost = normalizeUrl(meta.profileUrl);
      const returnedWebsiteHost = normalizeUrl(meta.profileWebsite);
      void returnedWebsiteHost;
      const nameLooksWrong = returnedName && requestedName && !returnedName.includes(requestedName) && !requestedName.includes(returnedName);
      const linkedinLooksWrong = requestedLinkedInHost && returnedLinkedInHost && requestedLinkedInHost !== returnedLinkedInHost;
      if (nameLooksWrong && linkedinLooksWrong) {
        throw new Error(`LinkedIn mismatch. Requested "${companyName}" but got "${meta.profileName || 'Unknown'}" (${meta.profileUrl || 'no url'})`);
      }
      const scrapedAt = new Date().toISOString();
      try {
        const res = await fetch(`${RESEARCH_API_URL}/company/linkedin/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail, companyName, data, linkedinUrl }),
        });
        if (!res.ok) throw new Error('Failed to save LinkedIn data');
        const saved = await res.json();
        if (!saved.success) throw new Error(saved.error || 'Failed to save LinkedIn data');
        await loadCompanyEntities([companyName], true);
      } catch (e) {
        console.warn('Failed to persist LinkedIn data:', e);
        alert('LinkedIn saved failed. Please retry.');
      }
      setLinkedinCache((prev) => ({ ...prev, [companyName]: { data, scrapedAt } }));
      setLinkedinDrawer({ companyName, data, scrapedAt });
      finishScrapeJob(jobId, true, 'LinkedIn ready');
    } catch (e) {
      console.error('LinkedIn scrape failed:', e);
      alert('LinkedIn scrape failed');
      finishScrapeJob(jobId, false, 'LinkedIn failed');
    } finally {
      setLinkedinLoading((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const handleGlassdoorScrape = async (companyName: string) => {
    if (!userEmail) return;
    const jobId = startScrapeJob(`${companyName} · Glassdoor`, 'Scraping Glassdoor data');
    setGlassdoorLoading((prev) => ({ ...prev, [companyName]: true }));
    try {
      const result = await startGlassdoorScrape(companyName, userEmail, true);
      if (!result?.success) throw new Error(result?.error || 'Glassdoor scrape failed');
      const data = await pollGlassdoorResults(result, (progress, message) => {
        updateScrapeJob(jobId, { progress: Math.min(95, Math.max(10, Math.round(progress))), message });
      });
      const scrapedAt = new Date().toISOString();
      try {
        const res = await fetch(`${RESEARCH_API_URL}/company/glassdoor/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail, companyName, data }),
        });
        if (!res.ok) throw new Error('Failed to save Glassdoor data');
        const saved = await res.json();
        if (!saved.success) throw new Error(saved.error || 'Failed to save Glassdoor data');
        await loadCompanyEntities([companyName], true);
      } catch (e) {
        console.warn('Failed to persist Glassdoor data:', e);
        alert('Glassdoor save failed. Please retry.');
      }
      setGlassdoorCache((prev) => ({ ...prev, [companyName]: { data, scrapedAt } }));
      setGlassdoorDrawer({ companyName, data, scrapedAt });
      finishScrapeJob(jobId, true, 'Glassdoor ready');
    } catch (e) {
      console.error('Glassdoor scrape failed:', e);
      alert('Glassdoor scrape failed');
      finishScrapeJob(jobId, false, 'Glassdoor failed');
    } finally {
      setGlassdoorLoading((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const handleG2Scrape = async (companyName: string) => {
    if (!userEmail) return;
    const jobId = startScrapeJob(`${companyName} · G2`, 'Scraping G2 reviews');
    setG2Loading((prev) => ({ ...prev, [companyName]: true }));
    try {
      const entity = getCompanyEntity(companyName);
      const domain = entity?.domain || (entity?.website_url ? normalizeDomain(entity.website_url) : undefined);
      const result = await startG2Scrape(companyName, userEmail, true, domain);
      if (!result?.success) throw new Error(result?.error || 'G2 scrape failed');
      const data = await pollG2Results(result, (progress, message) => {
        updateScrapeJob(jobId, { progress: Math.min(95, Math.max(10, Math.round(progress))), message });
      });
      const scrapedAt = new Date().toISOString();
      try {
        const res = await fetch(`${RESEARCH_API_URL}/company/g2/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail, companyName, data }),
        });
        if (!res.ok) throw new Error('Failed to save G2 data');
        const saved = await res.json();
        if (!saved.success) throw new Error(saved.error || 'Failed to save G2 data');
        await loadCompanyEntities([companyName], true);
      } catch (e) {
        console.warn('Failed to persist G2 data:', e);
        alert('G2 save failed. Please retry.');
      }
      setG2Cache((prev) => ({ ...prev, [companyName]: { data, scrapedAt } }));
      setG2Drawer({ companyName, data, scrapedAt });
      finishScrapeJob(jobId, true, 'G2 ready');
    } catch (e) {
      console.error('G2 scrape failed:', e);
      alert('G2 scrape failed');
      finishScrapeJob(jobId, false, 'G2 failed');
    } finally {
      setG2Loading((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const normalizeInstagramUsername = (value?: string) => {
    const raw = (value || '').trim();
    if (!raw) return '';
    const cleaned = raw.replace(/.*instagram\.com\/@?/i, '').replace(/^@/, '').split('?')[0].split('/')[0];
    return cleaned.trim();
  };

  const normalizeTikTokHandle = (value?: string) => {
    const raw = (value || '').trim();
    if (!raw) return '';
    const cleaned = raw.replace(/.*tiktok\.com\/@?/i, '').replace(/^@/, '').split('?')[0].split('/')[0];
    return cleaned.trim();
  };

  const pollEcomResults = async (
    runId: string,
    datasetId?: string,
    onProgress?: (progress: number, message: string) => void,
    options?: { maxAttempts?: number; delayMs?: number }
  ) => {
    const extractResults = (payload: any): unknown[] => {
      if (Array.isArray(payload?.results)) return payload.results;
      if (Array.isArray(payload?.items)) return payload.items;
      if (Array.isArray(payload?.data)) return payload.data;
      if (Array.isArray(payload?.data?.items)) return payload.data.items;
      if (Array.isArray(payload?.data?.results)) return payload.data.results;
      if (Array.isArray(payload?.content)) return payload.content;
      return [];
    };
    const maxAttempts = options?.maxAttempts ?? 90;
    const delayMs = options?.delayMs ?? 3000;
    let emptySuccessCount = 0;
    for (let i = 0; i < maxAttempts; i += 1) {
      await sleep(delayMs);
      const progress = 30 + Math.floor((i / maxAttempts) * 50);
      onProgress?.(progress, `Checking status (${i + 1}/${maxAttempts})...`);
      const statusRes = await fetch(`${ECOM_ORCHESTRATOR_API}/check_status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ run_id: runId, dataset_id: datasetId }),
      });
      const statusData = await statusRes.json();
      if (statusData.status === 'SUCCEEDED') {
        const resultsRes = await fetch(`${ECOM_ORCHESTRATOR_API}/get_results`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dataset_id: datasetId || statusData.dataset_id }),
        });
        const resultsData = await resultsRes.json();
        const extracted = extractResults(resultsData);
        if (extracted.length === 0 && emptySuccessCount < 5) {
          emptySuccessCount += 1;
          onProgress?.(progress, 'Waiting for dataset items…');
          continue;
        }
        return extracted;
      } else if ((statusData.status === 'FAILED' || statusData.status === 'ABORTED') && (datasetId || statusData.dataset_id)) {
        const resultsRes = await fetch(`${ECOM_ORCHESTRATOR_API}/get_results`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dataset_id: datasetId || statusData.dataset_id }),
        });
        const resultsData = await resultsRes.json();
        const results = extractResults(resultsData);
        if (Array.isArray(results) && results.length > 0) return results;
        continue;
      } else if (statusData.status === 'FAILED' || statusData.status === 'ABORTED') {
        throw new Error('Scrape failed');
      }
    }
    throw new Error('Scrape timeout');
  };

  const pollWebsiteResults = async (
    contentRunId: string,
    contentDatasetId?: string,
    linksRunId?: string,
    linksDatasetId?: string,
    onProgress?: (progress: number, message: string) => void,
    options?: { maxAttempts?: number; delayMs?: number }
  ) => {
    const maxAttempts = options?.maxAttempts ?? 90;
    const delayMs = options?.delayMs ?? 3000;
    let contentReady = false;
    let linksReady = false;
    let resolvedContentDs = contentDatasetId;
    let resolvedLinksDs = linksDatasetId;

    for (let i = 0; i < maxAttempts; i += 1) {
      await sleep(delayMs);
      const progress = 30 + Math.floor((i / maxAttempts) * 50);
      onProgress?.(progress, `Checking status (${i + 1}/${maxAttempts})...`);

      if (!contentReady) {
        const statusRes = await fetch(`${RESEARCH_API_URL}/company/website/status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ run_id: contentRunId, dataset_id: resolvedContentDs }),
        });
        const statusData = await statusRes.json();
        if (statusData.status === 'SUCCEEDED') {
          contentReady = true;
          resolvedContentDs = statusData.dataset_id || resolvedContentDs;
        } else if (statusData.status === 'FAILED' || statusData.status === 'ABORTED') {
          throw new Error('Website content scrape failed');
        }
      }

      if (linksRunId && !linksReady) {
        const statusRes = await fetch(`${RESEARCH_API_URL}/company/website/status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ run_id: linksRunId, dataset_id: resolvedLinksDs }),
        });
        const statusData = await statusRes.json();
        if (statusData.status === 'SUCCEEDED') {
          linksReady = true;
          resolvedLinksDs = statusData.dataset_id || resolvedLinksDs;
        } else if (statusData.status === 'FAILED' || statusData.status === 'ABORTED') {
          throw new Error('Website links scrape failed');
        }
      } else if (!linksRunId) {
        linksReady = true;
      }

      if (contentReady && linksReady) break;
    }

    if (!contentReady) throw new Error('Website content scrape timeout');

    const contentRes = await fetch(`${RESEARCH_API_URL}/company/website/results`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dataset_id: resolvedContentDs }),
    });
    const contentData = await contentRes.json();
    const contentItems = Array.isArray(contentData?.results) ? contentData.results : [];

    let linkItems: unknown[] = [];
    if (linksRunId) {
      const linksRes = await fetch(`${RESEARCH_API_URL}/company/website/results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dataset_id: resolvedLinksDs }),
      });
      const linksData = await linksRes.json();
      linkItems = Array.isArray(linksData?.results) ? linksData.results : [];
    }

    return { contentItems, linkItems };
  };

  const handleInstagramScrape = async (companyName: string) => {
    if (!userEmail) return;
    const jobId = startScrapeJob(`${companyName} · Instagram`, 'Scraping Instagram');
    setInstagramLoading((prev) => ({ ...prev, [companyName]: true }));
    try {
      const entity = getCompanyEntity(companyName);
      const rawUrl = entity?.instagram_url || '';
      const cleanId = normalizeInstagramUsername(rawUrl);
      const useUsername = cleanId && (!rawUrl || !isPlatformUrl('instagram', rawUrl) || rawUrl.startsWith('@') || !rawUrl.includes('.'));
      const res = await fetch(`${ECOM_ORCHESTRATOR_API}/scrape/instagram`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: companyName,
          website_url: normalizeWebsiteUrl(entity?.website_url || entity?.domain || ''),
          ...(useUsername ? { username: cleanId } : {}),
          contact_name: companyName,
          userEmail,
          force_refresh: true,
        }),
      });
      const data = await res.json();
      if (!data.success && data.error_code === 'profile_not_found') {
        alert('Could not locate Instagram profile. Please add a profile URL or username.');
        finishScrapeJob(jobId, false, 'Instagram failed');
        return;
      }
      if (!data?.success || !data?.run_id) throw new Error(data?.error || 'Scrape failed');

      const results = await pollEcomResults(data.run_id, data.dataset_id, (progress, message) => {
        updateScrapeJob(jobId, { progress: Math.min(95, Math.max(10, Math.round(progress))), message });
      });
      const scrapedAt = new Date().toISOString();
      const payload = Array.isArray(results) ? { results } : results;
      try {
        const saveRes = await fetch(`${RESEARCH_API_URL}/company/instagram/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail, companyName, data: payload }),
        });
        if (!saveRes.ok) throw new Error('Failed to save Instagram data');
        const saved = await saveRes.json();
        if (!saved.success) throw new Error(saved.error || 'Failed to save Instagram data');
        await loadCompanyEntities([companyName], true);
      } catch (e) {
        console.warn('Failed to persist Instagram data:', e);
        alert('Instagram save failed. Please retry.');
      }
      setInstagramCache((prev) => ({ ...prev, [companyName]: { data: payload, scrapedAt } }));
      setInstagramDrawer({ companyName, data: payload, scrapedAt });
      finishScrapeJob(jobId, true, 'Instagram ready');
    } catch (e) {
      console.error('Instagram scrape failed:', e);
      alert('Instagram scrape failed');
      finishScrapeJob(jobId, false, 'Instagram failed');
    } finally {
      setInstagramLoading((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const handleFacebookScrape = async (companyName: string) => {
    if (!userEmail) return;
    const jobId = startScrapeJob(`${companyName} · Facebook`, 'Scraping Facebook');
    setFacebookLoading((prev) => ({ ...prev, [companyName]: true }));
    try {
      const entity = getCompanyEntity(companyName);
      const rawUrl = entity?.facebook_url || '';
      const pageUrl = isPlatformUrl('facebook', rawUrl) ? normalizePlatformUrl(rawUrl) : '';
      const res = await fetch(`${ECOM_ORCHESTRATOR_API}/scrape/facebook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: companyName,
          website_url: normalizeWebsiteUrl(entity?.website_url || entity?.domain || ''),
          ...(pageUrl ? { page_url: pageUrl } : {}),
          userEmail,
          force_refresh: true,
        }),
      });
      const data = await res.json();
      if (!data.success && data.error_code === 'profile_not_found') {
        alert('Could not locate Facebook page. Please add a page URL.');
        finishScrapeJob(jobId, false, 'Facebook failed');
        return;
      }
      if (!data?.success || !data?.run_id) throw new Error(data?.error || 'Scrape failed');

      const posts = await pollEcomResults(data.run_id, data.dataset_id, (progress, message) => {
        updateScrapeJob(jobId, { progress: Math.min(95, Math.max(10, Math.round(progress))), message });
      });

      let page: unknown[] = [];
      if (data.page_dataset_id) {
        try {
          const pageRes = await fetch(`${ECOM_ORCHESTRATOR_API}/get_results`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dataset_id: data.page_dataset_id }),
          });
          const pageData = await pageRes.json();
          page = pageData?.results ?? pageData?.items ?? pageData?.data ?? [];
        } catch (e) {
          console.warn('Failed to load Facebook page dataset:', e);
        }
      }

      const scrapedAt = new Date().toISOString();
      const payload = { page, posts };
      try {
        const saveRes = await fetch(`${RESEARCH_API_URL}/company/facebook/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail, companyName, data: payload }),
        });
        if (!saveRes.ok) throw new Error('Failed to save Facebook data');
        const saved = await saveRes.json();
        if (!saved.success) throw new Error(saved.error || 'Failed to save Facebook data');
        await loadCompanyEntities([companyName], true);
      } catch (e) {
        console.warn('Failed to persist Facebook data:', e);
        alert('Facebook save failed. Please retry.');
      }
      setFacebookCache((prev) => ({ ...prev, [companyName]: { data: payload, scrapedAt } }));
      setFacebookDrawer({ companyName, data: payload, scrapedAt });
      finishScrapeJob(jobId, true, 'Facebook ready');
    } catch (e) {
      console.error('Facebook scrape failed:', e);
      alert('Facebook scrape failed');
      finishScrapeJob(jobId, false, 'Facebook failed');
    } finally {
      setFacebookLoading((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const handleYouTubeScrape = async (companyName: string) => {
    if (!userEmail) return;
    const jobId = startScrapeJob(`${companyName} · YouTube`, 'Scraping YouTube');
    setYoutubeLoading((prev) => ({ ...prev, [companyName]: true }));
    try {
      const entity = getCompanyEntity(companyName);
      const rawUrl = entity?.youtube_url || '';
      const channelUrl = isPlatformUrl('youtube', rawUrl) ? normalizePlatformUrl(rawUrl) : '';
      const res = await fetch(`${ECOM_ORCHESTRATOR_API}/scrape/youtube`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: companyName,
          website_url: normalizeWebsiteUrl(entity?.website_url || entity?.domain || ''),
          ...(channelUrl ? { channel_url: channelUrl } : {}),
          contact_name: companyName,
          userEmail,
          force_refresh: true,
        }),
      });
      const data = await res.json();
      if (!data.success && data.error_code === 'profile_not_found') {
        alert('Could not locate YouTube channel. Please add a channel URL.');
        finishScrapeJob(jobId, false, 'YouTube failed');
        return;
      }
      if (!data?.success || !data?.run_id) throw new Error(data?.error || 'Scrape failed');

      const results = await pollEcomResults(data.run_id, data.dataset_id, (progress, message) => {
        updateScrapeJob(jobId, { progress: Math.min(95, Math.max(10, Math.round(progress))), message });
      });
      const scrapedAt = new Date().toISOString();
      try {
        const saveRes = await fetch(`${RESEARCH_API_URL}/company/youtube/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail, companyName, data: results }),
        });
        if (!saveRes.ok) throw new Error('Failed to save YouTube data');
        const saved = await saveRes.json();
        if (!saved.success) throw new Error(saved.error || 'Failed to save YouTube data');
        await loadCompanyEntities([companyName], true);
      } catch (e) {
        console.warn('Failed to persist YouTube data:', e);
        alert('YouTube save failed. Please retry.');
      }
      setYoutubeCache((prev) => ({ ...prev, [companyName]: { data: results, scrapedAt } }));
      setYoutubeDrawer({ companyName, data: results, scrapedAt });
      finishScrapeJob(jobId, true, 'YouTube ready');
    } catch (e) {
      console.error('YouTube scrape failed:', e);
      alert('YouTube scrape failed');
      finishScrapeJob(jobId, false, 'YouTube failed');
    } finally {
      setYoutubeLoading((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const handleTikTokScrape = async (companyName: string) => {
    if (!userEmail) return;
    const jobId = startScrapeJob(`${companyName} · TikTok`, 'Scraping TikTok');
    setTiktokLoading((prev) => ({ ...prev, [companyName]: true }));
    try {
      const entity = getCompanyEntity(companyName);
      const rawUrl = entity?.tiktok_url || '';
      const handle = normalizeTikTokHandle(rawUrl);
      const profileUrl = isPlatformUrl('tiktok', rawUrl) ? normalizePlatformUrl(rawUrl) : '';
      const res = await fetch(`${ECOM_ORCHESTRATOR_API}/scrape/tiktok`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: companyName,
          website_url: normalizeWebsiteUrl(entity?.website_url || entity?.domain || ''),
          ...(profileUrl ? { profile_url: profileUrl } : {}),
          ...(handle && !profileUrl ? { username: handle } : {}),
          contact_name: companyName,
          userEmail,
          force_refresh: true,
        }),
      });
      const data = await res.json();
      if (!data.success && data.error_code === 'profile_not_found') {
        alert('Could not locate TikTok profile. Please add a profile URL or username.');
        finishScrapeJob(jobId, false, 'TikTok failed');
        return;
      }
      if (!data?.success || !data?.run_id) throw new Error(data?.error || 'Scrape failed');

      const results = await pollEcomResults(data.run_id, data.dataset_id, (progress, message) => {
        updateScrapeJob(jobId, { progress: Math.min(95, Math.max(10, Math.round(progress))), message });
      });
      const scrapedAt = new Date().toISOString();
      try {
        const saveRes = await fetch(`${RESEARCH_API_URL}/company/tiktok/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail, companyName, data: results }),
        });
        if (!saveRes.ok) throw new Error('Failed to save TikTok data');
        const saved = await saveRes.json();
        if (!saved.success) throw new Error(saved.error || 'Failed to save TikTok data');
        await loadCompanyEntities([companyName], true);
      } catch (e) {
        console.warn('Failed to persist TikTok data:', e);
        alert('TikTok save failed. Please retry.');
      }
      setTiktokCache((prev) => ({ ...prev, [companyName]: { data: results, scrapedAt } }));
      setTiktokDrawer({ companyName, data: results, scrapedAt });
      finishScrapeJob(jobId, true, 'TikTok ready');
    } catch (e) {
      console.error('TikTok scrape failed:', e);
      alert('TikTok scrape failed');
      finishScrapeJob(jobId, false, 'TikTok failed');
    } finally {
      setTiktokLoading((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const handleXScrape = async (companyName: string) => {
    if (!userEmail) return;
    const jobId = startScrapeJob(`${companyName} · X`, 'Scraping X');
    setXLoading((prev) => ({ ...prev, [companyName]: true }));
    try {
      const entity = getCompanyEntity(companyName);
      const rawUrl = entity?.x_url || '';
      const profileUrl = isPlatformUrl('x', rawUrl) ? normalizePlatformUrl(rawUrl) : '';
      const res = await fetch(`${ECOM_ORCHESTRATOR_API}/scrape/x`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: companyName,
          website_url: normalizeWebsiteUrl(entity?.website_url || entity?.domain || ''),
          ...(profileUrl ? { profile_url: profileUrl } : {}),
          contact_name: companyName,
          userEmail,
          force_refresh: true,
        }),
      });
      const data = await res.json();
      if (!data.success && data.error_code === 'profile_not_found') {
        alert('Could not locate X profile. Please add a profile URL.');
        finishScrapeJob(jobId, false, 'X failed');
        return;
      }
      if (!data?.success || !data?.run_id) throw new Error(data?.error || 'Scrape failed');

      const results = await pollEcomResults(data.run_id, data.dataset_id, (progress, message) => {
        updateScrapeJob(jobId, { progress: Math.min(95, Math.max(10, Math.round(progress))), message });
      });
      const scrapedAt = new Date().toISOString();
      try {
        const saveRes = await fetch(`${RESEARCH_API_URL}/company/x/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail, companyName, data: results }),
        });
        if (!saveRes.ok) throw new Error('Failed to save X data');
        const saved = await saveRes.json();
        if (!saved.success) throw new Error(saved.error || 'Failed to save X data');
        await loadCompanyEntities([companyName], true);
      } catch (e) {
        console.warn('Failed to persist X data:', e);
        alert('X save failed. Please retry.');
      }
      setXCache((prev) => ({ ...prev, [companyName]: { data: results, scrapedAt } }));
      setXDrawer({ companyName, data: results, scrapedAt });
      finishScrapeJob(jobId, true, 'X ready');
    } catch (e) {
      console.error('X scrape failed:', e);
      alert('X scrape failed');
      finishScrapeJob(jobId, false, 'X failed');
    } finally {
      setXLoading((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const handleThreadsScrape = async (companyName: string) => {
    if (!userEmail) return;
    const jobId = startScrapeJob(`${companyName} · Threads`, 'Scraping Threads');
    setThreadsLoading((prev) => ({ ...prev, [companyName]: true }));
    try {
      const entity = getCompanyEntity(companyName);
      const rawUrl = (entity?.threads_url || '').trim();
      const profileValue = rawUrl ? (isPlatformUrl('threads', rawUrl) ? normalizePlatformUrl(rawUrl) : rawUrl) : '';
      if (!profileValue) {
        alert('Please add a Threads profile URL or handle first.');
        finishScrapeJob(jobId, false, 'Threads failed');
        return;
      }
      const res = await fetch(`${ECOM_ORCHESTRATOR_API}/scrape/threads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          urls: [profileValue],
          postsPerSource: 25,
          company_name: companyName,
          website_url: normalizeWebsiteUrl(entity?.website_url || entity?.domain || ''),
          contact_name: companyName,
          userEmail,
          force_refresh: true,
        }),
      });
      const data = await res.json();
      if (!data?.success || !data?.run_id) throw new Error(data?.error || 'Scrape failed');

      const results = await pollEcomResults(data.run_id, data.dataset_id, (progress, message) => {
        updateScrapeJob(jobId, { progress: Math.min(95, Math.max(10, Math.round(progress))), message });
      });
      const scrapedAt = new Date().toISOString();
      try {
        const saveRes = await fetch(`${RESEARCH_API_URL}/company/threads/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail, companyName, data: results }),
        });
        if (!saveRes.ok) throw new Error('Failed to save Threads data');
        const saved = await saveRes.json();
        if (!saved.success) throw new Error(saved.error || 'Failed to save Threads data');
        await loadCompanyEntities([companyName], true);
      } catch (e) {
        console.warn('Failed to persist Threads data:', e);
        alert('Threads save failed. Please retry.');
      }
      setThreadsCache((prev) => ({ ...prev, [companyName]: { data: results, scrapedAt } }));
      setThreadsDrawer({ companyName, data: results, scrapedAt });
      finishScrapeJob(jobId, true, 'Threads ready');
    } catch (e) {
      console.error('Threads scrape failed:', e);
      alert('Threads scrape failed');
      finishScrapeJob(jobId, false, 'Threads failed');
    } finally {
      setThreadsLoading((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const handleTrustpilotScrape = async (companyName: string) => {
    if (!userEmail) return;
    const jobId = startScrapeJob(`${companyName} · Trustpilot`, 'Scraping Trustpilot reviews');
    setTrustpilotLoading((prev) => ({ ...prev, [companyName]: true }));
    try {
      const entity = getCompanyEntity(companyName);
      const domain = entity?.domain || (entity?.website_url ? normalizeDomain(entity.website_url) : undefined);
      const result = await startTrustpilotScrape(companyName, userEmail, true, domain);
      if (!result?.success) throw new Error(result?.error || 'Trustpilot scrape failed');
      const data = await pollTrustpilotResults(result, (progress, message) => {
        updateScrapeJob(jobId, { progress: Math.min(95, Math.max(10, Math.round(progress))), message });
      });
      const scrapedAt = new Date().toISOString();
      try {
        const res = await fetch(`${RESEARCH_API_URL}/company/trustpilot/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail, companyName, data }),
        });
        if (!res.ok) throw new Error('Failed to save Trustpilot data');
        const saved = await res.json();
        if (!saved.success) throw new Error(saved.error || 'Failed to save Trustpilot data');
        await loadCompanyEntities([companyName], true);
      } catch (e) {
        console.warn('Failed to persist Trustpilot data:', e);
        alert('Trustpilot save failed. Please retry.');
      }
      setTrustpilotCache((prev) => ({ ...prev, [companyName]: { data, scrapedAt } }));
      setTrustpilotDrawer({ companyName, data, scrapedAt });
      finishScrapeJob(jobId, true, 'Trustpilot ready');
    } catch (e) {
      console.error('Trustpilot scrape failed:', e);
      alert('Trustpilot scrape failed');
      finishScrapeJob(jobId, false, 'Trustpilot failed');
    } finally {
      setTrustpilotLoading((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const getFallbackLinkedIn = (companyName: string) =>
    companies.find((c) => c.companyName === companyName)?.fallbackLinkedIn;


  const getCompanyEntity = (companyName: string): CompanyEntity | undefined => companyEntities[companyName];

  const dedupeContacts = (items: Contact[]) => {
    const seen = new Set<string>();
    const result: Contact[] = [];
    for (const contact of items) {
      const name = (contact.fullName || contact.Name || '').trim().toLowerCase();
      const title = (contact.title || contact.Title || '').trim().toLowerCase();
      const company = (contact.company || contact.Company || '').trim().toLowerCase();
      const key = `${name}|${title}|${company}`;
      if (!name && !title && !company) continue;
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(contact);
    }
    return result;
  };

  const groupContactsByHierarchy = (contactsForCompany: Contact[]) => {
    const uniqueContacts = dedupeContacts(contactsForCompany);
    const groups: Record<string, Contact[]> = {
      'Owner/Founder/Co Founder': [],
      'C-Suite': [],
      'Sr VPs, Managing Partners and VPs': [],
      'Directors': [],
      'Managers': [],
      'Other': [],
    };
    for (const contact of uniqueContacts) {
      const title = String(contact.title || contact.Title || '').toLowerCase();
      if (/(owner|founder|co[- ]?founder|principal|proprietor)/.test(title)) {
        groups['Owner/Founder/Co Founder'].push(contact);
      } else if (/(chief|ceo|cfo|coo|cto|cmo|cio|cso|chro|cpo|cdo|president)/.test(title)) {
        groups['C-Suite'].push(contact);
      } else if (/(vice president|\\bvp\\b|svp|evp|managing partner|managing director)/.test(title)) {
        groups['Sr VPs, Managing Partners and VPs'].push(contact);
      } else if (/director/.test(title)) {
        groups['Directors'].push(contact);
      } else if (/(manager|head of|lead)/.test(title)) {
        groups['Managers'].push(contact);
      } else {
        groups['Other'].push(contact);
      }
    }
    const sortByName = (a: Contact, b: Contact) =>
      (a.fullName || a.Name || '').localeCompare(b.fullName || b.Name || '');
    return Object.entries(groups).map(([label, items]) => ({
      label,
      items: [...items].sort(sortByName),
    }));
  };

  const getHierarchyTitleClass = (label: string) => {
    if (label === 'Owner/Founder/Co Founder') return 'text-amber-400';
    if (label === 'C-Suite') return 'text-indigo-400';
    if (label === 'Sr VPs, Managing Partners and VPs') return 'text-blue-400';
    if (label === 'Directors') return 'text-sky-400';
    if (label === 'Managers') return 'text-emerald-400';
    return 'text-gray-400';
  };

  const handleDrawerRerunSeamless = async (contact: Contact) => {
    if (!userEmail) return;
    const searchResultId = contact.searchResultId;
    if (!searchResultId) return;
    setDrawerRerunLoading((prev) => ({ ...prev, [searchResultId]: true }));
    try {
      const lead = {
        searchResultId,
        contactSearchId: contact.contactSearchId ?? 0,
        uiMatchId: contact.uiMatchId,
        name: contact.fullName || contact.Name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
        title: contact.title || contact.Title || '',
        companyName: contact.company || contact.Company || '',
        domain: contact.CompanyDomain || contact.Website || '',
        liUrl: contact.linkedIn || contact.LIProfileUrl || '',
        email: contact.email1 || contact.email || '',
        personalEmail1: contact.personalEmail1 || contact.PersonalEmail || '',
      };
      const selectedCampaignId =
        typeof window !== 'undefined' ? String(localStorage.getItem(PEPAI_CAMPAIGN_STORAGE_KEY) || '').trim() : '';
      const pepaiEnabledForRerun = Boolean(selectedCampaignId);
      await researchContact(lead as any, userEmail, {
        preserveSearchResultId: true,
        pepaiResearch: pepaiEnabledForRerun,
        extraTags: pepaiEnabledForRerun ? ['PEPAI-research'] : [],
        instantlyCampaignId: selectedCampaignId || undefined,
        targetGhlLocationId,
        skipCrmSync: !sendToCrm,
      });
      await loadContacts();
      getCredits(userEmail).then(setCredits);
    } catch (e) {
      console.error('Rerun research failed:', e);
      alert('Rerun research failed');
    } finally {
      setDrawerRerunLoading((prev) => ({ ...prev, [searchResultId]: false }));
    }
  };

  const handleCompanySearch = async (companyName: string, domain?: string) => {
    if (!userEmail) return;
    const jobId = startScrapeJob(`${companyName} · Search`, 'Searching contacts...');
    setCompanySearchLoading((prev) => ({ ...prev, [companyName]: true }));
    try {
      const filters = {
        names: [],
        companies: [companyName],
        titles: [],
        seniorities: [],
        departments: [],
        industries: [],
        locations: [],
        employeeSizes: [],
        estimatedRevenues: [],
        keywords: domain ? [domain] : [],
      };
      updateScrapeJob(jobId, { progress: 35, message: 'Finding best matches...' });
      const result = await searchLeads(filters, 0, 25);
      setCompanySearchResults((prev) => ({
        ...prev,
        [companyName]: { results: result.contacts || [], total: result.total || 0 },
      }));
      finishScrapeJob(jobId, true, `Found ${result.contacts?.length || 0} contacts`);
    } catch (e) {
      console.error('Company search failed:', e);
      alert('Company search failed');
      finishScrapeJob(jobId, false, 'Search failed');
    } finally {
      setCompanySearchLoading((prev) => ({ ...prev, [companyName]: false }));
    }
  };

  const handleCompanySearchResearch = async (lead: Lead) => {
    if (!userEmail) return;
    const searchResultId = lead.searchResultId;
    if (searchResultId) {
      setCompanySearchResearchLoading((prev) => ({ ...prev, [searchResultId]: true }));
    }
    if (credits < 1) {
      alert('Insufficient credits. Please purchase more.');
      if (searchResultId) {
        setCompanySearchResearchLoading((prev) => ({ ...prev, [searchResultId]: false }));
      }
      return;
    }

    if (!FEATURE_FLAGS.SERVER_SIDE_CREDITS) {
      try {
        const newCredits = await deductCredits(userEmail, 1, 'get_data');
        setCredits(newCredits);
      } catch {
        alert('Failed to deduct credit');
        if (searchResultId) {
          setCompanySearchResearchLoading((prev) => ({ ...prev, [searchResultId]: false }));
        }
        return;
      }
    }

    try {
      const result = await researchContact(lead, userEmail, {
        targetGhlLocationId,
        skipCrmSync: !sendToCrm,
      });
      if (result.success && result.data) {
        saveResearchedContact(lead.searchResultId, result.data);
        const targetList = currentListFilter || defaultList;
        if (targetList) {
          try {
            await fetch(`${RESEARCH_API_URL}/update-lists`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                userEmail,
                entityType: LIST_ENTITY_TYPE,
                companyNames: [lead.companyName || lead.Company || ''],
                action: 'add',
                listName: targetList,
              }),
            });
          } catch (e) {
            console.warn('Failed to add to list:', e);
          }
        }
        await loadContacts();
        try {
          const refreshed = await getCredits(userEmail);
          setCredits(refreshed);
        } catch {
          // ignore
        }
      } else {
        throw new Error('No data');
      }
    } catch (err) {
      console.error(err);
      try {
        const refreshed = await getCredits(userEmail);
        setCredits(refreshed);
      } catch {
        // ignore
      }
    }
    if (searchResultId) {
      setCompanySearchResearchLoading((prev) => ({ ...prev, [searchResultId]: false }));
    }
  };

  const handleSendCompanyToCrm = async (companyName: string) => {
    if (!userEmail || !companyName) return;
    setCrmSyncLoading(true);
    try {
      const result = await syncExistingContactsToCrm({
        userEmail,
        companyNames: [companyName],
        targetGhlLocationId,
      });
      if (!result.success) throw new Error(result.error || 'CRM sync failed');
      alert(
        `CRM Sync Complete: ${result.sent ?? 0} sent` +
          `${result.failed ? `, ${result.failed} failed` : ''}` +
          `${result.skipped ? `, ${result.skipped} skipped` : ''}.`
      );
    } catch (error) {
      console.error('Send company contacts to CRM failed', error);
      alert('Failed to send company contacts to CRM.');
    } finally {
      setCrmSyncLoading(false);
    }
  };

  // Legacy helpers/state kept for future modal parity.
  void drawerRerunLoading;
  void setDrawerRerunLoading;
  void companySearchLoading;
  void setCompanySearchLoading;
  void companySearchResults;
  void setCompanySearchResults;
  void companySearchResearchLoading;
  void setCompanySearchResearchLoading;
  void buyIntentSubmitting;
  void setBuyIntentSubmitting;
  void buyIntentStatus;
  void setBuyIntentStatus;
  void goToProfileForms;
  void summarizeInstagramData;
  void summarizeFacebookData;
  void summarizeYouTubeData;
  void summarizeTikTokData;
  void summarizeXData;
  void openBuyIntent;
  void submitBuyIntent;
  void groupContactsByHierarchy;
  void getHierarchyTitleClass;
  void handleDrawerRerunSeamless;
  void handleCompanySearch;
  void handleCompanySearchResearch;

  const companiesGridTemplate =
    '220px 160px 180px 160px 90px 90px 90px 90px 90px 90px 90px 90px 90px 90px 90px 90px 140px';
  const companyRowHeight = 64;
  const companiesListHeight = Math.min(600, Math.max(200, paginatedCompanies.length * companyRowHeight));
  type CompaniesRowProps = { items: CompanyRow[] };

  const CompaniesRow = ({ index, style, items }: RowComponentProps<CompaniesRowProps>) => {
    const c = items[index];
    if (!c) return null;
    const entity = getCompanyEntity(c.companyName) || {};
    const domain = entity.domain || c.sampleDomain || '';
    const hasWebsiteFull = Array.isArray(entity.website_ds) && entity.website_ds.length > 0;
    const companyName = decodeAmp(c.companyName);
    const industryLabel = decodeAmp(c.industry || '');

    return (
      <div
        className="companies-grid-row border-b border-gray-800 hover:bg-gray-900/50 text-sm"
        style={{
          ...style,
          display: 'grid',
          gridTemplateColumns: companiesGridTemplate,
          alignItems: 'center',
        }}
      >
        <div className="px-3 py-2">
          <button
            onClick={() => setCompanyDrawer(c)}
            className="text-white font-semibold hover:text-red-200 text-left"
          >
            {companyName}
          </button>
        </div>
        <div className="px-3 py-2 text-gray-300">{c.location || '-'}</div>
        <div className="px-3 py-2 text-gray-300">{industryLabel || '-'}</div>
        <div className="px-3 py-2 text-gray-300">
          <button
            onClick={() => setWebsiteActions({ companyName: c.companyName })}
            className="inline-flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700"
            title={domain || 'Website'}
          >
            <img
              src="https://storage.googleapis.com/msgsndr/ceLRhUdWIdfZ8UcJopVc/media/698a7dc87bc5a9806f0f5a6b.png"
              className={`w-8 h-8 inline ${hasWebsiteFull ? '' : 'grayscale opacity-40'}`}
              alt="Website"
            />
          </button>
        </div>
        <div className="px-3 py-2 text-center text-gray-200">{c.contactsCount}</div>
        <div className="px-3 py-2 text-center">
          {(() => {
            const hasCrunchbaseFull = Array.isArray(entity.crunchbase_ds) && entity.crunchbase_ds.length > 0;
            const cached = crunchbaseCache[c.companyName];
            const onView = async () => {
              if (cached) {
                setCrunchbaseDrawer({ companyName: c.companyName, data: cached.data, scrapedAt: cached.scrapedAt });
                return;
              }
              if (!hasCrunchbaseFull) return;
              const full = await fetchCrunchbaseFull(c.companyName);
              if (full) {
                const payload = { data: full, scrapedAt: entity.crunchbase_scraped_at };
                setCrunchbaseCache((prev) => ({ ...prev, [c.companyName]: payload }));
                setCrunchbaseDrawer({ companyName: c.companyName, data: full, scrapedAt: entity.crunchbase_scraped_at });
              }
            };
            const hasCrunchbaseReport = cached || hasCrunchbaseFull;
            return (
              <button
                onClick={hasCrunchbaseReport ? onView : () => handleCrunchbasePurchase(c.companyName)}
                disabled={!hasCrunchbaseReport && !!crunchbaseLoading[c.companyName]}
                className="inline-flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-50"
                title={hasCrunchbaseReport ? 'View Crunchbase report' : 'Purchase Crunchbase (2 creds)'}
              >
                <img
                  src="https://storage.googleapis.com/msgsndr/ceLRhUdWIdfZ8UcJopVc/media/698a7d6d7f6dcfbcd89e366a.png"
                  className={`w-8 h-8 inline ${hasCrunchbaseReport ? '' : 'grayscale opacity-40'}`}
                  alt="Crunchbase"
                />
              </button>
            );
          })()}
        </div>
        {(['linkedin','glassdoor','g2','trustpilot','instagram','facebook','youtube','tiktok','x','threads'] as SocialPlatform[]).map((platform) => {
          const url = (entity[socialField[platform]] as string | undefined) || (platform === 'linkedin' ? c.fallbackLinkedIn : undefined);
          const hasReport =
            platform === 'linkedin'
              ? (Array.isArray(entity.linkedin_profile_ds) && entity.linkedin_profile_ds.length > 0) ||
                (Array.isArray(entity.linkedin_posts_ds) && entity.linkedin_posts_ds.length > 0) ||
                (Array.isArray(entity.linkedin_jobs_ds) && entity.linkedin_jobs_ds.length > 0) ||
                (Array.isArray(entity.linkedin_employees_ds) && entity.linkedin_employees_ds.length > 0)
              : platform === 'glassdoor'
                ? (Array.isArray(entity.glassdoor_overview_ds) && entity.glassdoor_overview_ds.length > 0) ||
                  (Array.isArray(entity.glassdoor_reviews_ds) && entity.glassdoor_reviews_ds.length > 0) ||
                  (Array.isArray(entity.glassdoor_salaries_ds) && entity.glassdoor_salaries_ds.length > 0) ||
                  (Array.isArray(entity.glassdoor_interviews_ds) && entity.glassdoor_interviews_ds.length > 0) ||
                  (Array.isArray(entity.glassdoor_benefits_ds) && entity.glassdoor_benefits_ds.length > 0)
                : platform === 'g2'
                  ? Array.isArray(entity.g2_ds) && entity.g2_ds.length > 0
                  : platform === 'trustpilot'
                    ? Array.isArray(entity.trustpilot_ds) && entity.trustpilot_ds.length > 0
                    : platform === 'instagram'
                      ? Array.isArray(entity.instagram_ds) && entity.instagram_ds.length > 0
                      : platform === 'facebook'
                        ? Array.isArray(entity.facebook_ds) && entity.facebook_ds.length > 0
                        : platform === 'youtube'
                          ? Array.isArray(entity.youtube_ds) && entity.youtube_ds.length > 0
                          : platform === 'tiktok'
                            ? Array.isArray(entity.tiktok_ds) && entity.tiktok_ds.length > 0
                            : platform === 'threads'
                              ? Array.isArray(entity.threads_ds) && entity.threads_ds.length > 0
                              : platform === 'x'
                                ? Array.isArray(entity.x_ds) && entity.x_ds.length > 0
                                : false;
          return (
            <div key={platform} className="px-3 py-2 text-center">
              <div className="inline-flex items-center gap-2">
                {renderCompanySocialButton(platform, url, hasReport, () => setSocialDrawer({ companyName: c.companyName, platform }))}
              </div>
            </div>
          );
        })}
        <div className="px-3 py-2 text-gray-300">{formatDate(entity.linkedin_scraped_at || entity.glassdoor_scraped_at || entity.g2_scraped_at || entity.trustpilot_scraped_at || '')}</div>
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Please Log In</h1>
          <a href="/login" className="text-red-500 hover:text-red-400">Go to Login</a>
        </div>
      </div>
    );
  }

  return (
    <div className="companies-page min-h-screen bg-dark-bg">
      <Header />

      {(loading || loadingMore) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="bg-card-dark border border-gray-700 rounded-xl px-6 py-5 text-center min-w-[280px]">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500 mx-auto mb-4" />
            <p className="text-white text-lg font-semibold">
              {loading ? 'Loading companies…' : 'Loading more companies…'}
            </p>
            <p className="text-xs text-gray-400 mt-2">Please wait</p>
          </div>
        </div>
      )}

      {/* Create List Modal */}
      {createListModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/80" onClick={() => setCreateListModal(false)} />
          <div className="bg-card-dark border border-gray-700 rounded-xl max-w-md w-full mx-4 p-6 relative z-10">
            <h3 className="text-xl font-bold text-white mb-4">Create New List</h3>
            <input
              type="text"
              value={newListName}
              onChange={e => setNewListName(e.target.value)}
              placeholder="Enter list name..."
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white mb-4 focus:outline-none focus:border-red-500"
            />
            <label className="flex items-center mb-4 text-gray-300 text-sm">
              <input
                type="checkbox"
                checked={setAsDefault}
                onChange={e => setSetAsDefault(e.target.checked)}
                className="mr-2 accent-red-500"
              />
              Set as default list for new contacts
            </label>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setCreateListModal(false)} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white">Cancel</button>
              <button onClick={() => {
                if (!newListName.trim()) { alert('Please enter a list name'); return; }
                if (userLists.includes(newListName)) { alert('List already exists'); return; }
                fetch(`${RESEARCH_API_URL}/upsert-list`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    userEmail,
                    entityType: LIST_ENTITY_TYPE,
                    listName: newListName,
                  }),
                })
                  .then((res) => res.json())
                  .then((data) => {
                    if (!data.success) throw new Error(data.error || 'Failed to create list');
                    const canonicalName = String(data.listName || newListName);
                    setUserLists((prev) => [...prev, canonicalName].sort());
                    if (setAsDefault) {
                      setDefaultList(canonicalName);
                      localStorage.setItem(DEFAULT_LIST_STORAGE_KEY, canonicalName);
                    }
                    setCreateListModal(false);
                    setNewListName('');
                    setSetAsDefault(false);
                    loadUserLists();
                  })
                  .catch((err) => {
                    console.error('Failed to create company list:', err);
                    alert('Failed to create list');
                  });
              }} className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-semibold">Create List</button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Lists Modal */}
      {manageListsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/80" onClick={() => setManageListsModal(false)} />
          <div className="bg-card-dark border border-gray-700 rounded-xl max-w-lg w-full mx-4 p-6 relative z-10 max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Manage Lists</h3>
            {userLists.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No lists created yet.</p>
            ) : (
              <div className="space-y-2">
                {userLists.map(list => (
                  <div key={list} className="flex items-center justify-between bg-gray-800 rounded-lg px-4 py-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-white">{list}</span>
                      {list === defaultList && <span className="text-yellow-400">★</span>}
                    </div>
                    <div className="flex items-center space-x-2">
                      {list !== defaultList && (
                        <button onClick={() => {
                          setDefaultList(list);
                          localStorage.setItem(DEFAULT_LIST_STORAGE_KEY, list);
                        }} className="text-xs text-gray-400 hover:text-white">Set Default</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-gray-700">
              <button onClick={() => { setManageListsModal(false); setCreateListModal(true); }} className="w-full px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white text-sm">+ Create New List</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Company URL Modal */}
      {editCompanyUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/80" onClick={() => setEditCompanyUrl(null)} />
          <div className="bg-card-dark border border-gray-700 rounded-xl max-w-md w-full mx-4 p-6 relative z-10">
            <h3 className="text-xl font-bold text-white mb-2">Set Company URL</h3>
            <p className="text-sm text-gray-400 mb-4">{editCompanyUrl.companyName}</p>
            <input
              type="text"
              value={editCompanyUrlValue}
              onChange={(e) => setEditCompanyUrlValue(e.target.value)}
              placeholder="https://example.com"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white mb-4 focus:outline-none focus:border-red-500"
            />
            <div className="flex justify-end space-x-3">
              <button onClick={() => { setEditCompanyUrl(null); setEditCompanyUrlValue(''); }} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white">Cancel</button>
              <button onClick={handleSaveCompanyUrl} className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-semibold">Save URL</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Company Link Modal */}
      {editCompanyLink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/80" onClick={() => { setEditCompanyLink(null); setEditCompanyLinkValue(''); }} />
          <div className="bg-card-dark border border-gray-700 rounded-xl max-w-md w-full mx-4 p-6 relative z-10">
            <h3 className="text-xl font-bold text-white mb-2">Set {editCompanyLink.label}</h3>
            <p className="text-sm text-gray-400 mb-4">{editCompanyLink.companyName}</p>
            <input
              type="text"
              value={editCompanyLinkValue}
              onChange={(e) => setEditCompanyLinkValue(e.target.value)}
              placeholder="https://example.com"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white mb-4 focus:outline-none focus:border-red-500"
            />
            <div className="flex justify-end space-x-3">
              <button onClick={() => { setEditCompanyLink(null); setEditCompanyLinkValue(''); }} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white">Cancel</button>
              <button onClick={handleSaveCompanyLink} className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-semibold">Save URL</button>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="companies-page-header px-6 py-4 border-b border-gray-800">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">My Lists - Companies</h2>
            <p className="text-sm text-gray-400 mt-1">Companies derived from researched contacts</p>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-400">Default List:</span>
            <span className="text-sm text-red-400 font-semibold">{defaultList || 'None'}</span>
          </div>
        </div>
      </div>

      {loadingMore && !loading && (
        <div className="px-6 py-3 border-b border-gray-800 bg-gray-950">
          <div className="flex items-center gap-2 text-xs text-gray-300">
            <div className="h-2 w-2 animate-pulse rounded-full bg-yellow-400" />
            Loading additional companies…
          </div>
        </div>
      )}

      {/* Filters Bar */}
      <div className="companies-filters bg-card-dark border-b border-gray-800 px-6 py-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px] max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search company or domain..."
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(0); }}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-red-500"
            />
          </div>

          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-400">List:</label>
            <select
              value={currentListFilter}
              onChange={e => { setCurrentListFilter(e.target.value); setCurrentPage(0); }}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm min-w-[150px]"
            >
              <option value="">All Contacts</option>
              {userLists.map(list => (
                <option key={list} value={list}>{list}{list === defaultList ? ' ★' : ''}</option>
              ))}
            </select>
            <button onClick={() => setManageListsModal(true)} className="text-gray-400 hover:text-white" title="Manage Lists">
              <Settings className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-400">Sort:</label>
            <select
              value={sortKeyCompanies}
              onChange={e => { setSortKeyCompanies(e.target.value as typeof sortKeyCompanies); setCurrentPage(0); }}
              className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm min-w-[200px]"
            >
              <option value="company_asc">Company name (A-Z)</option>
              <option value="company_desc">Company name (Z-A)</option>
              <option value="contacts_desc">Contacts (high to low)</option>
              <option value="contacts_asc">Contacts (low to high)</option>
              <option value="recent_desc">Last researched (newest)</option>
              <option value="recent_asc">Last researched (oldest)</option>
            </select>
          </div>

          <label className="inline-flex items-center gap-2 rounded border border-gray-700 bg-gray-900/60 px-3 py-2 text-sm text-gray-200">
            <input
              type="checkbox"
              checked={sendToCrm}
              onChange={(e) => setSendToCrm(e.target.checked)}
              className="h-4 w-4 rounded-sm border-2 border-white bg-white accent-red-600"
            />
            <span className="font-medium">Send to my CRM</span>
          </label>

          {canChooseGhlTarget && (
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-400">Send To GHL:</label>
              <select
                value={ghlSendAsLocationId}
                disabled={!adminToken}
                onChange={async (e) => {
                  const nextValue = e.target.value;
                  setGhlSendAsLocationId(nextValue);
                  if (!adminToken || !userEmail) return;
                  setGhlSendAsSaving(true);
                  try {
                    await setGhlSendAsLocation({
                      token: adminToken,
                      email: userEmail,
                      ghlSendAsLocationId: nextValue || null,
                    });
                  } catch (error) {
                    console.error('Failed to save GHL send-as target', error);
                  } finally {
                    setGhlSendAsSaving(false);
                  }
                }}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm min-w-[280px]"
              >
                {!adminToken && <option value="">Admin token missing (re-login)</option>}
                <option value="">Send to my GHL account</option>
                {ghlTargetOptions.map((option) => (
                  <option key={option.locationId} value={option.locationId}>
                    Send to {option.label}
                  </option>
                ))}
              </select>
              {ghlSendAsSaving && <span className="text-xs text-gray-400">Saving...</span>}
            </div>
          )}

          <button
            onClick={() => { loadContacts(); loadUserLists(); }}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-400">
              Total:{' '}
              <span className="text-white font-bold">{companies.length}</span>
            </span>
            {loadingMore && !loading && (
              <span className="text-xs text-gray-400">Loading more…</span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-gray-400 text-sm">25 per page</span>
          </div>

          <div className="flex items-center space-x-1">
            <button onClick={() => setCurrentPage(p => Math.max(0, p - 1))} disabled={currentPage === 0} className="px-3 py-1 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded text-white text-sm">←</button>
            <span className="text-sm text-gray-400 px-2">Page {currentPage + 1} of {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))} disabled={currentPage >= totalPages - 1} className="px-3 py-1 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded text-white text-sm">→</button>
          </div>

          <button onClick={() => setCreateListModal(true)} className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New List
          </button>

          <button onClick={() => { /* no csv export yet */ }} className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/75 z-40 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-red-500 mx-auto mb-4" />
            <p className="text-white text-lg">Loading companies...</p>
          </div>
        </div>
      )}
      {loadingMore && !loading && (
        <div className="fixed bottom-6 right-6 bg-gray-900/90 border border-gray-700 text-gray-200 px-4 py-2 rounded-lg text-sm">
          Loading more companies…
        </div>
      )}

      {/* Table */}
      <div className="companies-table-wrap px-6 py-4">
        <div className="companies-table-shell bg-card-dark border border-gray-700 rounded-xl overflow-hidden">
          <div className="max-h-[calc(100vh-300px)] overflow-x-auto">
            {false && (<table className="w-full text-sm">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-gray-700">
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-400 uppercase bg-card-dark">Company Name</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-400 uppercase bg-card-dark">Company Location</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-400 uppercase bg-card-dark">Company Industry</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-400 uppercase bg-card-dark">Domain</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-400 uppercase bg-card-dark">Contacts</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-400 uppercase bg-card-dark">Crunchbase</th>
                  {Object.values(socialLabels).map(label => (
                    <th key={label} className="px-3 py-3 text-center text-xs font-semibold text-gray-400 uppercase bg-card-dark">{label}</th>
                  ))}
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-400 uppercase bg-card-dark">Last Researched</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-gray-400 uppercase bg-card-dark">View Contacts</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCompanies.length === 0 ? (
                  <tr>
                    <td colSpan={17} className="px-6 py-12 text-center text-gray-500">No companies found.</td>
                  </tr>
                ) : (
                  paginatedCompanies.map((c) => {
                    const entity = getCompanyEntity(c.companyName) || {};
                    const domain = entity.domain || c.sampleDomain || '';
                    const hasWebsiteFull = Array.isArray(entity.website_ds) && entity.website_ds.length > 0;
                    // website/websiteScrapedAt no longer used here

                    return (
                      <tr key={c.companyName} className="border-b border-gray-800 hover:bg-gray-900/50">
                        <td className="px-3 py-4">
                          <button
                            onClick={() => setCompanyDrawer(c)}
                            className="text-white font-semibold hover:text-red-200 text-left"
                          >
                            {c.companyName}
                          </button>
                        </td>
                        <td className="px-3 py-4 text-gray-300">{c.location || '-'}</td>
                        <td className="px-3 py-4 text-gray-300">{c.industry || '-'}</td>
                        <td className="px-3 py-4 text-gray-300">
                          <button
                            onClick={() => setWebsiteActions({ companyName: c.companyName })}
                            className="inline-flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700"
                            title={domain || 'Website'}
                          >
                            <img
                              src="https://storage.googleapis.com/msgsndr/ceLRhUdWIdfZ8UcJopVc/media/698a7dc87bc5a9806f0f5a6b.png"
                              className={`w-8 h-8 inline ${hasWebsiteFull ? '' : 'grayscale opacity-40'}`}
                              alt="Website"
                            />
                          </button>
                        </td>
                        <td className="px-3 py-4 text-center text-gray-200">{c.contactsCount}</td>
                        <td className="px-3 py-4 text-center">
                          {(() => {
                            const hasCrunchbaseFull = Array.isArray(entity.crunchbase_ds) && entity.crunchbase_ds.length > 0;
                            const cached = crunchbaseCache[c.companyName];
                            const onView = async () => {
                              if (cached) {
                                setCrunchbaseDrawer({ companyName: c.companyName, data: cached.data, scrapedAt: cached.scrapedAt });
                                return;
                              }
                              if (!hasCrunchbaseFull) return;
                              const full = await fetchCrunchbaseFull(c.companyName);
                              if (full) {
                                const payload = { data: full, scrapedAt: entity.crunchbase_scraped_at };
                                setCrunchbaseCache((prev) => ({ ...prev, [c.companyName]: payload }));
                                setCrunchbaseDrawer({ companyName: c.companyName, data: full, scrapedAt: entity.crunchbase_scraped_at });
                              }
                            };
                            const hasCrunchbaseReport = cached || hasCrunchbaseFull;
                            return (
                              <button
                                onClick={hasCrunchbaseReport ? onView : () => handleCrunchbasePurchase(c.companyName)}
                                disabled={!hasCrunchbaseReport && !!crunchbaseLoading[c.companyName]}
                                className="inline-flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-50"
                                title={hasCrunchbaseReport ? 'View Crunchbase report' : 'Purchase Crunchbase (2 creds)'}
                              >
                                <img
                                  src="https://storage.googleapis.com/msgsndr/ceLRhUdWIdfZ8UcJopVc/media/698a7d6d7f6dcfbcd89e366a.png"
                                  className={`w-8 h-8 inline ${hasCrunchbaseReport ? '' : 'grayscale opacity-40'}`}
                                  alt="Crunchbase"
                                />
                              </button>
                            );
                          })()}
                        </td>
                        {(['linkedin','glassdoor','g2','trustpilot','instagram','facebook','youtube','tiktok','x','threads'] as SocialPlatform[]).map((platform) => {
                          const url = (entity[socialField[platform]] as string | undefined) || (platform === 'linkedin' ? c.fallbackLinkedIn : undefined);
                          const hasReport =
                            platform === 'linkedin'
                              ? (Array.isArray(entity.linkedin_profile_ds) && entity.linkedin_profile_ds.length > 0) ||
                                (Array.isArray(entity.linkedin_posts_ds) && entity.linkedin_posts_ds.length > 0) ||
                                (Array.isArray(entity.linkedin_jobs_ds) && entity.linkedin_jobs_ds.length > 0) ||
                                (Array.isArray(entity.linkedin_employees_ds) && entity.linkedin_employees_ds.length > 0)
                              : platform === 'glassdoor'
                                ? (Array.isArray(entity.glassdoor_overview_ds) && entity.glassdoor_overview_ds.length > 0) ||
                                  (Array.isArray(entity.glassdoor_reviews_ds) && entity.glassdoor_reviews_ds.length > 0) ||
                                  (Array.isArray(entity.glassdoor_salaries_ds) && entity.glassdoor_salaries_ds.length > 0) ||
                                  (Array.isArray(entity.glassdoor_interviews_ds) && entity.glassdoor_interviews_ds.length > 0) ||
                                  (Array.isArray(entity.glassdoor_benefits_ds) && entity.glassdoor_benefits_ds.length > 0)
                                : platform === 'g2'
                                  ? Array.isArray(entity.g2_ds) && entity.g2_ds.length > 0
                                  : platform === 'trustpilot'
                                    ? Array.isArray(entity.trustpilot_ds) && entity.trustpilot_ds.length > 0
                                    : platform === 'instagram'
                                      ? Array.isArray(entity.instagram_ds) && entity.instagram_ds.length > 0
                                      : platform === 'facebook'
                                        ? Array.isArray(entity.facebook_ds) && entity.facebook_ds.length > 0
                                        : platform === 'youtube'
                                          ? Array.isArray(entity.youtube_ds) && entity.youtube_ds.length > 0
                                          : platform === 'tiktok'
                                            ? Array.isArray(entity.tiktok_ds) && entity.tiktok_ds.length > 0
                                            : platform === 'threads'
                                              ? Array.isArray(entity.threads_ds) && entity.threads_ds.length > 0
                                              : platform === 'x'
                                                ? Array.isArray(entity.x_ds) && entity.x_ds.length > 0
                                                : false;
                          return (
                            <td key={platform} className="px-3 py-4 text-center">
                              <div className="inline-flex items-center gap-2">
                                {renderCompanySocialButton(platform, url, hasReport, () => setSocialDrawer({ companyName: c.companyName, platform }))}
                              </div>
                            </td>
                          );
                        })}
                        <td className="px-3 py-4 text-gray-400 text-sm">
                          {c.lastResearchedAt ? new Date(c.lastResearchedAt).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-3 py-4 text-center">
                          <button
                            onClick={() => setCompanyDrawer(c)}
                            className="px-2 py-1 rounded text-white text-xs bg-gray-700 hover:bg-gray-600"
                          >
                            View Contacts
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>)}
            <div className="min-w-[2100px]">
              <div
                className="companies-grid-head sticky top-0 z-10 grid border-b border-gray-700 bg-card-dark text-xs font-semibold uppercase text-gray-400"
                style={{ gridTemplateColumns: companiesGridTemplate }}
              >
                <div className="px-3 py-3 text-left">Company Name</div>
                <div className="px-3 py-3 text-left">Company Location</div>
                <div className="px-3 py-3 text-left">Company Industry</div>
                <div className="px-3 py-3 text-left">Domain</div>
                <div className="px-3 py-3 text-center">Contacts</div>
                <div className="px-3 py-3 text-center">Crunchbase</div>
                {Object.values(socialLabels).map(label => (
                  <div key={label} className="px-3 py-3 text-center">{label}</div>
                ))}
                <div className="px-3 py-3 text-left">Last Researched</div>
              </div>
              {paginatedCompanies.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500">No companies found.</div>
              ) : (
                <List
                  rowCount={paginatedCompanies.length}
                  rowHeight={companyRowHeight}
                  rowComponent={CompaniesRow}
                  rowProps={{ items: paginatedCompanies }}
                  style={{ height: companiesListHeight, width: '100%' }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Company Modal */}
      {companyDrawer && (() => {
        const entity = getCompanyEntity(companyDrawer.companyName) || {};
        const contacts = dedupeContacts(companyDrawer.contacts || []);
        const domain =
          entity.domain ||
          (entity.website_url ? normalizeDomain(entity.website_url) : '') ||
          normalizeDomain(companyDrawer.sampleDomain || '');
        const industry =
          companyDrawer.industry ||
          formatIndustry(pickFromContacts(contacts, ['CompanyIndustries', 'CompanyIndustry', 'Industry', 'industry'])) ||
          '-';
        const employeeCount = formatFieldValue(
          pickFromContacts(contacts, ['CompanyStaffCount', 'CompanyStaffCountRange', 'employeeCount', 'EmployeeCount', 'staffCount', 'StaffCount'])
        );
        const revenueRange = formatFieldValue(
          pickFromContacts(contacts, ['CompanyRevenue', 'RevenueRange', 'revenueRange', 'CompanyRevenueRange', 'CompanyAnnualRevenue'])
        );
        const headquarters = formatLocation(
          pickFromContacts(contacts, ['CompanyLocation', 'CompanyHQ', 'Headquarters', 'headquarters', 'HeadquarterLocation'])
        );
        const foundedYear = formatFieldValue(
          pickFromContacts(contacts, ['FoundedYear', 'CompanyFoundedYear', 'companyFoundedYear', 'CompanyFounded', 'Founded'])
        );
        const websiteValueRaw =
          formatFieldValue(
            pickFromContacts(contacts, ['CompanyWebsite', 'Website', 'companyWebsite', 'companyDomain', 'CompanyDomain'])
          ) ||
          entity.website_url ||
          entity.domain ||
          companyDrawer.sampleDomain ||
          '';
        const websiteValue = websiteValueRaw ? normalizeWebsiteUrl(websiteValueRaw) : '';
        const websiteHost = (() => {
          if (!websiteValue) return '';
          try {
            return new URL(websiteValue).hostname.replace(/^www\./i, '');
          } catch {
            return websiteValue.replace(/^https?:\/\//i, '').split('/')[0].replace(/^www\./i, '');
          }
        })();
        const cachedCrunchbase = crunchbaseCache[companyDrawer.companyName];
        const entityCrunchbase = (entity as any).crunchbase_ds;
        const entityCrunchbaseData =
          Array.isArray(entityCrunchbase)
            ? (entityCrunchbase.find((item: any) => item && typeof item === 'object') || null)
            : (entityCrunchbase && typeof entityCrunchbase === 'object' ? entityCrunchbase : null);
        const cbRaw = cachedCrunchbase?.data || entityCrunchbaseData;
        const cb = cbRaw ? normalizeCrunchbaseData(cbRaw) : null;
        const crunchbaseData = cachedCrunchbase?.data ?? entityCrunchbaseData ?? null;
        const crunchbaseDataNormalized = Array.isArray(crunchbaseData) ? (crunchbaseData[0] || null) : crunchbaseData;
        const companyLogoUrl =
          (typeof (crunchbaseDataNormalized as any)?.logo_url === 'string' ? (crunchbaseDataNormalized as any).logo_url : '') ||
          (typeof (crunchbaseDataNormalized as any)?.logo === 'string' ? (crunchbaseDataNormalized as any).logo : '') ||
          (websiteHost ? `https://logo.clearbit.com/${websiteHost}` : '');
        const crunchbaseScrapedAt = cachedCrunchbase?.scrapedAt || entity.crunchbase_scraped_at || '';
        const cachedLinkedin = linkedinCache[companyDrawer.companyName];
        const cachedGlassdoor = glassdoorCache[companyDrawer.companyName];
        const cachedG2 = g2Cache[companyDrawer.companyName];
        const cachedTrustpilot = trustpilotCache[companyDrawer.companyName];
        const cachedInstagram = instagramCache[companyDrawer.companyName];
        const cachedFacebook = facebookCache[companyDrawer.companyName];
        const cachedYouTube = youtubeCache[companyDrawer.companyName];
        const cachedTikTok = tiktokCache[companyDrawer.companyName];
        const cachedX = xCache[companyDrawer.companyName];
        const cachedThreads = threadsCache[companyDrawer.companyName];
        const linkedinUrl = getFallbackLinkedIn(companyDrawer.companyName);
        const fundingTotal =
          pick(cb, ['properties'])?.funding_total?.value ||
          pick(cb, ['funding_total', 'total_funding', 'total_funding_usd', 'total_funding_usd_value'], '') ||
          extractTotalFunding(crunchbaseDataNormalized) ||
          '';
        const lastFundingType =
          pick(cb, ['overview_fields_extended'])?.last_funding_type ||
          pick(cb, ['funding_rounds_summary'])?.last_funding_type ||
          pick(cb, ['last_funding_type'], '');
        const techList = extractTechList(crunchbaseDataNormalized || crunchbaseData);
        const techCount = techList.length;
        const intentSignals = (deepFindArray(crunchbaseDataNormalized, ['bombora_surge_list']) || []).map((item: any) =>
          item?.topic_identifier?.value || item?.topic?.value || item?.name || item?.value
        ).filter(Boolean);
        const reviewAverage = computeReviewAverage(
          cachedG2?.data || (entity as any)?.g2_ds,
          cachedTrustpilot?.data || (entity as any)?.trustpilot_ds
        );
        const linkedinActivity = extractLinkedinActivity(cachedLinkedin?.data);
        const fundingSignals: string[] = [];
        if (fundingTotal) fundingSignals.push(`Total raised: ${fundingTotal}`);
        if (lastFundingType) fundingSignals.push(`Last round: ${String(lastFundingType).replace(/_/g, ' ')}`);
        const fundingDate =
          pick(cb, ['funding_rounds_summary'])?.last_funding_date ||
          pick(cb, ['last_funding_date', 'last_funding_at', 'last_funding_on'], '');
        if (fundingDate) fundingSignals.push(`Last funding date: ${formatDate(fundingDate)}`);

        const hiringSignals: string[] = [];
        const linkedinOpenRoles = extractOpenRoles(cachedLinkedin?.data);
        const glassdoorOpenRoles = extractOpenRoles(cachedGlassdoor?.data);
        if (linkedinOpenRoles !== null) hiringSignals.push(`LinkedIn open roles: ${linkedinOpenRoles}`);
        if (glassdoorOpenRoles !== null) hiringSignals.push(`Glassdoor open roles: ${glassdoorOpenRoles}`);

        const buyingSignals: string[] = [];
        if (reviewAverage) buyingSignals.push(`Review average: ${reviewAverage} / 5`);
        const reviewMentions = deepFindArray(cachedG2?.data || cachedTrustpilot?.data || (entity as any)?.g2_ds || (entity as any)?.trustpilot_ds, ['reviews', 'review_list', 'items']) || [];
        if (Array.isArray(reviewMentions) && reviewMentions.length) {
          const topics = reviewMentions
            .map((r: any) => r?.title || r?.subject || r?.summary)
            .filter(Boolean)
            .slice(0, 3);
          if (topics.length) buyingSignals.push(`Review themes: ${topics.join(', ')}`);
        }

        const socialSignals: string[] = [];
        if (linkedinActivity?.count) {
          socialSignals.push(`LinkedIn posts: ${linkedinActivity.count}${linkedinActivity.latest ? ` (latest ${formatDate(linkedinActivity.latest)})` : ''}`);
        }
        if (cachedInstagram?.data) socialSignals.push('Instagram activity captured');
        if (cachedFacebook?.data) socialSignals.push('Facebook activity captured');
        if (cachedYouTube?.data) socialSignals.push('YouTube activity captured');
        if (cachedTikTok?.data) socialSignals.push('TikTok activity captured');
        if (cachedX?.data) socialSignals.push('X activity captured');
        if (cachedThreads?.data) socialSignals.push('Threads activity captured');
        const glassdoorActivity = extractGlassdoorActivity(cachedGlassdoor?.data);
        void glassdoorActivity;
        const totalRaisedDisplay = fundingTotal || (lastFundingType ? String(lastFundingType).replace(/_/g, ' ') : '');
        const headcountLabel = employeeCount || formatFieldValue(pickFromContacts(contacts, ['CompanyStaffCountRange', 'CompanySize', 'CompanySizeBrackets']));
        const people = contacts;
        const enrichedCount = contacts.filter((c) => c.email1 || c.email || c.Email || c.linkedIn).length;
        const hasEnrichment =
          Array.isArray(entity.website_ds) ||
          Array.isArray(entity.crunchbase_ds) ||
          Array.isArray(entity.linkedin_profile_ds) ||
          Array.isArray(entity.glassdoor_overview_ds);
        const makeInitials = (value: string) =>
          value
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((p) => p[0]!.toUpperCase())
            .join('');
        const overlayClick = (event: MouseEvent<HTMLDivElement>) => {
          if (event.target === event.currentTarget) setCompanyDrawer(null);
        };
        const activityItems = [
          { label: 'Company enriched', date: entity.crunchbase_scraped_at || entity.website_scraped_at || '', color: 'var(--g-t)' },
          { label: 'LinkedIn scraped', date: entity.linkedin_scraped_at || '', color: 'var(--b-t)' },
          { label: 'Glassdoor scraped', date: entity.glassdoor_scraped_at || '', color: 'var(--p-t)' },
        ].filter((item) => item.date);
        const pepPolicyMode = (pepPolicyProfile?.approvalMode || 'manual').toUpperCase();
        const pepPolicyVersion = pepPolicyProfile?.updatedAt || '—';
        const pepPolicyLayers = pepPolicyLocked
          ? 'system > team default (locked)'
          : pepCompanyPolicyProfile
            ? 'system > user profile > company override'
            : 'system > user profile';
        const companyPolicySources = {
          system: COMPANY_PEP_SYSTEM_DEFAULT,
          tenant: {},
          user: pepPolicyProfile || {},
          company: pepCompanyPolicyProfile || {},
          runtime: {},
        };
        const companyPolicyMerged = {
          ...COMPANY_PEP_SYSTEM_DEFAULT,
          ...(pepPolicyProfile || {}),
          ...(pepCompanyPolicyProfile || {}),
        };

        return (
          <div className="ss-company-modal companies-modal overlay open" data-theme={companyModalTheme} onClick={overlayClick}>
            <div className="modal">
                <div className="mhead">
                <div className="mhead-logo" style={{ background: 'linear-gradient(135deg,#1e3a8a,#2563eb)', position: 'relative', overflow: 'hidden' }}>
                  <span style={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'white' }}>
                    {makeInitials(companyDrawer.companyName || 'CO')}
                  </span>
                  {companyLogoUrl ? (
                    <img
                      src={companyLogoUrl}
                      alt={`${companyDrawer.companyName} logo`}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : null}
                </div>
                <div>
                  <div className="mhead-name">{companyDrawer.companyName}</div>
                  <div className="mhead-sub">
                    {websiteValue && (
                      <a href={websiteValue} style={{ color: 'var(--b-t)', fontSize: 11, textDecoration: 'none' }} target="_blank" rel="noreferrer">
                        {websiteValue.replace(/^https?:\/\//, '')} ↗
                      </a>
                    )}
                    {industry && industry !== '-' && <span className="b bb">{industry}</span>}
                    {lastFundingType && <span className="b bb">{String(lastFundingType).replace(/_/g, ' ')}</span>}
                    {cb?.bombora_surge_list?.length ? <span className="b bam">High Intent</span> : null}
                    {hasEnrichment ? <span className="b bg">Enriched</span> : null}
                  </div>
                </div>
                <div className="mhead-actions">
                  <button className="btn">Export</button>
                  <button
                    className="btn btn-p"
                    onClick={() => handleSendCompanyToCrm(companyDrawer.companyName)}
                    disabled={crmSyncLoading}
                  >
                    {crmSyncLoading ? 'Sending…' : 'Send to CRM'}
                  </button>
                  <button className="close-btn" onClick={() => setCompanyDrawer(null)}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <line x1="3" y1="3" x2="13" y2="13" />
                      <line x1="13" y1="3" x2="3" y2="13" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="mtabs">
                <div className={`mtab ${companyModalTab === 'ov' ? 'on' : ''}`} onClick={() => setCompanyModalTab('ov')}>Overview</div>
                <div className={`mtab ${companyModalTab === 'ppl' ? 'on' : ''}`} onClick={() => setCompanyModalTab('ppl')}>People ({contacts.length})</div>
                <div className={`mtab ${companyModalTab === 'sig' ? 'on' : ''}`} onClick={() => setCompanyModalTab('sig')}>Signals</div>
                <div className={`mtab ${companyModalTab === 'li' ? 'on' : ''}`} onClick={() => setCompanyModalTab('li')}>LinkedIn 360</div>
                <div className={`mtab ${companyModalTab === 'gd' ? 'on' : ''}`} onClick={() => setCompanyModalTab('gd')}>Glassdoor 360</div>
                <div className={`mtab ${companyModalTab === 'cb' ? 'on' : ''}`} onClick={() => setCompanyModalTab('cb')}>Crunchbase</div>
                <div className={`mtab ${companyModalTab === 'rev' ? 'on' : ''}`} onClick={() => setCompanyModalTab('rev')}>Reviews</div>
                <div className={`mtab ${companyModalTab === 'soc' ? 'on' : ''}`} onClick={() => setCompanyModalTab('soc')}>Socials</div>
                <div className={`mtab ${companyModalTab === 'tech' ? 'on' : ''}`} onClick={() => setCompanyModalTab('tech')}>Tech Stack</div>
                <div className={`mtab ${companyModalTab === 'act' ? 'on' : ''}`} onClick={() => setCompanyModalTab('act')}>Activity</div>
              </div>

              <div className="mbody">
                {companyModalTab === 'ov' && (
                  <div className="mpad">
                    <div className="kv-row kv-row-4" style={{ marginBottom: 12 }}>
                      <div className="kv-mini">
                        <div className="kv-mini-lbl">Employees</div>
                        <div className="kv-mini-val">{headcountLabel || '—'}</div>
                        <div className="kv-mini-sub">{headcountLabel ? 'Headcount' : '—'}</div>
                      </div>
                      <div className="kv-mini">
                        <div className="kv-mini-lbl">Total Raised</div>
                        <div className="kv-mini-val">{totalRaisedDisplay || '—'}</div>
                        <div className="kv-mini-sub">{lastFundingType ? String(lastFundingType).replace(/_/g, ' ') : 'Funding'}</div>
                      </div>
                      <div className="kv-mini">
                        <div className="kv-mini-lbl">Open Roles</div>
                        <div className="kv-mini-val">{extractOpenRoles(cachedLinkedin?.data || cachedGlassdoor?.data || cbRaw) ?? '—'}</div>
                        <div className="kv-mini-sub">Hiring</div>
                      </div>
                      <div className="kv-mini">
                        <div className="kv-mini-lbl">G2 Rating</div>
                        <div className="kv-mini-val">{reviewAverage || '—'}</div>
                        <div className="kv-mini-sub">Reviews</div>
                      </div>
                    </div>
                    <div className="mcard" style={{ marginBottom: 12 }}>
                      <div className="mcard-title">P.E.P.ai Policy</div>
                      <div className="mcard-row"><span className="mcard-key">Mode</span><span className="mcard-val">{pepPolicyMode}</span></div>
                      <div className="mcard-row"><span className="mcard-key">Version</span><span className="mcard-val">{pepPolicyVersion}</span></div>
                      <div className="mcard-row"><span className="mcard-key">Scope</span><span className="mcard-val">{pepPolicyLayers}</span></div>
                      <div className="mcard-row"><span className="mcard-key">Account</span><span className="mcard-val">{pepPolicyLocked ? 'Locked (profitibull.com)' : 'Customizable'}</span></div>
                      {!pepPolicyLocked && (
                        <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                          <div>
                            <div className="mcard-key">Company Sequence Policy Override</div>
                            <textarea
                              className="w-full bg-black/25 border border-gray-700 rounded px-2 py-2 text-xs text-gray-200"
                              rows={3}
                              value={pepCompanyPolicyDraft?.sequencePolicy || ''}
                              onChange={(e) =>
                                setPepCompanyPolicyDraft((prev) => ({
                                  ...(prev || {}),
                                  sequencePolicy: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div>
                            <div className="mcard-key">Company Touchpoint Rules Override</div>
                            <textarea
                              className="w-full bg-black/25 border border-gray-700 rounded px-2 py-2 text-xs text-gray-200"
                              rows={3}
                              value={pepCompanyPolicyDraft?.touchpointRules || ''}
                              onChange={(e) =>
                                setPepCompanyPolicyDraft((prev) => ({
                                  ...(prev || {}),
                                  touchpointRules: e.target.value,
                                }))
                              }
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              className="btn"
                              disabled={pepCompanyPolicySaving || !userEmail || !companyDrawer?.companyName}
                              onClick={async () => {
                                if (!userEmail || !companyDrawer?.companyName) return;
                                setPepCompanyPolicySaving(true);
                                try {
                                  const profilePayload: PepProfile = {
                                    brandVoice: pepCompanyPolicyDraft.brandVoice || pepPolicyProfile?.brandVoice || '',
                                    icpPersona: pepCompanyPolicyDraft.icpPersona || pepPolicyProfile?.icpPersona || '',
                                    offersProducts: pepCompanyPolicyDraft.offersProducts || pepPolicyProfile?.offersProducts || '',
                                    languageDos: pepCompanyPolicyDraft.languageDos || pepPolicyProfile?.languageDos || '',
                                    languageDonts: pepCompanyPolicyDraft.languageDonts || pepPolicyProfile?.languageDonts || '',
                                    channelConstraints: pepCompanyPolicyDraft.channelConstraints || pepPolicyProfile?.channelConstraints || '',
                                    sequencePolicy: pepCompanyPolicyDraft.sequencePolicy || '',
                                    touchpointRules: pepCompanyPolicyDraft.touchpointRules || '',
                                    approvalMode: pepPolicyProfile?.approvalMode || 'manual',
                                    updatedAt: pepCompanyPolicyDraft.updatedAt || pepPolicyProfile?.updatedAt,
                                  };
                                  const res = await savePepCompanyProfile(userEmail, companyDrawer.companyName, profilePayload);
                                  if (res?.success) {
                                    setPepCompanyPolicyProfile(res.profile || null);
                                    setPepCompanyPolicyDraft(res.profile || {});
                                  }
                                } finally {
                                  setPepCompanyPolicySaving(false);
                                }
                              }}
                            >
                              {pepCompanyPolicySaving ? 'Saving...' : 'Save Company Override'}
                            </button>
                            <button
                              className="btn"
                              disabled={pepCompanyPolicySaving || !userEmail || !companyDrawer?.companyName}
                              onClick={async () => {
                                if (!userEmail || !companyDrawer?.companyName) return;
                                setPepCompanyPolicySaving(true);
                                try {
                                  const res = await savePepCompanyProfile(userEmail, companyDrawer.companyName, null);
                                  if (res?.success) {
                                    setPepCompanyPolicyProfile(null);
                                    setPepCompanyPolicyDraft({});
                                  }
                                } finally {
                                  setPepCompanyPolicySaving(false);
                                }
                              }}
                            >
                              Clear Override
                            </button>
                          </div>
                        </div>
                      )}
                      <div style={{ marginTop: 8 }}>
                        <button className="btn" onClick={() => setPepPolicyExpanded((prev) => !prev)}>
                          {pepPolicyExpanded ? 'Hide Source Detail' : 'View Source Detail'}
                        </button>
                      </div>
                      {pepPolicyExpanded && (
                        <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                          <div>
                            <div className="mcard-key">Merged</div>
                            <pre className="mtext" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(companyPolicyMerged, null, 2)}</pre>
                          </div>
                          <div>
                            <div className="mcard-key">System</div>
                            <pre className="mtext" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(companyPolicySources.system, null, 2)}</pre>
                          </div>
                          <div>
                            <div className="mcard-key">Tenant</div>
                            <pre className="mtext" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(companyPolicySources.tenant, null, 2)}</pre>
                          </div>
                          <div>
                            <div className="mcard-key">User</div>
                            <pre className="mtext" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(companyPolicySources.user, null, 2)}</pre>
                          </div>
                          <div>
                            <div className="mcard-key">Company</div>
                            <pre className="mtext" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(companyPolicySources.company, null, 2)}</pre>
                          </div>
                          <div>
                            <div className="mcard-key">Runtime</div>
                            <pre className="mtext" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(companyPolicySources.runtime, null, 2)}</pre>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mcols">
                      <div className="mcol-left">
                        <div className="mcard">
                          <div className="mcard-title">Company Info</div>
                          <div className="mcard-row"><span className="mcard-key">Industry</span><span className="mcard-val">{industry}</span></div>
                          <div className="mcard-row"><span className="mcard-key">Founded</span><span className="mcard-val">{foundedYear || '—'}</span></div>
                          <div className="mcard-row"><span className="mcard-key">HQ</span><span className="mcard-val">{companyDrawer.location || headquarters || '—'}</span></div>
                          <div className="mcard-row"><span className="mcard-key">Revenue</span><span className="mcard-val dim">{revenueRange || '—'}</span></div>
                          <div className="mcard-row"><span className="mcard-key">Domain</span><span className="mcard-val dim">{domain || '—'}</span></div>
                        </div>
                        <div className="mcard">
                          <div className="mcard-title">Fit Scores</div>
                          <div className="score-row">
                            <div className="score-head"><span style={{ color: 'var(--t1)' }}>ICP Match</span><span style={{ fontWeight: 700, color: 'var(--g-t)', fontSize: 10.5 }}>{entity.custom_kpi?.indicator ?? '—'}</span></div>
                            <div className="score-track"><div className="score-fill" style={{ width: `${Math.min(100, Number(entity.custom_kpi?.indicator || 0))}%`, background: '#16a34a', boxShadow: '0 0 5px rgba(22,163,74,.4)' }} /></div>
                          </div>
                          <div className="score-row">
                            <div className="score-head"><span style={{ color: 'var(--t1)' }}>Intent</span><span style={{ fontWeight: 700, color: 'var(--a-t)', fontSize: 10.5 }}>{cb?.bombora_surge_list?.length ? 76 : '—'}</span></div>
                            <div className="score-track"><div className="score-fill" style={{ width: `${cb?.bombora_surge_list?.length ? 76 : 0}%`, background: '#d97706', boxShadow: '0 0 5px rgba(217,119,6,.4)' }} /></div>
                          </div>
                          <div className="score-row">
                            <div className="score-head"><span style={{ color: 'var(--t1)' }}>Engagement</span><span style={{ fontWeight: 700, color: 'var(--b-t)', fontSize: 10.5 }}>{contacts.length ? 58 : '—'}</span></div>
                            <div className="score-track"><div className="score-fill" style={{ width: `${contacts.length ? 58 : 0}%`, background: '#2563eb', boxShadow: '0 0 5px rgba(37,99,235,.4)' }} /></div>
                          </div>
                        </div>
                        <div className="mcard">
                          <div className="mcard-title">Data Sources</div>
                          <div className="tag-row">
                            {Array.isArray(entity.linkedin_profile_ds) && entity.linkedin_profile_ds.length > 0 && <span className="b bg">LinkedIn</span>}
                            {Array.isArray(entity.crunchbase_ds) && entity.crunchbase_ds.length > 0 && <span className="b bg">Crunchbase</span>}
                            {Array.isArray(entity.glassdoor_overview_ds) && entity.glassdoor_overview_ds.length > 0 && <span className="b bg">Glassdoor</span>}
                            {Array.isArray(entity.g2_ds) && entity.g2_ds.length > 0 && <span className="b bg">G2</span>}
                            {!hasEnrichment && <span className="b bn">No sources yet</span>}
                          </div>
                        </div>
                      </div>
                      <div className="mcol-right">
                        <div className="mcard">
                          <div className="mtext-title">About</div>
                          <div className="mtext">
                            {pick(cb, ['overview_description', 'description'])?.description ||
                              pick(cb, ['description'], '') ||
                              'No company overview available yet.'}
                          </div>
                        </div>
                        <div className="mcard">
                          <div className="mtext-title">Recommended Approach <span className="b bpu" style={{ marginLeft: 5, verticalAlign: 'middle' }}>AI</span></div>
                          <div className="mtext">
                            {cb?.overview_description?.description ||
                              `Focus on ${industry !== '-' ? industry : 'industry'} priorities, highlight data quality wins, and reach out to the primary buying group first.`}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {companyModalTab === 'ppl' && (
                  <div className="mpad">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <span style={{ fontSize: 12, color: 'var(--t1)' }}>
                        <strong style={{ color: 'var(--t0)' }}>{contacts.length}</strong> contacts found ·{' '}
                        <strong style={{ color: 'var(--t0)' }}>{enrichedCount}</strong> enriched
                      </span>
                      <button className="btn btn-p" style={{ fontSize: 11 }}>Find More Contacts</button>
                    </div>
                    <div className="mcard" style={{ padding: '4px 6px', maxHeight: 420, overflowY: 'auto' }}>
                      {(() => {
                        const deptOrder = [
                          'Org',
                          'Sales',
                          'Marketing',
                          'Operations',
                          'Finance',
                          'Accounting',
                          'Revenue',
                          'Support',
                          'Legal',
                          'Engineering',
                          'Human Resources',
                          'IT',
                          'Other',
                        ];
                        const tiers = ['C-Level', 'VPs', 'Directors', 'Managers', 'Other'];
                        const normalizeDept = (dept: string) => (dept === 'Org' ? 'Org' : dept);
                        const filtered = peopleDeptTab === 'Org'
                          ? people
                          : people.filter((p) => normalizeDept(classifyDepartment(p.title || p.Title)) === peopleDeptTab);
                        const tierGroups: Record<string, typeof people> = {};
                        tiers.forEach((t) => { tierGroups[t] = []; });
                        filtered.forEach((person) => {
                          const title = person.title || person.Title || '';
                          const tier = classifySeniority(title);
                          if (!tierGroups[tier]) tierGroups[tier] = [];
                          tierGroups[tier].push(person);
                        });
                        return (
                          <>
                            <div className="mtabs" style={{ marginBottom: 10 }}>
                              {deptOrder.map((dept) => (
                                <div
                                  key={dept}
                                  className={`mtab ${peopleDeptTab === dept ? 'on' : ''}`}
                                  onClick={() => setPeopleDeptTab(dept)}
                                >
                                  {dept}
                                </div>
                              ))}
                            </div>
                            <div className="people-list">
                              {tiers.map((tier) => {
                                const tierList = tierGroups[tier] || [];
                                const isOpen = peopleTierOpen[tier] ?? false;
                                return (
                                  <div key={tier}>
                                    <div
                                      className="person-row"
                                      style={{ fontWeight: 600, cursor: 'pointer' }}
                                      onClick={() => setPeopleTierOpen((prev) => ({ ...prev, [tier]: !isOpen }))}
                                    >
                                      <div style={{ flex: 1 }}>
                                        <div className="person-name">{tier}</div>
                                        <div className="person-title">{tierList.length} contacts</div>
                                      </div>
                                      <span className="b bn" style={{ flexShrink: 0 }}>{isOpen ? 'Hide' : 'Show'}</span>
                                    </div>
                                    {isOpen && (
                                      tierList.length ? tierList.map((person, idx) => {
                                        const name = getContactName(person) || 'Unknown';
                                        const title = person.title || person.Title || 'Contact';
                                        const initials = makeInitials(name || 'CN');
                                        return (
                                          <div className="person-row" key={`${tier}-${name}-${idx}`} style={{ marginLeft: 8 }}>
                                            <div className="ava-sm" style={{ background: 'linear-gradient(135deg,#1e40af,#2563eb)' }}>{initials}</div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                              <div className="person-name">{name}</div>
                                              <div className="person-title">{title}</div>
                                            </div>
                                            <span className="b bg" style={{ flexShrink: 0 }}>{person.company ? 'Enriched' : 'Lead'}</span>
                                          </div>
                                        );
                                      }) : (
                                        <div className="person-row" style={{ marginLeft: 8 }}>
                                          <div className="person-title">No contacts in this tier.</div>
                                        </div>
                                      )
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {companyModalTab === 'sig' && (
                  <div className="mpad">
                    <div className="mcard">
                      <div className="mtext-title">
                        Company Signals <span className="b bam" style={{ marginLeft: 5, verticalAlign: 'middle' }}>{intentSignals.length + fundingSignals.length + hiringSignals.length + buyingSignals.length + socialSignals.length} active</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 10 }}>
                        <div>
                          <div className="sec-label" style={{ marginBottom: 6 }}>Buying Signals</div>
                          <div className="signals">
                            {buyingSignals.map((topic: any, idx: number) => (
                              <div key={`signal-buy-${idx}`} className="signal-item">
                                <div className="signal-dot" style={{ background: '#d97706', boxShadow: '0 0 5px rgba(217,119,6,.5)' }} />
                                <span className="signal-text">{String(topic)}</span>
                              </div>
                            ))}
                            {!buyingSignals.length && (
                              <div className="signal-item">
                                <div className="signal-dot" style={{ background: '#d97706', boxShadow: '0 0 5px rgba(217,119,6,.5)' }} />
                                <span className="signal-text">No buying signals captured yet.</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="sec-label" style={{ marginBottom: 6 }}>Hiring Signals</div>
                          <div className="signals">
                            {hiringSignals.map((item: string, idx: number) => (
                              <div key={`signal-hiring-${idx}`} className="signal-item">
                                <div className="signal-dot" style={{ background: '#2563eb', boxShadow: '0 0 5px rgba(37,99,235,.4)' }} />
                                <span className="signal-text">{item}</span>
                              </div>
                            ))}
                            {!hiringSignals.length && (
                              <div className="signal-item">
                                <div className="signal-dot" style={{ background: '#2563eb', boxShadow: '0 0 5px rgba(37,99,235,.4)' }} />
                                <span className="signal-text">No hiring signals captured yet.</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="sec-label" style={{ marginBottom: 6 }}>Intent Signals</div>
                          <div className="signals">
                            {intentSignals.map((topic: any, idx: number) => (
                              <div key={`signal-intent-${idx}`} className="signal-item">
                                <div className="signal-dot" style={{ background: '#f59e0b', boxShadow: '0 0 5px rgba(245,158,11,.4)' }} />
                                <span className="signal-text">{String(topic)}</span>
                              </div>
                            ))}
                            {!intentSignals.length && (
                              <div className="signal-item">
                                <div className="signal-dot" style={{ background: '#f59e0b', boxShadow: '0 0 5px rgba(245,158,11,.4)' }} />
                                <span className="signal-text">No intent signals captured yet.</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="sec-label" style={{ marginBottom: 6 }}>Funding Signals</div>
                          <div className="signals">
                            {fundingSignals.map((item: string, idx: number) => (
                              <div key={`signal-funding-${idx}`} className="signal-item">
                                <div className="signal-dot" style={{ background: '#22c55e', boxShadow: '0 0 5px rgba(34,197,94,.4)' }} />
                                <span className="signal-text">{item}</span>
                              </div>
                            ))}
                            {!fundingSignals.length && (
                              <div className="signal-item">
                                <div className="signal-dot" style={{ background: '#22c55e', boxShadow: '0 0 5px rgba(34,197,94,.4)' }} />
                                <span className="signal-text">No funding signals captured yet.</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <div className="sec-label" style={{ marginBottom: 6 }}>Social Signals</div>
                          <div className="signals">
                            {socialSignals.map((item: string, idx: number) => (
                              <div key={`signal-social-${idx}`} className="signal-item">
                                <div className="signal-dot" style={{ background: '#a855f7', boxShadow: '0 0 5px rgba(168,85,247,.4)' }} />
                                <span className="signal-text">{item}</span>
                              </div>
                            ))}
                            {!socialSignals.length && (
                              <div className="signal-item">
                                <div className="signal-dot" style={{ background: '#a855f7', boxShadow: '0 0 5px rgba(168,85,247,.4)' }} />
                                <span className="signal-text">No social signals captured yet.</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {companyModalTab === 'li' && (
                  <div className="mpad">
                    <div className="mcard" style={{ padding: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div className="mtext-title">LinkedIn 360</div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span className="b bn">Open Roles: {extractOpenRoles(cachedLinkedin?.data) ?? '—'}</span>
                          {linkedinUrl ? (
                            <button className="btn btn-p" onClick={() => handleLinkedInScrape(companyDrawer.companyName, linkedinUrl)}>
                              {linkedinLoading[companyDrawer.companyName] ? 'Running…' : 'Run LinkedIn 360'}
                            </button>
                          ) : (
                            <span className="b bn">Missing LinkedIn URL</span>
                          )}
                        </div>
                      </div>
                      {cachedLinkedin?.data ? (
                        <LinkedIn360Content data={cachedLinkedin.data} companyName={companyDrawer.companyName} />
                      ) : (
                        <div className="mtext">No LinkedIn 360 data yet.</div>
                      )}
                    </div>
                  </div>
                )}

                {companyModalTab === 'gd' && (
                  <div className="mpad">
                    <div className="mcard" style={{ padding: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div className="mtext-title">Glassdoor 360</div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span className="b bn">Open Roles: {extractOpenRoles(cachedGlassdoor?.data) ?? '—'}</span>
                          <button className="btn btn-p" onClick={() => handleGlassdoorScrape(companyDrawer.companyName)}>
                            {glassdoorLoading[companyDrawer.companyName] ? 'Running…' : 'Run Glassdoor 360'}
                          </button>
                        </div>
                      </div>
                      {cachedGlassdoor?.data ? (
                        <Glassdoor360Content data={cachedGlassdoor.data} />
                      ) : (
                        <div className="mtext">No Glassdoor data yet.</div>
                      )}
                    </div>
                  </div>
                )}

                {companyModalTab === 'cb' && (
                  <div className="mpad">
                    {crunchbaseData ? (
                      <div className="mcard" style={{ padding: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <div className="mtext-title">Crunchbase Report</div>
                          {crunchbaseScrapedAt && (
                            <span className="b bg">Scraped {formatDate(crunchbaseScrapedAt)}</span>
                          )}
                        </div>
                        <CrunchbaseContent data={crunchbaseData} companyName={companyDrawer.companyName} />
                      </div>
                    ) : (
                      <div className="mcard" style={{ padding: 16 }}>
                        <div className="mtext-title">Crunchbase Report</div>
                        <div className="mtext" style={{ marginTop: 8 }}>
                          No Crunchbase data yet. Run a Crunchbase scrape to populate this tab.
                        </div>
                        <div style={{ marginTop: 12 }}>
                          <button className="btn btn-p" onClick={() => handleCrunchbasePurchase(companyDrawer.companyName)}>
                            Run Crunchbase Scrape
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {companyModalTab === 'rev' && (
                  <div className="mpad">
                    {(() => {
                      const combinedRatings = [
                        ...collectRatings(cachedG2?.data),
                        ...collectRatings(cachedTrustpilot?.data),
                      ];
                      const average = combinedRatings.length
                        ? (combinedRatings.reduce((a, b) => a + b, 0) / combinedRatings.length).toFixed(2)
                        : null;
                      return (
                        <div className="mcard" style={{ padding: 16, marginBottom: 12 }}>
                          <div className="mtext-title">Review Average</div>
                          <div className="mtext">{average ? `${average} / 5` : '—'}</div>
                        </div>
                      );
                    })()}
                    <div className="mcard" style={{ padding: 16, marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div className="mtext-title">G2 Reviews</div>
                        <button className="btn btn-p" onClick={() => handleG2Scrape(companyDrawer.companyName)}>
                          {g2Loading[companyDrawer.companyName] ? 'Running…' : 'Run G2'}
                        </button>
                      </div>
                      {cachedG2?.data ? <G2MultiContent data={cachedG2.data} /> : <div className="mtext">No G2 data yet.</div>}
                    </div>
                    <div className="mcard" style={{ padding: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <div className="mtext-title">Trustpilot</div>
                        <button className="btn btn-p" onClick={() => handleTrustpilotScrape(companyDrawer.companyName)}>
                          {trustpilotLoading[companyDrawer.companyName] ? 'Running…' : 'Run Trustpilot'}
                        </button>
                      </div>
                      {cachedTrustpilot?.data ? <TrustpilotOnlyContent data={cachedTrustpilot.data} /> : <div className="mtext">No Trustpilot data yet.</div>}
                    </div>
                  </div>
                )}

                {companyModalTab === 'soc' && (
                  <div className="mpad">
                    <div className="mtabs" style={{ marginBottom: 12 }}>
                      <div className={`mtab ${companySocialTab === 'ig' ? 'on' : ''}`} onClick={() => setCompanySocialTab('ig')}>IG</div>
                      <div className={`mtab ${companySocialTab === 'fb' ? 'on' : ''}`} onClick={() => setCompanySocialTab('fb')}>FB</div>
                      <div className={`mtab ${companySocialTab === 'yt' ? 'on' : ''}`} onClick={() => setCompanySocialTab('yt')}>YT</div>
                      <div className={`mtab ${companySocialTab === 'tt' ? 'on' : ''}`} onClick={() => setCompanySocialTab('tt')}>TT</div>
                      <div className={`mtab ${companySocialTab === 'x' ? 'on' : ''}`} onClick={() => setCompanySocialTab('x')}>X</div>
                      <div className={`mtab ${companySocialTab === 'threads' ? 'on' : ''}`} onClick={() => setCompanySocialTab('threads')}>Threads</div>
                    </div>

                    {companySocialTab === 'ig' && (
                      <div className="mcard" style={{ padding: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <div className="mtext-title">Instagram</div>
                          <button className="btn btn-p" onClick={() => handleInstagramScrape(companyDrawer.companyName)}>
                            {instagramLoading[companyDrawer.companyName] ? 'Running…' : 'Run Instagram'}
                          </button>
                        </div>
                        {cachedInstagram?.data ? <InstagramContent data={cachedInstagram.data} /> : <div className="mtext">No Instagram data yet.</div>}
                      </div>
                    )}

                    {companySocialTab === 'fb' && (
                      <div className="mcard" style={{ padding: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <div className="mtext-title">Facebook</div>
                          <button className="btn btn-p" onClick={() => handleFacebookScrape(companyDrawer.companyName)}>
                            {facebookLoading[companyDrawer.companyName] ? 'Running…' : 'Run Facebook'}
                          </button>
                        </div>
                        {cachedFacebook?.data ? <FacebookContent data={cachedFacebook.data} /> : <div className="mtext">No Facebook data yet.</div>}
                      </div>
                    )}

                    {companySocialTab === 'yt' && (
                      <div className="mcard" style={{ padding: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <div className="mtext-title">YouTube</div>
                          <button className="btn btn-p" onClick={() => handleYouTubeScrape(companyDrawer.companyName)}>
                            {youtubeLoading[companyDrawer.companyName] ? 'Running…' : 'Run YouTube'}
                          </button>
                        </div>
                        {cachedYouTube?.data ? <YouTubeContent data={cachedYouTube.data} /> : <div className="mtext">No YouTube data yet.</div>}
                      </div>
                    )}

                    {companySocialTab === 'tt' && (
                      <div className="mcard" style={{ padding: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <div className="mtext-title">TikTok</div>
                          <button className="btn btn-p" onClick={() => handleTikTokScrape(companyDrawer.companyName)}>
                            {tiktokLoading[companyDrawer.companyName] ? 'Running…' : 'Run TikTok'}
                          </button>
                        </div>
                        {cachedTikTok?.data ? <TikTokContent data={cachedTikTok.data} /> : <div className="mtext">No TikTok data yet.</div>}
                      </div>
                    )}

                    {companySocialTab === 'x' && (
                      <div className="mcard" style={{ padding: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <div className="mtext-title">X</div>
                          <button className="btn btn-p" onClick={() => handleXScrape(companyDrawer.companyName)}>
                            {xLoading[companyDrawer.companyName] ? 'Running…' : 'Run X'}
                          </button>
                        </div>
                        {cachedX?.data ? <XContent data={cachedX.data} /> : <div className="mtext">No X data yet.</div>}
                      </div>
                    )}

                    {companySocialTab === 'threads' && (
                      <div className="mcard" style={{ padding: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <div className="mtext-title">Threads</div>
                          <button className="btn btn-p" onClick={() => handleThreadsScrape(companyDrawer.companyName)}>
                            {threadsLoading[companyDrawer.companyName] ? 'Running…' : 'Run Threads'}
                          </button>
                        </div>
                        {cachedThreads?.data ? <ThreadsContent data={cachedThreads.data} /> : <div className="mtext">No Threads data yet.</div>}
                      </div>
                    )}
                  </div>
                )}

                {companyModalTab === 'tech' && (
                  <div className="mpad">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <span style={{ fontSize: 12, color: 'var(--t1)' }}>
                        <strong style={{ color: 'var(--t0)' }}>{techCount}</strong> tools detected
                      </span>
                      {intentSignals.length ? <span className="b bam">Intent signals active</span> : null}
                    </div>
                    <div className="sec-label">Core Stack</div>
                    <div className="tech-g" style={{ marginBottom: 12 }}>
                      {techList.slice(0, 12).map((tool: any, idx: number) => {
                        const toolName =
                          (typeof tool === 'string' && tool) ||
                          tool?.name ||
                          tool?.value ||
                          tool?.tech ||
                          tool?.product ||
                          tool?.identifier?.value ||
                          tool?.label ||
                          tool?.title ||
                          '';
                        const toolCategory =
                          tool?.category?.value ||
                          tool?.category ||
                          tool?.type ||
                          tool?.group ||
                          '';
                        return (
                        <div className="tech-item" key={`tech-${idx}`}>
                          <div className="tech-name">{toolName || 'Tool'}</div>
                          <div className="tech-cat">{toolCategory || 'Category'}</div>
                        </div>
                        );
                      })}
                      {!techList.length && (
                        <div className="tech-item">
                          <div className="tech-name">No stack data yet</div>
                          <div className="tech-cat">Run Website Scrape</div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {companyModalTab === 'act' && (
                  <div className="mpad">
                    <div className="mcard">
                      <div className="feed">
                        {activityItems.length ? activityItems.map((item, idx) => (
                          <div className="feed-item" key={`act-${idx}`}>
                            <div className="feed-dot-col">
                              <div className="feed-dot" style={{ background: item.color, boxShadow: '0 0 5px rgba(96,165,250,.4)' }} />
                              {idx !== activityItems.length - 1 && <div className="feed-line" />}
                            </div>
                            <div>
                              <div className="feed-action">{item.label}</div>
                              <div className="feed-meta">{formatDate(item.date)}</div>
                            </div>
                          </div>
                        )) : (
                          <div className="feed-item">
                            <div className="feed-action">No activity logged yet.</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mfoot">
                <span className="mfoot-meta">Enriched {formatDate(entity.crunchbase_scraped_at || entity.website_scraped_at || '')} · {hasEnrichment ? 'Sources available' : 'No sources yet'}</span>
                <div className="mfoot-actions">
                  <button className="btn" onClick={() => setWebsiteActions({ companyName: companyDrawer.companyName })}>Re-enrich</button>
                  {websiteValue && (
                    <button className="btn" onClick={() => window.open(websiteValue, '_blank')}>View Full Profile ↗</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {false && companyDrawer && null}
      {/* Legacy modal removed
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/80" onClick={() => setCompanyDrawer(null)} />
          <div className="bg-card-dark border border-gray-700 rounded-xl max-w-5xl w-full mx-4 p-6 relative z-10 max-h-[85vh] overflow-y-auto">
            {(() => {
              const entity = getCompanyEntity(companyDrawer.companyName) || {};
              const contacts = dedupeContacts(companyDrawer.contacts || []);
              const domain =
                entity.domain ||
                (entity.website_url ? normalizeDomain(entity.website_url) : '') ||
                normalizeDomain(companyDrawer.sampleDomain || '');
              const industry =
                companyDrawer.industry ||
                formatIndustry(pickFromContacts(contacts, ['CompanyIndustries', 'CompanyIndustry', 'Industry', 'industry'])) ||
                '-';
              const employeeCount = formatFieldValue(
                pickFromContacts(contacts, ['CompanyStaffCount', 'CompanyStaffCountRange', 'employeeCount', 'EmployeeCount', 'staffCount', 'StaffCount'])
              );
              const revenueRange = formatFieldValue(
                pickFromContacts(contacts, ['CompanyRevenue', 'RevenueRange', 'revenueRange', 'CompanyRevenueRange', 'CompanyAnnualRevenue'])
              );
              const headquarters = formatLocation(
                pickFromContacts(contacts, ['CompanyLocation', 'CompanyHQ', 'Headquarters', 'headquarters', 'HeadquarterLocation'])
              );
              const foundedYear = formatFieldValue(
                pickFromContacts(contacts, ['FoundedYear', 'CompanyFoundedYear', 'companyFoundedYear', 'CompanyFounded', 'Founded'])
              );
              const websiteValueRaw =
                formatFieldValue(
                  pickFromContacts(contacts, ['CompanyWebsite', 'Website', 'companyWebsite', 'companyDomain', 'CompanyDomain'])
                ) ||
                entity.website_url ||
                entity.domain ||
                companyDrawer.sampleDomain ||
                '';
              const websiteValue = websiteValueRaw ? normalizeWebsiteUrl(websiteValueRaw) : '';
              const keywordLink = domain
                ? `http://www.keywordspy.com/research/search.aspx?q=${encodeURIComponent(domain)}&tab=domain-overview`
                : '';
              const technologyUsage = formatFieldValue(
                pickFromContacts(contacts, ['CompanyTechnologies', 'CompanyTechnologiesCount', 'TechnologyUsage', 'TechnologyUsageCount'])
              );
              const companySizeBrackets = formatFieldValue(
                pickFromContacts(contacts, ['CompanyStaffCountRange', 'CompanySize', 'CompanySizeBrackets'])
              );
              const geographicTags = formatFieldValue(
                pickFromContacts(contacts, ['GeographicTags', 'GeoTags', 'geographicTags', 'locationTags', 'LocationTags'])
              );
              const industryCodes = formatFieldValue(
                pickFromContacts(contacts, ['IndustryCodes', 'NAICS', 'SIC', 'sic', 'naics'])
              );
              const hasWebsiteFull = Array.isArray(entity.website_ds) && entity.website_ds.length > 0;
              const hasCrunchbaseFull = Array.isArray(entity.crunchbase_ds) && entity.crunchbase_ds.length > 0;
              const cachedCrunchbase = crunchbaseCache[companyDrawer.companyName];
              const hasCrunchbaseReport = cachedCrunchbase || hasCrunchbaseFull;

              const iconButtonClass = (active: boolean) =>
                `inline-flex items-center justify-center rounded bg-gray-800 hover:bg-gray-700 ${active ? '' : 'opacity-40 grayscale'}`;

              return (
                <>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-white">Company Profile</h3>
                      <p className="text-sm text-gray-400">{companyDrawer.companyName}</p>
                    </div>
                    <button onClick={() => setCompanyDrawer(null)} className="text-gray-400 hover:text-white text-2xl">×</button>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {['FitMatch', 'IndustryMatch', 'TitleMatch'].map((label) => (
                      <div key={label} className="bg-gray-800 rounded-lg p-3 text-center">
                        <div className="text-xs text-gray-500">{label}</div>
                        <button
                          onClick={goToProfileForms}
                          className="mt-2 px-2 py-1 text-xs rounded bg-gray-700 text-white hover:bg-gray-600"
                        >
                          Complete Forms
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-4">
                      <div className="text-xs uppercase text-gray-500">Industry</div>
                      <div className="text-white mt-1">{industry || '-'}</div>
                    </div>
                    <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-4">
                      <div className="text-xs uppercase text-gray-500">Employee Count</div>
                      <div className="text-white mt-1">{employeeCount || '-'}</div>
                    </div>
                    <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-4">
                      <div className="text-xs uppercase text-gray-500">Company</div>
                      <div className="text-white mt-1">{companyDrawer.companyName}</div>
                    </div>
                    <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-4">
                      <div className="text-xs uppercase text-gray-500">Location</div>
                      <div className="text-white mt-1">{companyDrawer.location || headquarters || '-'}</div>
                    </div>
                  </div>

                  <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-4 mb-6">
                    <div className="text-sm text-gray-400 mb-3">Company Signals</div>
                    <div className="flex flex-wrap gap-3 items-center">
                      <button
                        onClick={() => setWebsiteActions({ companyName: companyDrawer.companyName })}
                        className={iconButtonClass(hasWebsiteFull)}
                        title="Website Scrape"
                      >
                        <img
                          src="https://storage.googleapis.com/msgsndr/ceLRhUdWIdfZ8UcJopVc/media/698a7dc87bc5a9806f0f5a6b.png"
                          className="w-8 h-8"
                          alt="Website"
                        />
                      </button>
                      <button
                        onClick={hasCrunchbaseReport ? async () => {
                          if (cachedCrunchbase) {
                            setCrunchbaseDrawer({ companyName: companyDrawer.companyName, data: cachedCrunchbase.data, scrapedAt: cachedCrunchbase.scrapedAt });
                            return;
                          }
                          const full = await fetchCrunchbaseFull(companyDrawer.companyName);
                          if (full) {
                            const payload = { data: full, scrapedAt: entity.crunchbase_scraped_at };
                            setCrunchbaseCache((prev) => ({ ...prev, [companyDrawer.companyName]: payload }));
                            setCrunchbaseDrawer({ companyName: companyDrawer.companyName, data: full, scrapedAt: entity.crunchbase_scraped_at });
                          }
                        } : () => handleCrunchbasePurchase(companyDrawer.companyName)}
                        className={iconButtonClass(!!hasCrunchbaseReport)}
                        title={hasCrunchbaseReport ? 'Crunchbase Report' : 'Purchase Crunchbase (2 creds)'}
                      >
                        <img
                          src="https://storage.googleapis.com/msgsndr/ceLRhUdWIdfZ8UcJopVc/media/698a7d6d7f6dcfbcd89e366a.png"
                          className="w-8 h-8"
                          alt="Crunchbase"
                        />
                      </button>
                      {(['linkedin','glassdoor','g2','trustpilot','instagram','facebook','youtube','tiktok','x','threads'] as SocialPlatform[]).map((platform) => {
                        const url =
                          (entity[socialField[platform]] as string | undefined) ||
                          (platform === 'linkedin' ? getFallbackLinkedIn(companyDrawer.companyName) : undefined);
                        const hasReport =
                          platform === 'linkedin'
                            ? (Array.isArray(entity.linkedin_profile_ds) && entity.linkedin_profile_ds.length > 0) ||
                              (Array.isArray(entity.linkedin_posts_ds) && entity.linkedin_posts_ds.length > 0) ||
                              (Array.isArray(entity.linkedin_jobs_ds) && entity.linkedin_jobs_ds.length > 0) ||
                              (Array.isArray(entity.linkedin_employees_ds) && entity.linkedin_employees_ds.length > 0)
                            : platform === 'glassdoor'
                              ? (Array.isArray(entity.glassdoor_overview_ds) && entity.glassdoor_overview_ds.length > 0) ||
                                (Array.isArray(entity.glassdoor_reviews_ds) && entity.glassdoor_reviews_ds.length > 0) ||
                                (Array.isArray(entity.glassdoor_salaries_ds) && entity.glassdoor_salaries_ds.length > 0) ||
                                (Array.isArray(entity.glassdoor_interviews_ds) && entity.glassdoor_interviews_ds.length > 0) ||
                                (Array.isArray(entity.glassdoor_benefits_ds) && entity.glassdoor_benefits_ds.length > 0)
                              : platform === 'g2'
                                ? Array.isArray(entity.g2_ds) && entity.g2_ds.length > 0
                                : platform === 'trustpilot'
                                  ? Array.isArray(entity.trustpilot_ds) && entity.trustpilot_ds.length > 0
                                  : platform === 'instagram'
                                    ? Array.isArray(entity.instagram_ds) && entity.instagram_ds.length > 0
                                    : platform === 'facebook'
                                      ? Array.isArray(entity.facebook_ds) && entity.facebook_ds.length > 0
                                      : platform === 'youtube'
                                        ? Array.isArray(entity.youtube_ds) && entity.youtube_ds.length > 0
                                        : platform === 'tiktok'
                                          ? Array.isArray(entity.tiktok_ds) && entity.tiktok_ds.length > 0
                                          : platform === 'threads'
                                            ? Array.isArray(entity.threads_ds) && entity.threads_ds.length > 0
                                            : platform === 'x'
                                              ? Array.isArray(entity.x_ds) && entity.x_ds.length > 0
                                              : false;
                        return (
                          <div key={platform} className="inline-flex items-center">
                            {renderCompanySocialButton(platform, url, hasReport, () => setSocialDrawer({ companyName: companyDrawer.companyName, platform }))}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm text-gray-400">Intent & Products</div>
                      {!hasCrunchbaseReport && (
                        <button
                          onClick={() => handleCrunchbasePurchase(companyDrawer.companyName)}
                          className="px-2 py-1 rounded text-xs text-amber-200 bg-amber-900/40 hover:bg-amber-900/60"
                        >
                          Run Crunchbase (2cr)
                        </button>
                      )}
                    </div>
                    <div className="space-y-4 text-sm text-gray-200">
                      {(() => {
                        if (!hasCrunchbaseReport) {
                          return (
                            <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-4 text-gray-400 text-sm">
                              Run Crunchbase to see Company Overview, Funding, Bombora Topics, and Products.
                            </div>
                          );
                        }

                        const cbRaw = cachedCrunchbase?.data;
                        const cb = cbRaw ? normalizeCrunchbaseData(cbRaw) : null;
                        const cbMatches = cb
                          ? crunchbaseMatchesCompany(cb, companyDrawer.companyName, entity, getFallbackLinkedIn(companyDrawer.companyName))
                          : false;
                        if (!cb) {
                          return (
                            <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-4 text-gray-400 text-sm">
                              Crunchbase report is ready but data could not be parsed.
                            </div>
                          );
                        }
                        if (!cbMatches) {
                          return (
                            <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-4 text-gray-400 text-sm">
                              Crunchbase data does not match this company. Please run Crunchbase again.
                            </div>
                          );
                        }

                        const companyLabel =
                          pick(cb, ['properties'])?.identifier?.value ||
                          pick(cb, ['name'], companyDrawer.companyName);
                        const description =
                          pick(cb, ['overview_description', 'description'])?.description ||
                          pick(cb, ['about_short_description'])?.short_description ||
                          pick(cb, ['properties'])?.short_description ||
                          pick(cb, ['description'], '');
                        const founded = pick(cb, ['founded_on', 'foundedDate'], '');
                        const hq =
                          pick(cb, ['overview_fields_extended'])?.location_group_identifiers?.[0]?.value ||
                          pick(cb, ['headquarters', 'location'], '');
                        const status = pick(cb, ['overview_fields_extended'])?.operating_status || pick(cb, ['status'], '');
                        const categories =
                          pick(cb, ['overview_fields_extended'])?.categories || pick(cb, ['categories'], []);
                        const growthAndHeat = pick(cb, ['growth_and_heat', 'growthAndHeat'], {});
                        const growthScore = pick(growthAndHeat, ['growth_score'], cb.growth_score ?? null);
                        const heatScore = pick(growthAndHeat, ['heat_score'], cb.heat_score ?? null);
                        const growthDelta90 = pick(growthAndHeat, ['growth_score_delta_d90'], null);
                        const heatDelta90 = pick(growthAndHeat, ['heat_score_delta_d90'], null);

                        const fundingSummary = pick(cb, ['funding_rounds_summary'], {});
                        const financials = pick(cb, ['company_financials_highlights'], {});
                        const numRounds = pick(fundingSummary, ['num_funding_rounds'], pick(financials, ['num_funding_rounds'], cb.num_funding_rounds ?? 0));
                        const lastFundingType =
                          pick(cb, ['overview_fields_extended'])?.last_funding_type ||
                          pick(fundingSummary, ['last_funding_type'], pick(cb, ['last_funding_type'], ''));
                        const numInvestors = pick(financials, ['num_investors'], cb.num_investors ?? 0);
                        const investorsList = pick(cb, ['investors_list', 'investors'], []);
                        const uniqueInvestors = [...new Set(investorsList.map((i: any) => i.investor_identifier?.value || i.name || i).filter(Boolean))];

                        const bomboraTopics = pick(cb, ['bombora_surge_list'], []);
                        const products = pick(cb, ['product', 'products', 'siftery_product_list'], []);

                        return (
                          <>
                            <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-4">
                              <div className="text-sm font-semibold text-purple-300 mb-3">Company Overview</div>
                              <div className="text-lg text-white font-semibold mb-2">{companyLabel}</div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                <div className="bg-gray-900/70 rounded-lg p-4">
                                  <div className="text-xs uppercase text-gray-500">Growth Score</div>
                                  <div className="text-3xl font-semibold mt-1 text-emerald-300">{growthScore ?? '—'}</div>
                                  {growthDelta90 !== null && (
                                    <div className="text-xs text-rose-300 mt-1">90d: {growthDelta90}</div>
                                  )}
                                </div>
                                <div className="bg-gray-900/70 rounded-lg p-4">
                                  <div className="text-xs uppercase text-gray-500">Heat Score</div>
                                  <div className="text-3xl font-semibold mt-1 text-amber-300">{heatScore ?? '—'}</div>
                                  {heatDelta90 !== null && (
                                    <div className="text-xs text-rose-300 mt-1">90d: {heatDelta90}</div>
                                  )}
                                </div>
                              </div>
                              {description && (
                                <div className="text-gray-300 text-sm leading-relaxed">{String(description).slice(0, 500)}</div>
                              )}
                              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-400">
                                {founded && <div>Founded: <span className="text-gray-200">{String(founded)}</span></div>}
                                {hq && <div>HQ: <span className="text-gray-200">{String(hq)}</span></div>}
                                {status && <div>Status: <span className="text-gray-200">{String(status)}</span></div>}
                              </div>
                              {Array.isArray(categories) && categories.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                                  {categories.slice(0, 6).map((tag: any, idx: number) => (
                                    <span key={`${tag?.value || tag?.name || tag}-${idx}`} className="px-2 py-1 rounded bg-purple-900/40 text-purple-200">
                                      {tag?.value || tag?.name || String(tag)}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-4">
                              <div className="text-sm font-semibold text-purple-300 mb-3">Funding</div>
                              <div className="grid grid-cols-2 gap-4 mb-3 text-center">
                                <div>
                                  <div className="text-3xl font-semibold text-sky-300">{numRounds || '—'}</div>
                                  <div className="text-xs text-gray-500">Rounds</div>
                                </div>
                                <div>
                                  <div className="text-3xl font-semibold text-fuchsia-300">{numInvestors || '—'}</div>
                                  <div className="text-xs text-gray-500">Investors</div>
                                </div>
                              </div>
                              {lastFundingType && (
                                <div className="text-sm text-gray-300 mb-3">
                                  Last funding: <span className="text-gray-100">{String(lastFundingType).replace(/_/g, ' ')}</span>
                                </div>
                              )}
                              {uniqueInvestors.length > 0 && (
                                <>
                                  <div className="text-xs text-gray-500 mb-2">Notable Investors:</div>
                                  <div className="flex flex-wrap gap-2">
                                    {uniqueInvestors.slice(0, 10).map((inv, idx) => (
                                      <span key={`${inv}-${idx}`} className="px-2 py-1 rounded bg-emerald-900/40 text-emerald-200 text-xs">
                                        {String(inv)}
                                      </span>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>

                            <div>
                              <div className="flex items-center justify-between">
                                <div className="text-xs uppercase text-amber-300 tracking-wide">🔥 Bombora Topics</div>
                                <button
                                  onClick={() => openBuyIntent(companyDrawer.companyName)}
                                  className="px-2 py-1 rounded text-xs text-white bg-emerald-700/80 hover:bg-emerald-700"
                                >
                                  Buy Intent Leads
                                </button>
                              </div>
                              <div className="mt-2 space-y-2">
                                {Array.isArray(bomboraTopics) && bomboraTopics.length > 0 ? (
                                  bomboraTopics.slice(0, 8).map((topic: any, idx: number) => {
                                    const label =
                                      topic?.topic_identifier?.value ||
                                      topic?.topic?.value ||
                                      topic?.topic?.name ||
                                      topic?.topic?.title ||
                                      topic?.topic ||
                                      topic?.name ||
                                      topic?.value ||
                                      topic?.label ||
                                      topic?.title ||
                                      (typeof topic === 'string' ? topic : 'Topic');
                                    const score =
                                      topic?.surge_score ??
                                      topic?.score ??
                                      topic?.topic_score ??
                                      topic?.topicScore ??
                                      '—';
                                    const wow =
                                      topic?.surge_wow_growth ??
                                      topic?.wow ??
                                      topic?.wow_change ??
                                      topic?.wow_delta ??
                                      topic?.wowChange ??
                                      topic?.wowDelta ??
                                      '—';
                                    return (
                                      <div key={`bombora-${idx}`} className="flex items-center justify-between gap-4">
                                        <span>{label}</span>
                                        <span className="text-gray-400">
                                          Score: {score} • WoW: {wow}
                                        </span>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div className="text-gray-500 text-sm">No Bombora topics found.</div>
                                )}
                              </div>
                            </div>

                            <div>
                              <div className="text-xs uppercase text-blue-300 tracking-wide">📦 Products</div>
                              <div className="mt-2 space-y-3">
                                {Array.isArray(products) && products.length > 0 ? (
                                  products.slice(0, 6).map((product: any, idx: number) => (
                                    <div key={`product-${idx}`}>
                                      <div className="text-gray-100">
                                        {product?.product_name || product?.name || product?.value || 'Product'}
                                      </div>
                                      {product?.description && (
                                        <div className="text-gray-400">{String(product.description).slice(0, 260)}</div>
                                      )}
                                    </div>
                                  ))
                                ) : (
                                  <div className="text-gray-500 text-sm">No products found.</div>
                                )}
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-4 mb-6">
                    <div className="text-sm text-gray-400 mb-3">Company Intelligence</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="flex items-start justify-between gap-4">
                        <span className="text-gray-400">Industry</span>
                        <span className="text-gray-200 text-right break-words max-w-[60%]">{industry || '—'}</span>
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <span className="text-gray-400">Employee Count</span>
                        <span className="text-gray-200 text-right break-words max-w-[60%]">{employeeCount || '—'}</span>
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <span className="text-gray-400">Revenue Range</span>
                        <span className="text-gray-200 text-right break-words max-w-[60%]">{revenueRange || '—'}</span>
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <span className="text-gray-400">Headquarters Location</span>
                        <span className="text-gray-200 text-right break-words max-w-[60%]">{headquarters || '—'}</span>
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <span className="text-gray-400">Founded Year</span>
                        <span className="text-gray-200 text-right break-words max-w-[60%]">{foundedYear || '—'}</span>
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <span className="text-gray-400">Company Website</span>
                        <span className="text-gray-200 text-right break-words max-w-[60%]">
                          {websiteValue ? (
                            <a href={websiteValue} target="_blank" rel="noreferrer" className="text-blue-300 hover:text-blue-200">
                              {websiteValue}
                            </a>
                          ) : '—'}
                        </span>
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <span className="text-gray-400">Keyword Data</span>
                        <span className="text-gray-200 text-right break-words max-w-[60%]">
                          {keywordLink ? (
                            <a href={keywordLink} target="_blank" rel="noreferrer" className="text-blue-300 hover:text-blue-200">
                              {keywordLink}
                            </a>
                          ) : '—'}
                        </span>
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <span className="text-gray-400">Technology Usage</span>
                        <span className="text-gray-200 text-right break-words max-w-[60%]">{technologyUsage || '—'}</span>
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <span className="text-gray-400">Company Size Brackets</span>
                        <span className="text-gray-200 text-right break-words max-w-[60%]">{companySizeBrackets || '—'}</span>
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <span className="text-gray-400">Geographic Tags</span>
                        <span className="text-gray-200 text-right break-words max-w-[60%]">{geographicTags || '—'}</span>
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <span className="text-gray-400">Industry Codes</span>
                        <span className="text-gray-200 text-right break-words max-w-[60%]">{industryCodes || '—'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-4 mb-6">
                    <div className="text-sm text-gray-400 mb-3">Reports</div>
                    <div className="space-y-3">
                      {(() => {
                        const cachedLinkedin = linkedinCache[companyDrawer.companyName];
                        const cachedGlassdoor = glassdoorCache[companyDrawer.companyName];
                        const cachedG2 = g2Cache[companyDrawer.companyName];
                        const cachedTrustpilot = trustpilotCache[companyDrawer.companyName];
                        const cachedInstagram = instagramCache[companyDrawer.companyName];
                        const cachedFacebook = facebookCache[companyDrawer.companyName];
                        const cachedYouTube = youtubeCache[companyDrawer.companyName];
                        const cachedTiktok = tiktokCache[companyDrawer.companyName];
                        const cachedX = xCache[companyDrawer.companyName];
                        const hasLinkedinFull =
                          (Array.isArray(entity.linkedin_profile_ds) && entity.linkedin_profile_ds.length > 0) ||
                          (Array.isArray(entity.linkedin_posts_ds) && entity.linkedin_posts_ds.length > 0) ||
                          (Array.isArray(entity.linkedin_jobs_ds) && entity.linkedin_jobs_ds.length > 0) ||
                          (Array.isArray(entity.linkedin_employees_ds) && entity.linkedin_employees_ds.length > 0);
                        const hasGlassdoorFull =
                          (Array.isArray(entity.glassdoor_overview_ds) && entity.glassdoor_overview_ds.length > 0) ||
                          (Array.isArray(entity.glassdoor_reviews_ds) && entity.glassdoor_reviews_ds.length > 0) ||
                          (Array.isArray(entity.glassdoor_salaries_ds) && entity.glassdoor_salaries_ds.length > 0) ||
                          (Array.isArray(entity.glassdoor_interviews_ds) && entity.glassdoor_interviews_ds.length > 0) ||
                          (Array.isArray(entity.glassdoor_benefits_ds) && entity.glassdoor_benefits_ds.length > 0);
                        const hasG2Full = Array.isArray(entity.g2_ds) && entity.g2_ds.length > 0;
                        const hasTrustpilotFull = Array.isArray(entity.trustpilot_ds) && entity.trustpilot_ds.length > 0;
                        const hasInstagramFull = Array.isArray(entity.instagram_ds) && entity.instagram_ds.length > 0;
                        const hasFacebookFull = Array.isArray(entity.facebook_ds) && entity.facebook_ds.length > 0;
                        const hasYouTubeFull = Array.isArray(entity.youtube_ds) && entity.youtube_ds.length > 0;
                        const hasTiktokFull = Array.isArray(entity.tiktok_ds) && entity.tiktok_ds.length > 0;
                        const hasXFull = Array.isArray(entity.x_ds) && entity.x_ds.length > 0;

                        const linkedinData = cachedLinkedin?.data as any;
                        const linkedinPosts = Array.isArray(linkedinData?.posts)
                          ? linkedinData.posts
                          : Array.isArray(linkedinData?.recent_posts)
                          ? linkedinData.recent_posts
                          : Array.isArray(linkedinData?.company_posts)
                          ? linkedinData.company_posts
                          : [];
                        const linkedinJobs = Array.isArray(linkedinData?.jobs)
                          ? linkedinData.jobs
                          : Array.isArray(linkedinData?.open_positions)
                          ? linkedinData.open_positions
                          : Array.isArray(linkedinData?.positions)
                          ? linkedinData.positions
                          : Array.isArray(linkedinData?.job_postings)
                          ? linkedinData.job_postings
                          : [];
                        const linkedinProfile =
                          linkedinData?.profile || linkedinData?.company || linkedinData?.company_profile || {};
                        const linkedinSpecialties = Array.isArray(linkedinProfile?.specialties)
                          ? linkedinProfile.specialties
                          : Array.isArray(linkedinProfile?.speciality)
                          ? linkedinProfile.speciality
                          : [];
                        const linkedinSimilarPages = Array.isArray(linkedinProfile?.similar_pages)
                          ? linkedinProfile.similar_pages
                          : Array.isArray(linkedinProfile?.similarPages)
                          ? linkedinProfile.similarPages
                          : Array.isArray(linkedinProfile?.similar_companies)
                          ? linkedinProfile.similar_companies
                          : [];
                        const extractPostDate = (p: any): string => {
                          const raw =
                            p.postedDate ||
                            p.publishedAt ||
                            p.date ||
                            p.postedAt ||
                            p.createdAt ||
                            p.created_time ||
                            p.createdTime ||
                            p.time ||
                            p.timestamp ||
                            p.timestampMs ||
                            p.timestamp_ms ||
                            p.postedDateTimestamp ||
                            p.postedDateTimestampMs ||
                            p.createdAtTimestamp ||
                            p.created_at ||
                            p.created_at_timestamp ||
                            p.shareDate ||
                            p.share_date ||
                            p.posted_at ||
                            p.published_at ||
                            p.datePosted;
                          if (raw == null) return '';
                          if (typeof raw === 'number') {
                            const ms = raw < 2_000_000_000 ? raw * 1000 : raw;
                            const d = new Date(ms);
                            return Number.isNaN(d.getTime()) ? '' : d.toISOString();
                          }
                          if (typeof raw === 'string') {
                            const trimmed = raw.trim();
                            if (!trimmed) return '';
                            if (/^\d+$/.test(trimmed)) {
                              const num = Number(trimmed);
                              if (!Number.isNaN(num)) {
                                const ms = num < 2_000_000_000 ? num * 1000 : num;
                                const d = new Date(ms);
                                return Number.isNaN(d.getTime()) ? '' : d.toISOString();
                              }
                            }
                            const lower = trimmed.toLowerCase();
                            if (lower === 'yesterday') {
                              const d = new Date();
                              d.setDate(d.getDate() - 1);
                              return d.toISOString();
                            }
                            const rel = lower.match(/(\d+)\s*(second|minute|hour|day|week|month|year)s?\s*ago/);
                            if (rel) {
                              const amount = Number(rel[1] || 0);
                              if (!Number.isNaN(amount) && amount > 0) {
                                const d = new Date();
                                const unit = rel[2];
                                if (unit === 'second') d.setSeconds(d.getSeconds() - amount);
                                if (unit === 'minute') d.setMinutes(d.getMinutes() - amount);
                                if (unit === 'hour') d.setHours(d.getHours() - amount);
                                if (unit === 'day') d.setDate(d.getDate() - amount);
                                if (unit === 'week') d.setDate(d.getDate() - amount * 7);
                                if (unit === 'month') d.setMonth(d.getMonth() - amount);
                                if (unit === 'year') d.setFullYear(d.getFullYear() - amount);
                                return d.toISOString();
                              }
                            }
                            const parsed = new Date(trimmed);
                            return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
                          }
                          return '';
                        };

                        const recentLinkedinPosts = linkedinPosts
                          .slice()
                          .sort((a: any, b: any) => {
                            const ad = new Date(extractPostDate(a) || 0).getTime();
                            const bd = new Date(extractPostDate(b) || 0).getTime();
                            return bd - ad;
                          })
                          .slice(0, 1);

                        const normalizeRole = (val: any) => {
                          const text = val == null ? '' : String(val).trim();
                          return text ? text : 'Other';
                        };
                        const normalizeLocation = (val: any) => {
                          if (Array.isArray(val)) return val.filter(Boolean).join(', ');
                          const text = val == null ? '' : String(val).trim();
                          return text;
                        };
                        const buildJobGroups = (items: any[]) => {
                          const map = new Map<string, { count: number; locations: Set<string> }>();
                          items.forEach((item) => {
                            const role =
                              item?.jobFunction ||
                              item?.job_function ||
                              item?.function ||
                              item?.department ||
                              item?.jobCategory ||
                              item?.job_category ||
                              item?.jobFamily ||
                              item?.job_family ||
                              item?.title ||
                              'Other';
                            const locRaw =
                              item?.location ||
                              item?.locations ||
                              item?.jobLocation ||
                              item?.job_location ||
                              item?.city ||
                              item?.country ||
                              '';
                            const key = normalizeRole(role);
                            const entry = map.get(key) || { count: 0, locations: new Set<string>() };
                            entry.count += 1;
                            const loc = normalizeLocation(locRaw);
                            if (loc) entry.locations.add(loc);
                            map.set(key, entry);
                          });
                          return Array.from(map.entries())
                            .map(([role, stats]) => ({
                              role,
                              count: stats.count,
                              locations: Array.from(stats.locations).slice(0, 3).join(', '),
                            }))
                            .sort((a, b) => b.count - a.count);
                        };
                        const jobGroups = buildJobGroups(linkedinJobs).slice(0, 10);

                        const glassdoorData = cachedGlassdoor?.data as any;
                        const glassdoorReviews = Array.isArray(glassdoorData?.reviews) ? glassdoorData.reviews : [];
                        const glassdoorInterviews = Array.isArray(glassdoorData?.interviews) ? glassdoorData.interviews : [];
                        const normalizeDept = (val: any) => {
                          if (val && typeof val === 'object') {
                            const candidate =
                              (val as any).text ||
                              (val as any).name ||
                              (val as any).title ||
                              (val as any).value ||
                              (val as any).label;
                            if (candidate) return String(candidate).trim() || 'Other';
                          }
                          const text = val == null ? '' : String(val).trim();
                          return text ? text : 'Other';
                        };
                        const buildDeptRatings = (items: any[]) => {
                          const map = new Map<string, { count: number; ratingSum: number; ratingCount: number }>();
                          items.forEach((item) => {
                            const dept =
                              item?.jobTitle?.text ||
                              item?.jobTitle ||
                              item?.department?.text ||
                              item?.department ||
                              item?.department_name ||
                              item?.departmentName ||
                              item?.jobDepartment ||
                              item?.jobFunction ||
                              item?.jobFamily ||
                              item?.job_title_department ||
                              item?.position ||
                              item?.title ||
                              'Other';
                            const key = normalizeDept(dept);
                            const ratingRaw =
                              item?.rating ||
                              item?.overallRating ||
                              item?.overall_rating ||
                              item?.starRating ||
                              item?.stars ||
                              item?.score;
                            const rating = ratingRaw == null ? NaN : Number(ratingRaw);
                            const entry = map.get(key) || { count: 0, ratingSum: 0, ratingCount: 0 };
                            entry.count += 1;
                            if (!Number.isNaN(rating)) {
                              entry.ratingSum += rating;
                              entry.ratingCount += 1;
                            }
                            map.set(key, entry);
                          });
                          return Array.from(map.entries())
                            .map(([dept, stats]) => ({
                              dept,
                              count: stats.count,
                              rating: stats.ratingCount ? stats.ratingSum / stats.ratingCount : null,
                            }))
                            .sort((a, b) => b.count - a.count);
                        };
                        const buildInterviewDeptStats = (items: any[]) => {
                          const map = new Map<string, { count: number; positive: number }>();
                          items.forEach((item) => {
                            const dept =
                              item?.jobTitle?.text ||
                              item?.jobTitle ||
                              item?.department?.text ||
                              item?.department ||
                              item?.department_name ||
                              item?.departmentName ||
                              item?.jobDepartment ||
                              item?.jobFunction ||
                              item?.jobFamily ||
                              item?.job_title_department ||
                              item?.position ||
                              item?.title ||
                              'Other';
                            const key = normalizeDept(dept);
                            const outcomeRaw =
                              item?.outcome ||
                              item?.result ||
                              item?.experience ||
                              item?.experienceType ||
                              item?.interviewOutcome ||
                              item?.decision;
                            const outcome = outcomeRaw == null ? '' : String(outcomeRaw).toLowerCase();
                            const entry = map.get(key) || { count: 0, positive: 0 };
                            entry.count += 1;
                            if (outcome.includes('positive')) entry.positive += 1;
                            map.set(key, entry);
                          });
                          return Array.from(map.entries())
                            .map(([dept, stats]) => ({
                              dept,
                              count: stats.count,
                              positivePercent: stats.count ? Math.round((stats.positive / stats.count) * 100) : null,
                            }))
                            .sort((a, b) => b.count - a.count);
                        };
                        const reviewDepartments = buildDeptRatings(glassdoorReviews).slice(0, 10);
                        const interviewDepartments = buildInterviewDeptStats(glassdoorInterviews).slice(0, 10);

                        const g2Items = (() => {
                          const data = cachedG2?.data as any;
                          if (Array.isArray(data?.g2)) return data.g2;
                          if (Array.isArray(data?.items)) return data.items;
                          if (Array.isArray(data)) return data;
                          return [];
                        })();
                        const g2ByPlatform = g2Items.reduce((acc: Record<string, any[]>, item: any) => {
                          const key = (item?.platform || item?.source || 'g2').toString().toLowerCase();
                          acc[key] = acc[key] || [];
                          acc[key].push(item);
                          return acc;
                        }, {});
                        const pickReview = (items: any[]) => {
                          if (!items || items.length === 0) return null;
                          return items[0];
                        };
                        const normalizeReview = (item: any) => {
                          if (!item) return {};
                          return {
                            product: item.productName || item.product || item.vendorName || item.companyName || item.title,
                            rating: item.ratingOutOfFive || item.rating || item.stars || item.score,
                            title: item.reviewTitle || item.title || item.summary || '',
                            body: item.reviewText || item.review || item.descriptionSnippet || item.description || item.text || '',
                            reviewer: item.reviewerName || item.reviewer || item.user || item.author || item.reviewerTitle || '',
                            role: item.reviewerTitle || item.jobTitle || item.role || '',
                            date: item.reviewDate || item.date || item.createdAt || item.created_at || '',
                            url: item.reviewsUrl || item.reviewUrl || item.url || '',
                          };
                        };
                        const g2Summary = normalizeReview(pickReview(g2ByPlatform.g2 || g2Items));
                        const capterraSummary = normalizeReview(pickReview(g2ByPlatform.capterra || []));
                        const gartnerSummary = normalizeReview(pickReview(g2ByPlatform.gartner || []));

                        const trustpilotItems = (() => {
                          const data = cachedTrustpilot?.data as any;
                          if (Array.isArray(data?.trustpilot)) return data.trustpilot;
                          if (Array.isArray(data?.trustpilot?.reviews)) return data.trustpilot.reviews;
                          if (Array.isArray(data?.reviews)) return data.reviews;
                          if (Array.isArray(data?.items)) return data.items;
                          if (Array.isArray(data)) return data;
                          return [];
                        })();
                        const trustpilotCompanyName = trustpilotItems[0]?.companyName || companyDrawer.companyName;
                        const trustpilotRatings = trustpilotItems.map((r: any) => r.reviewRatingScore).filter((r: any) => r != null);
                        const trustpilotAvg =
                          trustpilotRatings.length > 0
                            ? (trustpilotRatings.reduce((a: number, b: number) => a + Number(b), 0) / trustpilotRatings.length).toFixed(1)
                            : null;
                        const trustpilotEnglish = trustpilotItems.filter((r: any) => r.reviewLanguage === 'en' || !r.reviewLanguage);
                        const trustpilotRecent = trustpilotEnglish.slice(0, 1);
                        const instagramSummary = cachedInstagram?.data ? summarizeInstagramData(cachedInstagram.data) : null;
                        const facebookSummary = cachedFacebook?.data ? summarizeFacebookData(cachedFacebook.data) : null;
                        const youtubeSummary = cachedYouTube?.data ? summarizeYouTubeData(cachedYouTube.data) : null;
                        const tiktokSummary = cachedTiktok?.data ? summarizeTikTokData(cachedTiktok.data) : null;
                        const xSummary = cachedX?.data ? summarizeXData(cachedX.data) : null;

                        const loadLinkedin = async () => {
                          if (cachedLinkedin) {
                            return;
                          }
                          const full = await fetchLinkedinFull(companyDrawer.companyName);
                          if (full) {
                            const payload = { data: full, scrapedAt: entity.linkedin_scraped_at };
                            setLinkedinCache((prev) => ({ ...prev, [companyDrawer.companyName]: payload }));
                          }
                        };

                        const loadGlassdoor = async () => {
                          if (cachedGlassdoor) {
                            return;
                          }
                          const full = await fetchGlassdoorFull(companyDrawer.companyName);
                          if (full) {
                            const payload = { data: full, scrapedAt: entity.glassdoor_scraped_at };
                            setGlassdoorCache((prev) => ({ ...prev, [companyDrawer.companyName]: payload }));
                          }
                        };

                        const loadG2 = async () => {
                          if (cachedG2) {
                            return;
                          }
                          const full = await fetchG2Full(companyDrawer.companyName);
                          if (full) {
                            const payload = { data: full, scrapedAt: entity.g2_scraped_at };
                            setG2Cache((prev) => ({ ...prev, [companyDrawer.companyName]: payload }));
                          }
                        };

                        const loadTrustpilot = async () => {
                          if (cachedTrustpilot) {
                            return;
                          }
                          const full = await fetchTrustpilotFull(companyDrawer.companyName);
                          if (full) {
                            const payload = { data: full, scrapedAt: entity.trustpilot_scraped_at };
                            setTrustpilotCache((prev) => ({ ...prev, [companyDrawer.companyName]: payload }));
                          }
                        };
                        const loadInstagram = async () => {
                          if (cachedInstagram) {
                            return;
                          }
                          const full = await fetchInstagramFull(companyDrawer.companyName);
                          if (full) {
                            const payload = { data: full, scrapedAt: entity.instagram_scraped_at };
                            setInstagramCache((prev) => ({ ...prev, [companyDrawer.companyName]: payload }));
                          }
                        };
                        const loadFacebook = async () => {
                          if (cachedFacebook) {
                            return;
                          }
                          const full = await fetchFacebookFull(companyDrawer.companyName);
                          if (full) {
                            const payload = { data: full, scrapedAt: entity.facebook_scraped_at };
                            setFacebookCache((prev) => ({ ...prev, [companyDrawer.companyName]: payload }));
                          }
                        };
                        const loadYouTube = async () => {
                          if (cachedYouTube) {
                            return;
                          }
                          const full = await fetchYouTubeFull(companyDrawer.companyName);
                          if (full) {
                            const payload = { data: full, scrapedAt: entity.youtube_scraped_at };
                            setYoutubeCache((prev) => ({ ...prev, [companyDrawer.companyName]: payload }));
                          }
                        };
                        const loadTiktok = async () => {
                          if (cachedTiktok) {
                            return;
                          }
                          const full = await fetchTikTokFull(companyDrawer.companyName);
                          if (full) {
                            const payload = { data: full, scrapedAt: entity.tiktok_scraped_at };
                            setTiktokCache((prev) => ({ ...prev, [companyDrawer.companyName]: payload }));
                          }
                        };
                        const loadX = async () => {
                          if (cachedX) {
                            return;
                          }
                          const full = await fetchXFull(companyDrawer.companyName);
                          if (full) {
                            const payload = { data: full, scrapedAt: entity.x_scraped_at };
                            setXCache((prev) => ({ ...prev, [companyDrawer.companyName]: payload }));
                          }
                        };

                        const viewLinkedin = async () => {
                          if (!cachedLinkedin) await loadLinkedin();
                          const next = linkedinCache[companyDrawer.companyName] || cachedLinkedin;
                          if (next) {
                            setLinkedinDrawer({ companyName: companyDrawer.companyName, data: next.data, scrapedAt: next.scrapedAt });
                          }
                        };

                        const viewGlassdoor = async () => {
                          if (!cachedGlassdoor) await loadGlassdoor();
                          const next = glassdoorCache[companyDrawer.companyName] || cachedGlassdoor;
                          if (next) {
                            setGlassdoorDrawer({ companyName: companyDrawer.companyName, data: next.data, scrapedAt: next.scrapedAt });
                          }
                        };

                        const viewG2 = async () => {
                          if (!cachedG2) await loadG2();
                          const next = g2Cache[companyDrawer.companyName] || cachedG2;
                          if (next) {
                            setG2Drawer({ companyName: companyDrawer.companyName, data: next.data, scrapedAt: next.scrapedAt });
                          }
                        };

                        const viewTrustpilot = async () => {
                          if (!cachedTrustpilot) await loadTrustpilot();
                          const next = trustpilotCache[companyDrawer.companyName] || cachedTrustpilot;
                          if (next) {
                            setTrustpilotDrawer({ companyName: companyDrawer.companyName, data: next.data, scrapedAt: next.scrapedAt });
                          }
                        };
                        const viewInstagram = async () => {
                          if (!cachedInstagram) await loadInstagram();
                          const next = instagramCache[companyDrawer.companyName] || cachedInstagram;
                          if (next) {
                            setInstagramDrawer({ companyName: companyDrawer.companyName, data: next.data, scrapedAt: next.scrapedAt });
                          }
                        };
                        const viewFacebook = async () => {
                          if (!cachedFacebook) await loadFacebook();
                          const next = facebookCache[companyDrawer.companyName] || cachedFacebook;
                          if (next) {
                            setFacebookDrawer({ companyName: companyDrawer.companyName, data: next.data, scrapedAt: next.scrapedAt });
                          }
                        };
                        const viewYouTube = async () => {
                          if (!cachedYouTube) await loadYouTube();
                          const next = youtubeCache[companyDrawer.companyName] || cachedYouTube;
                          if (next) {
                            setYoutubeDrawer({ companyName: companyDrawer.companyName, data: next.data, scrapedAt: next.scrapedAt });
                          }
                        };
                        const viewTiktok = async () => {
                          if (!cachedTiktok) await loadTiktok();
                          const next = tiktokCache[companyDrawer.companyName] || cachedTiktok;
                          if (next) {
                            setTiktokDrawer({ companyName: companyDrawer.companyName, data: next.data, scrapedAt: next.scrapedAt });
                          }
                        };
                        const viewX = async () => {
                          if (!cachedX) await loadX();
                          const next = xCache[companyDrawer.companyName] || cachedX;
                          if (next) {
                            setXDrawer({ companyName: companyDrawer.companyName, data: next.data, scrapedAt: next.scrapedAt });
                          }
                        };

                        const glassdoorSummary = {
                          reviewsCount: glassdoorReviews.length,
                          interviewsCount: glassdoorInterviews.length,
                          reviewSnippet: glassdoorReviews[0]?.summary || glassdoorReviews[0]?.pros || glassdoorReviews[0]?.cons || '',
                          interviewSnippet: glassdoorInterviews[0]?.title || glassdoorInterviews[0]?.experience || '',
                        };

                        return (
                          <>
                            <details className="bg-gray-800 rounded-lg p-4" onToggle={(e) => {
                              if ((e.currentTarget as HTMLDetailsElement).open) loadLinkedin();
                            }}>
                              <summary className="cursor-pointer text-gray-200 font-medium">LinkedIn 360</summary>
                              <div className="mt-3 text-sm text-gray-300 space-y-4">
                                <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
                                  <div className="text-xs text-blue-300 uppercase tracking-wide mb-2">
                                    Open Positions ({linkedinJobs.length || 0} total)
                                  </div>
                                  {jobGroups.length ? (
                                    <div className="space-y-2">
                                      {jobGroups.map((row) => (
                                        <div key={`li-job-${row.role}`} className="flex items-center justify-between gap-3">
                                          <div>
                                            <div className="text-gray-200">{row.role}</div>
                                            <div className="text-[11px] text-gray-500">{row.count} roles</div>
                                          </div>
                                          <div className="text-amber-300 text-xs text-right max-w-[55%] break-words">
                                            {row.locations || '—'}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="text-gray-500">No open position data found.</div>
                                  )}
                                </div>

                                <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
                                  <div className="text-xs text-blue-300 uppercase tracking-wide mb-2">
                                    Recent Post
                                  </div>
                                  {recentLinkedinPosts.length ? (() => {
                                    const post = recentLinkedinPosts[0];
                                    const date = extractPostDate(post);
                                    const text =
                                      post?.text ||
                                      post?.postText ||
                                      post?.content ||
                                      post?.summary ||
                                      '';
                                    const likes = post?.numLikes || post?.likesCount || post?.socialCounts?.numLikes;
                                    const comments = post?.numComments || post?.commentsCount || post?.socialCounts?.numComments;
                                    return (
                                      <div>
                                        <div className="text-xs text-gray-400">
                                          {date ? new Date(date).toLocaleDateString() : 'No date'}
                                        </div>
                                        <div className="text-sm text-gray-200 mt-1">{String(text).slice(0, 220)}</div>
                                        <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                          {likes !== undefined && <span>👍 {likes} likes</span>}
                                          {comments !== undefined && <span>💬 {comments} comments</span>}
                                        </div>
                                      </div>
                                    );
                                  })() : (
                                    <div className="text-gray-500">No LinkedIn post data found.</div>
                                  )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
                                    <div className="text-xs text-blue-300 uppercase tracking-wide mb-2">Specialties</div>
                                    {linkedinSpecialties.length ? (
                                      <div className="flex flex-wrap gap-2">
                                        {linkedinSpecialties.map((spec: any, idx: number) => (
                                          <span key={`li-spec-${idx}`} className="px-2 py-1 rounded bg-gray-800 text-gray-200 text-xs">
                                            {String(spec)}
                                          </span>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="text-gray-500">No specialties found.</div>
                                    )}
                                  </div>
                                  <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
                                    <div className="text-xs text-blue-300 uppercase tracking-wide mb-2">Similar Pages</div>
                                    {linkedinSimilarPages.length ? (
                                      <div className="grid grid-cols-2 gap-2 text-sm text-blue-200">
                                        {linkedinSimilarPages.slice(0, 10).map((page: any, idx: number) => (
                                          <div key={`li-sim-${idx}`} className="truncate">
                                            {page?.name || page?.title || page?.value || page}
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="text-gray-500">No similar pages found.</div>
                                    )}
                                  </div>
                                </div>
                                <button onClick={viewLinkedin} className="company-side-btn company-side-btn-view">
                                  View Full Report
                                </button>
                                {(!cachedLinkedin && !hasLinkedinFull) && (
                                  <button
                                    onClick={() => {
                                      const url =
                                        (entity[socialField.linkedin] as string | undefined) ||
                                        getFallbackLinkedIn(companyDrawer.companyName);
                                      handleLinkedInScrape(companyDrawer.companyName, url);
                                    }}
                                    className="company-side-btn company-side-btn-run"
                                  >
                                    Run LinkedIn 360 (2cr)
                                  </button>
                                )}
                              </div>
                            </details>

                            <details className="bg-gray-800 rounded-lg p-4" onToggle={(e) => {
                              if ((e.currentTarget as HTMLDetailsElement).open) loadInstagram();
                            }}>
                              <summary className="cursor-pointer text-gray-200 font-medium">Instagram</summary>
                              <div className="mt-3 text-sm text-gray-300 space-y-4">
                                {instagramSummary ? (
                                  <>
                                    <div className="bg-gradient-to-br from-fuchsia-900/40 to-gray-900 border border-fuchsia-700/40 rounded-xl p-5">
                                      <div className="flex items-start gap-4">
                                        <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
                                          {instagramSummary.avatar ? (
                                            <img src={instagramSummary.avatar} alt="" className="w-full h-full object-cover" />
                                          ) : (
                                            <span className="text-xs text-gray-400">Avatar</span>
                                          )}
                                        </div>
                                        <div className="flex-1">
                                          <p className="text-xs uppercase tracking-wide text-fuchsia-300/80">Instagram Profile</p>
                                          <h4 className="text-lg font-semibold text-white">{instagramSummary.fullName || 'Instagram Profile'}</h4>
                                          <p className="text-sm text-gray-400 mt-1">{instagramSummary.bio || 'No bio found.'}</p>
                                        </div>
                                        <div className="flex flex-col items-end gap-2">
                                          {instagramSummary.isVerified && (
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-fuchsia-600 text-white">Verified</span>
                                          )}
                                          {instagramSummary.username && (
                                            <span className="text-xs text-fuchsia-200">@{instagramSummary.username}</span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-300">
                                        <div className="bg-gray-900/60 rounded-lg p-2">
                                          <span className="text-gray-500">Followers</span>
                                          <div>{instagramSummary.followers ?? 'Unknown'}</div>
                                        </div>
                                        <div className="bg-gray-900/60 rounded-lg p-2">
                                          <span className="text-gray-500">Following</span>
                                          <div>{instagramSummary.following ?? 'Unknown'}</div>
                                        </div>
                                        <div className="bg-gray-900/60 rounded-lg p-2">
                                          <span className="text-gray-500">Posts</span>
                                          <div>{instagramSummary.postsCount ?? 'Unknown'}</div>
                                        </div>
                                      </div>
                                      {instagramSummary.website && (
                                        <div className="mt-3 text-xs text-gray-400">Website: {instagramSummary.website}</div>
                                      )}
                                      {instagramSummary.profileUrl && (
                                        <a className="text-xs text-fuchsia-300 hover:text-fuchsia-200 mt-2 inline-block" href={instagramSummary.profileUrl} target="_blank" rel="noreferrer">
                                          View profile
                                        </a>
                                      )}
                                    </div>

                                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                                      <p className="text-xs uppercase text-gray-500 mb-3">Recent Post</p>
                                      {instagramSummary.recentPosts.length === 0 ? (
                                        <p className="text-sm text-gray-400">No posts captured.</p>
                                      ) : (() => {
                                        const post = instagramSummary.recentPosts[0];
                                        return (
                                          <div className="bg-gray-800/60 rounded-lg p-3">
                                            <div className="text-xs text-gray-400">
                                              {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                                            </div>
                                            <div className="text-sm text-gray-200 mt-1">{post.caption || 'Post content unavailable'}</div>
                                            {(post.image || post.video) && (
                                              <div className="mt-2 space-y-2">
                                                {post.image && (
                                                  <img src={post.image} alt="" className="w-full rounded-md border border-gray-700" />
                                                )}
                                                {post.video && (
                                                  <video
                                                    src={post.video}
                                                    controls
                                                    preload="metadata"
                                                    className="w-full rounded-md border border-gray-700"
                                                  />
                                                )}
                                              </div>
                                            )}
                                            <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                              {post.likes !== undefined && (
                                                <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>
                                              )}
                                              {post.comments !== undefined && (
                                                <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {post.comments}</span>
                                              )}
                                            </div>
                                            {post.url && (
                                              <a className="text-xs text-fuchsia-300 hover:text-fuchsia-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                                                View post
                                              </a>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-gray-500">No Instagram data found.</div>
                                )}
                                <button onClick={viewInstagram} className="company-side-btn company-side-btn-view">
                                  View Full Report
                                </button>
                                {(!cachedInstagram && !hasInstagramFull) && (
                                  <button
                                    onClick={() => handleInstagramScrape(companyDrawer.companyName)}
                                    className="company-side-btn company-side-btn-run"
                                  >
                                    Run Instagram (1cr)
                                  </button>
                                )}
                              </div>
                            </details>

                            <details className="bg-gray-800 rounded-lg p-4" onToggle={(e) => {
                              if ((e.currentTarget as HTMLDetailsElement).open) loadFacebook();
                            }}>
                              <summary className="cursor-pointer text-gray-200 font-medium">Facebook</summary>
                              <div className="mt-3 text-sm text-gray-300 space-y-4">
                                {facebookSummary ? (
                                  <>
                                    <div className="bg-gradient-to-br from-indigo-900/40 to-gray-900 border border-indigo-700/40 rounded-xl p-5">
                                      <div className="flex items-start gap-4">
                                        <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
                                          {facebookSummary.profilePictureUrl ? (
                                            <img src={facebookSummary.profilePictureUrl} alt="" className="w-full h-full object-cover" />
                                          ) : (
                                            <span className="text-xs text-gray-400">Logo</span>
                                          )}
                                        </div>
                                        <div className="flex-1">
                                          <p className="text-xs uppercase tracking-wide text-indigo-300/80">Facebook Page</p>
                                          <h4 className="text-lg font-semibold text-white">{facebookSummary.title || 'Facebook Page'}</h4>
                                          <p className="text-sm text-gray-400 mt-1">{facebookSummary.intro || 'No intro found.'}</p>
                                        </div>
                                        <div className="flex flex-col items-end gap-2">
                                          {facebookSummary.followers !== undefined && (
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-600 text-white">
                                              {facebookSummary.followers} followers
                                            </span>
                                          )}
                                          {facebookSummary.likes !== undefined && (
                                            <span className="text-xs text-indigo-200">{facebookSummary.likes} likes</span>
                                          )}
                                        </div>
                                      </div>
                                      {facebookSummary.coverPhotoUrl && (
                                        <div className="mt-4 rounded-lg overflow-hidden border border-gray-800">
                                          <img src={facebookSummary.coverPhotoUrl} alt="" className="w-full h-28 object-cover" />
                                        </div>
                                      )}
                                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-300">
                                        <div className="bg-gray-900/60 rounded-lg p-2">
                                          <span className="text-gray-500">Category</span>
                                          <div>{facebookSummary.category || 'Unknown'}</div>
                                        </div>
                                        <div className="bg-gray-900/60 rounded-lg p-2">
                                          <span className="text-gray-500">Phone</span>
                                          <div>{facebookSummary.phone || 'Unknown'}</div>
                                        </div>
                                        <div className="bg-gray-900/60 rounded-lg p-2">
                                          <span className="text-gray-500">Website</span>
                                          <div>{facebookSummary.website || 'Unknown'}</div>
                                        </div>
                                      </div>
                                      {facebookSummary.address && (
                                        <div className="mt-3 text-xs text-gray-400">Address: {facebookSummary.address}</div>
                                      )}
                                      {facebookSummary.pageUrl && (
                                        <a className="text-xs text-indigo-300 hover:text-indigo-200 mt-2 inline-block" href={facebookSummary.pageUrl} target="_blank" rel="noreferrer">
                                          View page
                                        </a>
                                      )}
                                    </div>

                                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                                      <p className="text-xs uppercase text-gray-500 mb-3">Recent Post</p>
                                      {facebookSummary.posts.length === 0 ? (
                                        <p className="text-sm text-gray-400">No posts captured.</p>
                                      ) : (() => {
                                        const post = facebookSummary.posts[0];
                                        return (
                                          <div className="bg-gray-800/60 rounded-lg p-3">
                                            <div className="text-xs text-gray-400">
                                              {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                                            </div>
                                            <div className="text-sm text-gray-200 mt-1 line-clamp-4">{post.text || 'Post content unavailable'}</div>
                                            <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                              {post.likes !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>}
                                              {post.comments !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {post.comments}</span>}
                                              {post.shares !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Shares {post.shares}</span>}
                                              {post.views !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Views {post.views}</span>}
                                            </div>
                                            {post.url && (
                                              <a className="text-xs text-indigo-300 hover:text-indigo-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                                                View post
                                              </a>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-gray-500">No Facebook data found.</div>
                                )}
                                <button onClick={viewFacebook} className="company-side-btn company-side-btn-view">
                                  View Full Report
                                </button>
                                {(!cachedFacebook && !hasFacebookFull) && (
                                  <button
                                    onClick={() => handleFacebookScrape(companyDrawer.companyName)}
                                    className="company-side-btn company-side-btn-run"
                                  >
                                    Run Facebook (1cr)
                                  </button>
                                )}
                              </div>
                            </details>

                            <details className="bg-gray-800 rounded-lg p-4" onToggle={(e) => {
                              if ((e.currentTarget as HTMLDetailsElement).open) loadYouTube();
                            }}>
                              <summary className="cursor-pointer text-gray-200 font-medium">YouTube</summary>
                              <div className="mt-3 text-sm text-gray-300 space-y-4">
                                {youtubeSummary ? (
                                  <>
                                    <div className="bg-gradient-to-br from-red-900/40 to-gray-900 border border-red-700/40 rounded-xl p-5">
                                      <div className="flex items-start gap-4">
                                        <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
                                          {youtubeSummary.channelAvatar ? (
                                            <img src={youtubeSummary.channelAvatar} alt="" className="w-full h-full object-cover" />
                                          ) : (
                                            <span className="text-xs text-gray-400">Avatar</span>
                                          )}
                                        </div>
                                        <div className="flex-1">
                                          <p className="text-xs uppercase tracking-wide text-red-300/80">YouTube Channel</p>
                                          <h4 className="text-lg font-semibold text-white">
                                            {youtubeSummary.channelName || 'YouTube Channel'}
                                          </h4>
                                          <p className="text-sm text-gray-400 mt-1">
                                            {youtubeSummary.channelLocation || 'Location unknown'}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-300">
                                        <div className="bg-gray-900/60 rounded-lg p-2">
                                          <span className="text-gray-500">Subscribers</span>
                                          <div>{youtubeSummary.subscribers ?? 'Unknown'}</div>
                                        </div>
                                        <div className="bg-gray-900/60 rounded-lg p-2">
                                          <span className="text-gray-500">Total Views</span>
                                          <div>{youtubeSummary.totalViews ?? 'Unknown'}</div>
                                        </div>
                                        <div className="bg-gray-900/60 rounded-lg p-2">
                                          <span className="text-gray-500">Videos</span>
                                          <div>{youtubeSummary.totalVideos ?? 'Unknown'}</div>
                                        </div>
                                      </div>
                                      {youtubeSummary.channelUrl && (
                                        <a className="text-xs text-red-300 hover:text-red-200 mt-2 inline-block" href={youtubeSummary.channelUrl} target="_blank" rel="noreferrer">
                                          View channel
                                        </a>
                                      )}
                                    </div>

                                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                                      <p className="text-xs uppercase text-gray-500 mb-3">Recent Video</p>
                                      {youtubeSummary.recentVideos.length === 0 ? (
                                        <p className="text-sm text-gray-400">No videos captured.</p>
                                      ) : (() => {
                                        const video = youtubeSummary.recentVideos[0];
                                        return (
                                          <div className="bg-gray-800/60 rounded-lg p-3">
                                            <div className="text-xs text-gray-400">
                                              {video.date ? new Date(video.date).toLocaleDateString() : 'No date'}
                                            </div>
                                            <div className="text-sm text-gray-200 mt-1">
                                              {video.title || video.text || 'Video'}
                                            </div>
                                            {video.thumbnail && (
                                              <div className="mt-2">
                                                <img src={video.thumbnail} alt="" className="w-full rounded-md border border-gray-700" />
                                              </div>
                                            )}
                                            {video.text && (
                                              <div className="mt-2 text-xs text-gray-300">{video.text}</div>
                                            )}
                                            <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                              {video.views !== undefined && (
                                                <span className="px-2 py-0.5 rounded bg-gray-900/60">Views {video.views}</span>
                                              )}
                                              {video.likes !== undefined && (
                                                <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {video.likes}</span>
                                              )}
                                              {video.comments !== undefined && (
                                                <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {video.comments}</span>
                                              )}
                                            </div>
                                            {video.url && (
                                              <a className="text-xs text-red-300 hover:text-red-200 mt-2 inline-block" href={video.url} target="_blank" rel="noreferrer">
                                                View video
                                              </a>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-gray-500">No YouTube data found.</div>
                                )}
                                <button onClick={viewYouTube} className="company-side-btn company-side-btn-view">
                                  View Full Report
                                </button>
                                {(!cachedYouTube && !hasYouTubeFull) && (
                                  <button
                                    onClick={() => handleYouTubeScrape(companyDrawer.companyName)}
                                    className="company-side-btn company-side-btn-run"
                                  >
                                    Run YouTube (1cr)
                                  </button>
                                )}
                              </div>
                            </details>

                            <details className="bg-gray-800 rounded-lg p-4" onToggle={(e) => {
                              if ((e.currentTarget as HTMLDetailsElement).open) loadTiktok();
                            }}>
                              <summary className="cursor-pointer text-gray-200 font-medium">TikTok</summary>
                              <div className="mt-3 text-sm text-gray-300 space-y-4">
                                {tiktokSummary ? (
                                  <>
                                    <div className="bg-gradient-to-br from-cyan-900/40 to-gray-900 border border-cyan-700/40 rounded-xl p-5">
                                      <div className="flex items-start gap-4">
                                        <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
                                          {tiktokSummary.avatar ? (
                                            <img src={tiktokSummary.avatar} alt="" className="w-full h-full object-cover" />
                                          ) : (
                                            <span className="text-xs text-gray-400">Avatar</span>
                                          )}
                                        </div>
                                        <div className="flex-1">
                                          <p className="text-xs uppercase tracking-wide text-cyan-300/80">TikTok Profile</p>
                                          <h4 className="text-lg font-semibold text-white">{tiktokSummary.fullName || 'TikTok Profile'}</h4>
                                          <p className="text-sm text-gray-400 mt-1">{tiktokSummary.bio || 'No bio found.'}</p>
                                        </div>
                                        <div className="flex flex-col items-end gap-2">
                                          {tiktokSummary.isVerified && (
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-cyan-600 text-white">Verified</span>
                                          )}
                                          {tiktokSummary.username && (
                                            <span className="text-xs text-cyan-200">@{tiktokSummary.username}</span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs text-gray-300">
                                        <div className="bg-gray-900/60 rounded-lg p-2">
                                          <span className="text-gray-500">Followers</span>
                                          <div>{tiktokSummary.followers ?? 'Unknown'}</div>
                                        </div>
                                        <div className="bg-gray-900/60 rounded-lg p-2">
                                          <span className="text-gray-500">Following</span>
                                          <div>{tiktokSummary.following ?? 'Unknown'}</div>
                                        </div>
                                        <div className="bg-gray-900/60 rounded-lg p-2">
                                          <span className="text-gray-500">Videos</span>
                                          <div>{tiktokSummary.videos ?? 'Unknown'}</div>
                                        </div>
                                        <div className="bg-gray-900/60 rounded-lg p-2">
                                          <span className="text-gray-500">Hearts</span>
                                          <div>{tiktokSummary.hearts ?? 'Unknown'}</div>
                                        </div>
                                      </div>
                                      {tiktokSummary.website && (
                                        <div className="mt-3 text-xs text-gray-400">Website: {tiktokSummary.website}</div>
                                      )}
                                      {tiktokSummary.profileUrl && (
                                        <a className="text-xs text-cyan-300 hover:text-cyan-200 mt-2 inline-block" href={tiktokSummary.profileUrl} target="_blank" rel="noreferrer">
                                          View profile
                                        </a>
                                      )}
                                    </div>

                                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                                      <p className="text-xs uppercase text-gray-500 mb-3">Recent Post</p>
                                      {tiktokSummary.recentPosts.length === 0 ? (
                                        <p className="text-sm text-gray-400">No posts captured.</p>
                                      ) : (() => {
                                        const post = tiktokSummary.recentPosts[0];
                                        return (
                                          <div className="bg-gray-800/60 rounded-lg p-3">
                                            <div className="text-xs text-gray-400">
                                              {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                                            </div>
                                            <div className="text-sm text-gray-200 mt-1 line-clamp-3">{post.caption || 'Post content unavailable'}</div>
                                            {(post.image || post.duration) && (
                                              <div className="mt-2 text-xs text-cyan-200">{post.duration ? 'Video post' : 'Image post'}</div>
                                            )}
                                            <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                              {post.likes !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>}
                                              {post.comments !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Comments {post.comments}</span>}
                                              {post.shares !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Shares {post.shares}</span>}
                                              {post.views !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Views {post.views}</span>}
                                            </div>
                                            {post.url && (
                                              <a className="text-xs text-cyan-300 hover:text-cyan-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                                                View post
                                              </a>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-gray-500">No TikTok data found.</div>
                                )}
                                <button onClick={viewTiktok} className="company-side-btn company-side-btn-view">
                                  View Full Report
                                </button>
                                {(!cachedTiktok && !hasTiktokFull) && (
                                  <button
                                    onClick={() => handleTikTokScrape(companyDrawer.companyName)}
                                    className="company-side-btn company-side-btn-run"
                                  >
                                    Run TikTok (1cr)
                                  </button>
                                )}
                              </div>
                            </details>

                            <details className="bg-gray-800 rounded-lg p-4" onToggle={(e) => {
                              if ((e.currentTarget as HTMLDetailsElement).open) loadX();
                            }}>
                              <summary className="cursor-pointer text-gray-200 font-medium">X</summary>
                              <div className="mt-3 text-sm text-gray-300 space-y-4">
                                {xSummary ? (
                                  <>
                                    <div className="bg-gradient-to-br from-slate-900/60 to-gray-900 border border-slate-700/40 rounded-xl p-5">
                                      <div className="flex items-start gap-4">
                                        <div className="w-14 h-14 rounded-full bg-gray-800/80 overflow-hidden">
                                          {xSummary.avatar ? (
                                            <img src={xSummary.avatar} alt="" className="w-full h-full object-cover" />
                                          ) : (
                                            <span className="text-xs text-gray-400">Avatar</span>
                                          )}
                                        </div>
                                        <div className="flex-1">
                                          <p className="text-xs uppercase tracking-wide text-slate-300/80">X Profile</p>
                                          <h4 className="text-lg font-semibold text-white">{xSummary.profileName || 'X Profile'}</h4>
                                          <p className="text-sm text-gray-400 mt-1">{xSummary.bio || 'No bio found.'}</p>
                                        </div>
                                        {xSummary.profileHandle && (
                                          <div className="text-xs text-slate-300">@{xSummary.profileHandle}</div>
                                        )}
                                      </div>
                                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-300">
                                        <div className="bg-gray-900/60 rounded-lg p-2">
                                          <span className="text-gray-500">Followers</span>
                                          <div>{xSummary.followers ?? 'Unknown'}</div>
                                        </div>
                                        <div className="bg-gray-900/60 rounded-lg p-2">
                                          <span className="text-gray-500">Following</span>
                                          <div>{xSummary.following ?? 'Unknown'}</div>
                                        </div>
                                      </div>
                                      {xSummary.profileUrl && (
                                        <a className="text-xs text-slate-300 hover:text-slate-200 mt-2 inline-block" href={xSummary.profileUrl} target="_blank" rel="noreferrer">
                                          View profile
                                        </a>
                                      )}
                                    </div>

                                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                                      <p className="text-xs uppercase text-gray-500 mb-3">Recent Post</p>
                                      {xSummary.recentPosts.length === 0 ? (
                                        <p className="text-sm text-gray-400">No posts captured.</p>
                                      ) : (() => {
                                        const post = xSummary.recentPosts[0];
                                        return (
                                          <div className="bg-gray-800/60 rounded-lg p-3">
                                            <div className="text-xs text-gray-400">
                                              {post.date ? new Date(post.date).toLocaleDateString() : 'No date'}
                                            </div>
                                            {post.media && (
                                              <div className="mt-2">
                                                <img src={post.media} alt="" className="w-full rounded-md border border-gray-700" />
                                              </div>
                                            )}
                                            <div className="text-sm text-gray-200 mt-2 line-clamp-3">{post.text || 'Post content unavailable'}</div>
                                            <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-gray-400">
                                              {post.likes !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Likes {post.likes}</span>}
                                              {post.replies !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Replies {post.replies}</span>}
                                              {post.reposts !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Reposts {post.reposts}</span>}
                                              {post.quotes !== undefined && <span className="px-2 py-0.5 rounded bg-gray-900/60">Quotes {post.quotes}</span>}
                                            </div>
                                            {post.url && (
                                              <a className="text-xs text-slate-300 hover:text-slate-200 mt-2 inline-block" href={post.url} target="_blank" rel="noreferrer">
                                                View post
                                              </a>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </>
                                ) : (
                                  <div className="text-gray-500">No X data found.</div>
                                )}
                                <button onClick={viewX} className="company-side-btn company-side-btn-view">
                                  View Full Report
                                </button>
                                {(!cachedX && !hasXFull) && (
                                  <button
                                    onClick={() => handleXScrape(companyDrawer.companyName)}
                                    className="company-side-btn company-side-btn-run"
                                  >
                                    Run X (1cr)
                                  </button>
                                )}
                              </div>
                            </details>

                            <details className="bg-gray-800 rounded-lg p-4" onToggle={(e) => {
                              if ((e.currentTarget as HTMLDetailsElement).open) loadGlassdoor();
                            }}>
                              <summary className="cursor-pointer text-gray-200 font-medium">Glassdoor 360</summary>
                              <div className="mt-3 text-sm text-gray-300 space-y-2">
                                <div>Interviews: {glassdoorSummary.interviewsCount || '—'} • Reviews: {glassdoorSummary.reviewsCount || '—'}</div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
                                    <div className="text-xs text-emerald-300 uppercase tracking-wide mb-2">
                                      Reviews by Department
                                    </div>
                                    {reviewDepartments.length ? (
                                      <div className="space-y-2">
                                        {reviewDepartments.map((row) => (
                                          <div key={`gd-review-${row.dept}`} className="flex items-center justify-between gap-3">
                                            <div>
                                              <div className="text-gray-200">{row.dept}</div>
                                              <div className="text-[11px] text-gray-500">{row.count} reviews</div>
                                            </div>
                                            <div className="text-amber-300 text-sm">
                                              ★ {row.rating != null ? row.rating.toFixed(1) : '—'}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="text-gray-500">No department breakdown available.</div>
                                    )}
                                  </div>
                                  <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">
                                    <div className="text-xs text-emerald-300 uppercase tracking-wide mb-2">
                                      Interviews by Department
                                    </div>
                                    {interviewDepartments.length ? (
                                      <div className="space-y-2">
                                        {interviewDepartments.map((row) => (
                                          <div key={`gd-interview-${row.dept}`} className="flex items-center justify-between gap-3">
                                            <div>
                                              <div className="text-gray-200">{row.dept}</div>
                                              <div className="text-[11px] text-gray-500">{row.count} interviews</div>
                                            </div>
                                            <div className="text-amber-300 text-sm">
                                              Average{row.positivePercent != null ? ` • ${row.positivePercent}% positive` : ''}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="text-gray-500">No department breakdown available.</div>
                                    )}
                                  </div>
                                </div>
                                <button onClick={viewGlassdoor} className="company-side-btn company-side-btn-view">
                                  View Full Report
                                </button>
                                {(!cachedGlassdoor && !hasGlassdoorFull) && (
                                  <button
                                    onClick={() => handleGlassdoorScrape(companyDrawer.companyName)}
                                    className="company-side-btn company-side-btn-run"
                                  >
                                    Run Glassdoor 360 (2cr)
                                  </button>
                                )}
                              </div>
                            </details>

                            <details className="bg-gray-800 rounded-lg p-4" onToggle={(e) => {
                              if ((e.currentTarget as HTMLDetailsElement).open) loadG2();
                            }}>
                              <summary className="cursor-pointer text-gray-200 font-medium">G2</summary>
                              <div className="mt-3 text-sm text-gray-300 space-y-4">
                                <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <span className="text-orange-400 font-semibold">G2</span>
                                    <span className="text-xs text-gray-500">{(g2ByPlatform.g2 || g2Items).length} items</span>
                                  </div>
                                  {g2Summary.product && (
                                    <div className="text-sm text-white font-medium">{g2Summary.product}</div>
                                  )}
                                  {g2Summary.rating && (
                                    <div className="text-yellow-400 text-sm mt-1">★ {g2Summary.rating}</div>
                                  )}
                                  {g2Summary.title && (
                                    <div className="text-xs text-blue-300 mt-1">{g2Summary.title}</div>
                                  )}
                                  {g2Summary.reviewer && (
                                    <div className="text-xs text-gray-500 mt-1">{g2Summary.reviewer}{g2Summary.role ? ` • ${g2Summary.role}` : ''}</div>
                                  )}
                                  {g2Summary.date && (
                                    <div className="text-xs text-gray-500">{new Date(g2Summary.date).toLocaleDateString()}</div>
                                  )}
                                  {g2Summary.body && (
                                    <div className="text-xs text-gray-300 mt-2">{String(g2Summary.body).slice(0, 260)}{String(g2Summary.body).length > 260 ? '…' : ''}</div>
                                  )}
                                  {g2Summary.url && (
                                    <a className="text-xs text-blue-300 hover:text-blue-200 mt-2 inline-block" href={g2Summary.url} target="_blank" rel="noreferrer">
                                      View source →
                                    </a>
                                  )}
                                </div>
                                <button onClick={viewG2} className="company-side-btn company-side-btn-view">
                                  View Full Report
                                </button>
                                {(!cachedG2 && !hasG2Full) && (
                                  <button
                                    onClick={() => handleG2Scrape(companyDrawer.companyName)}
                                    className="company-side-btn company-side-btn-run"
                                  >
                                    Run G2 Report (1cr)
                                  </button>
                                )}
                              </div>
                            </details>

                            <details className="bg-gray-800 rounded-lg p-4" onToggle={(e) => {
                              if ((e.currentTarget as HTMLDetailsElement).open) loadG2();
                            }}>
                              <summary className="cursor-pointer text-gray-200 font-medium">Capterra</summary>
                              <div className="mt-3 text-sm text-gray-300 space-y-4">
                                <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <span className="text-orange-300 font-semibold">Capterra</span>
                                    <span className="text-xs text-gray-500">{(g2ByPlatform.capterra || []).length} items</span>
                                  </div>
                                  {capterraSummary.product && (
                                    <div className="text-sm text-white font-medium">{capterraSummary.product}</div>
                                  )}
                                  {capterraSummary.rating && (
                                    <div className="text-yellow-400 text-sm mt-1">★ {capterraSummary.rating}</div>
                                  )}
                                  {capterraSummary.title && (
                                    <div className="text-xs text-blue-300 mt-1">{capterraSummary.title}</div>
                                  )}
                                  {capterraSummary.reviewer && (
                                    <div className="text-xs text-gray-500 mt-1">{capterraSummary.reviewer}{capterraSummary.role ? ` • ${capterraSummary.role}` : ''}</div>
                                  )}
                                  {capterraSummary.date && (
                                    <div className="text-xs text-gray-500">{new Date(capterraSummary.date).toLocaleDateString()}</div>
                                  )}
                                  {capterraSummary.body && (
                                    <div className="text-xs text-gray-300 mt-2">{String(capterraSummary.body).slice(0, 260)}{String(capterraSummary.body).length > 260 ? '…' : ''}</div>
                                  )}
                                  {capterraSummary.url && (
                                    <a className="text-xs text-blue-300 hover:text-blue-200 mt-2 inline-block" href={capterraSummary.url} target="_blank" rel="noreferrer">
                                      View source →
                                    </a>
                                  )}
                                </div>
                                <button onClick={viewG2} className="company-side-btn company-side-btn-view">
                                  View Full Report
                                </button>
                                {(!cachedG2 && !hasG2Full) && (
                                  <button
                                    onClick={() => handleG2Scrape(companyDrawer.companyName)}
                                    className="company-side-btn company-side-btn-run"
                                  >
                                    Run Capterra (via G2) (1cr)
                                  </button>
                                )}
                              </div>
                            </details>

                            <details className="bg-gray-800 rounded-lg p-4" onToggle={(e) => {
                              if ((e.currentTarget as HTMLDetailsElement).open) loadG2();
                            }}>
                              <summary className="cursor-pointer text-gray-200 font-medium">Gartner</summary>
                              <div className="mt-3 text-sm text-gray-300 space-y-4">
                                <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <span className="text-orange-300 font-semibold">Gartner</span>
                                    <span className="text-xs text-gray-500">{(g2ByPlatform.gartner || []).length} items</span>
                                  </div>
                                  {gartnerSummary.product && (
                                    <div className="text-sm text-white font-medium">{gartnerSummary.product}</div>
                                  )}
                                  {gartnerSummary.rating && (
                                    <div className="text-yellow-400 text-sm mt-1">★ {gartnerSummary.rating}</div>
                                  )}
                                  {gartnerSummary.title && (
                                    <div className="text-xs text-blue-300 mt-1">{gartnerSummary.title}</div>
                                  )}
                                  {gartnerSummary.reviewer && (
                                    <div className="text-xs text-gray-500 mt-1">{gartnerSummary.reviewer}{gartnerSummary.role ? ` • ${gartnerSummary.role}` : ''}</div>
                                  )}
                                  {gartnerSummary.date && (
                                    <div className="text-xs text-gray-500">{new Date(gartnerSummary.date).toLocaleDateString()}</div>
                                  )}
                                  {gartnerSummary.body && (
                                    <div className="text-xs text-gray-300 mt-2">{String(gartnerSummary.body).slice(0, 260)}{String(gartnerSummary.body).length > 260 ? '…' : ''}</div>
                                  )}
                                  {gartnerSummary.url && (
                                    <a className="text-xs text-blue-300 hover:text-blue-200 mt-2 inline-block" href={gartnerSummary.url} target="_blank" rel="noreferrer">
                                      View source →
                                    </a>
                                  )}
                                </div>
                                <button onClick={viewG2} className="company-side-btn company-side-btn-view">
                                  View Full Report
                                </button>
                                {(!cachedG2 && !hasG2Full) && (
                                  <button
                                    onClick={() => handleG2Scrape(companyDrawer.companyName)}
                                    className="company-side-btn company-side-btn-run"
                                  >
                                    Run Gartner (via G2) (1cr)
                                  </button>
                                )}
                              </div>
                            </details>

                            <details className="bg-gray-800 rounded-lg p-4" onToggle={(e) => {
                              if ((e.currentTarget as HTMLDetailsElement).open) loadTrustpilot();
                            }}>
                              <summary className="cursor-pointer text-gray-200 font-medium">Trustpilot</summary>
                              <div className="mt-3 text-sm text-gray-300 space-y-4">
                                {trustpilotItems.length ? (
                                  <div className="bg-gray-900/60 border border-gray-700 rounded-xl p-4">
                                    <div className="flex items-center justify-between mb-3">
                                      <div className="flex items-center gap-2">
                                        <span className="text-emerald-300">🛡️</span>
                                        <span className="text-emerald-300 font-semibold">Trustpilot</span>
                                      </div>
                                      {trustpilotAvg && (
                                        <span className="text-emerald-300 text-lg font-semibold">★ {trustpilotAvg}</span>
                                      )}
                                    </div>
                                    <div className="text-gray-200">{trustpilotCompanyName}</div>
                                    <div className="text-xs text-gray-400">{trustpilotItems.length} reviews scraped</div>

                                    {trustpilotRecent.length > 0 && (
                                      <div className="border-t border-gray-700 pt-3 mt-3 space-y-2">
                                        <div className="text-xs text-gray-500">Recent Reviews:</div>
                                        {trustpilotRecent.map((r: any, idx: number) => (
                                          <div key={`tp-review-${idx}`} className="bg-gray-800/60 rounded-lg p-3">
                                            <div className="flex items-center justify-between">
                                              <span className="text-xs text-blue-400">{r.reviewer || 'Reviewer'}</span>
                                              <div className="flex items-center gap-2">
                                                {r.reviewRatingScore && (
                                                  <span className="text-yellow-400 text-xs">★ {r.reviewRatingScore}/5</span>
                                                )}
                                                {r.reviewDate && (
                                                  <span className="text-gray-500 text-xs">
                                                    {new Date(r.reviewDate).toLocaleDateString()}
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                            {r.reviewTitle && (
                                              <div className="text-sm text-white font-medium mt-1">{r.reviewTitle}</div>
                                            )}
                                            <div className="text-xs text-gray-300 mt-1">
                                              {String(r.reviewDescription || '').slice(0, 260)}
                                              {(r.reviewDescription || '').length > 260 ? '…' : ''}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-gray-500">No Trustpilot data found.</div>
                                )}
                                <button onClick={viewTrustpilot} className="company-side-btn company-side-btn-view">
                                  View Full Report
                                </button>
                                {(!cachedTrustpilot && !hasTrustpilotFull) && (
                                  <button
                                    onClick={() => handleTrustpilotScrape(companyDrawer.companyName)}
                                    className="company-side-btn company-side-btn-run"
                                  >
                                    Run Trustpilot (1cr)
                                  </button>
                                )}
                              </div>
                            </details>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="bg-gray-800 rounded-lg p-4">
                    <div className="text-sm text-gray-400">Contacts ({contacts.length})</div>
                    <div className="mt-2">
                      <div className="text-xs text-gray-500 mb-2">Hierarchy</div>
                      <div className="space-y-4">
                        {groupContactsByHierarchy(contacts).map((group) => (
                          <div key={group.label}>
                            <div className="text-xs text-gray-400 mb-1">{group.label}</div>
                            {group.items.length === 0 ? (
                              <div className="text-xs text-gray-600">None</div>
                            ) : (
                              <div className="space-y-2">
                                {group.items.map((ct) => (
                                  <div key={ct.searchResultId} className="flex items-center justify-between text-sm">
                                    <button
                                      onClick={() => handleDrawerRerunSeamless(ct)}
                                      className="text-white hover:text-red-400 flex items-center gap-2"
                                      title="Click name to run research (1 credit)"
                                    >
                                      <span>{ct.fullName || ct.Name || '-'}</span>
                                      {drawerRerunLoading[ct.searchResultId] && <Loader2 className="w-3 h-3 animate-spin" />}
                                    </button>
                                    <div className={getHierarchyTitleClass(group.label)}>{ct.title || ct.Title || '-'}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="mt-6">
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-500">Search</div>
                        <button
                          onClick={() => handleCompanySearch(companyDrawer.companyName, companyDrawer.sampleDomain)}
                          disabled={companySearchLoading[companyDrawer.companyName]}
                          className="px-2 py-1 rounded text-white text-xs bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
                        >
                          {companySearchLoading[companyDrawer.companyName] ? 'Searching...' : 'Search (25)'}
                        </button>
                      </div>
                      <div className="mt-3 space-y-2">
                        {(companySearchResults[companyDrawer.companyName]?.results || []).length === 0 ? (
                          <div className="text-xs text-gray-500">No search results yet.</div>
                        ) : (
                          companySearchResults[companyDrawer.companyName]?.results.map((lead) => {
                            const normalize = (val?: string) => (val || '').toString().trim().toLowerCase();
                            const leadName = normalize(lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`.trim());
                            const leadTitle = normalize(lead.title || '');
                            const leadCompany = normalize(companyDrawer.companyName);
                            const alreadyInContacts = contacts.some((ct) => {
                              const ctName = normalize(ct.fullName || ct.Name);
                              const ctTitle = normalize(ct.title || ct.Title);
                              const ctCompany = normalize((ct.company as string) || (ct.Company as string) || companyDrawer.companyName);
                              return ctName === leadName && ctTitle === leadTitle && ctCompany === leadCompany;
                            });
                            return (
                              <div key={lead.searchResultId} className="flex items-center justify-between text-sm">
                                <button
                                  onClick={() => handleCompanySearchResearch(lead)}
                                  className="text-white hover:text-red-400 flex items-center gap-2 bg-gray-700 px-2 py-1 rounded"
                                  title="Click to run research (1 credit)"
                                >
                                  <span>{lead.name || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || '-'}</span>
                                  {companySearchResearchLoading[lead.searchResultId] && <Loader2 className="w-3 h-3 animate-spin" />}
                                </button>
                                <div className="flex items-center gap-3">
                                  <div className="text-gray-400">{lead.title || '-'}</div>
                                  {alreadyInContacts && (
                                    <span className="text-emerald-300 text-xs bg-emerald-900/40 border border-emerald-700/40 px-2 py-0.5 rounded-full">
                                      ✓ Done
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                      <div className="mt-2 text-[11px] text-gray-500">
                        Click name above to run research (1 credit).
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="text-xs text-gray-500 mb-2">Alphabetical</div>
                      <div className="space-y-2">
                        {[...contacts].sort((a, b) => (a.fullName || a.Name || '').localeCompare(b.fullName || b.Name || '')).map((ct) => (
                          <div key={`${ct.searchResultId}-alpha`} className="flex items-center justify-between text-sm">
                            <div className="text-white">{ct.fullName || ct.Name || '-'}</div>
                            <div className="text-gray-400">{ct.title || ct.Title || '-'}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {buyIntentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/80" onClick={() => setBuyIntentModal(null)} />
          <div className="bg-card-dark border border-gray-700 rounded-xl max-w-lg w-full mx-4 p-6 relative z-10">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-white">Buy Intent Leads</h3>
                <p className="text-sm text-gray-400">{buyIntentModal.companyName}</p>
              </div>
              <button onClick={() => setBuyIntentModal(null)} className="text-gray-400 hover:text-white text-2xl">×</button>
            </div>

            <p className="text-sm text-gray-300 mb-4">
              We will contact you within 24 hours to discuss next steps and pricing.
              We will also share the expected lead volume and price range so you can decide if it’s worth the investment.
            </p>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500">Name</label>
                <input
                  value={buyIntentName}
                  onChange={(e) => setBuyIntentName(e.target.value)}
                  className="mt-1 w-full rounded bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-white"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Email</label>
                <input
                  value={buyIntentEmail}
                  onChange={(e) => setBuyIntentEmail(e.target.value)}
                  className="mt-1 w-full rounded bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-white"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Keywords / Phrases (up to 3)</label>
                <div className="mt-1 space-y-2">
                  {buyIntentKeywords.map((value, idx) => (
                    <input
                      key={idx}
                      value={value}
                      onChange={(e) => {
                        const next = [...buyIntentKeywords];
                        next[idx] = e.target.value;
                        setBuyIntentKeywords(next);
                      }}
                      className="w-full rounded bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-white"
                      placeholder={`Keyword ${idx + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {buyIntentStatus && (
              <div className="mt-3 text-sm text-emerald-300">{buyIntentStatus}</div>
            )}

            <div className="mt-4 flex items-center gap-2">
              <button
                onClick={submitBuyIntent}
                disabled={buyIntentSubmitting}
                className="px-4 py-2 rounded bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50"
              >
                {buyIntentSubmitting ? 'Sending…' : 'Submit Request'}
              </button>
              <button
                onClick={() => setBuyIntentModal(null)}
                className="px-4 py-2 rounded bg-gray-800 text-gray-200 text-sm hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Social Drawer */}
      {socialDrawer && (
        <div className="ss-drawer company-side-drawer fixed inset-0 z-50" data-theme={companyModalTheme}>
          <div className="ss-drawer-overlay" onClick={() => setSocialDrawer(null)} />
          <div className="ss-drawer-panel company-side-panel">
            <div className="ss-drawer-inner">
              <div className="ss-drawer-head">
                <div>
                  <h3 className="ss-drawer-title">{socialDrawer.companyName}</h3>
                  <p className="ss-drawer-sub">{socialLabels[socialDrawer.platform]}</p>
                </div>
                <button onClick={() => setSocialDrawer(null)} className="ss-drawer-close">×</button>
              </div>
              <div className="company-side-card">
                {(() => {
                  const entity = getCompanyEntity(socialDrawer.companyName) || {};
                  const url = (entity[socialField[socialDrawer.platform]] as string | undefined) ||
                    (socialDrawer.platform === 'linkedin' ? getFallbackLinkedIn(socialDrawer.companyName) : undefined);
                  const hasReport =
                    socialDrawer.platform === 'linkedin'
                      ? (Array.isArray(entity.linkedin_profile_ds) && entity.linkedin_profile_ds.length > 0) ||
                        (Array.isArray(entity.linkedin_posts_ds) && entity.linkedin_posts_ds.length > 0) ||
                        (Array.isArray(entity.linkedin_jobs_ds) && entity.linkedin_jobs_ds.length > 0) ||
                        (Array.isArray(entity.linkedin_employees_ds) && entity.linkedin_employees_ds.length > 0)
                      : socialDrawer.platform === 'glassdoor'
                        ? (Array.isArray(entity.glassdoor_overview_ds) && entity.glassdoor_overview_ds.length > 0) ||
                          (Array.isArray(entity.glassdoor_reviews_ds) && entity.glassdoor_reviews_ds.length > 0) ||
                          (Array.isArray(entity.glassdoor_salaries_ds) && entity.glassdoor_salaries_ds.length > 0) ||
                          (Array.isArray(entity.glassdoor_interviews_ds) && entity.glassdoor_interviews_ds.length > 0) ||
                          (Array.isArray(entity.glassdoor_benefits_ds) && entity.glassdoor_benefits_ds.length > 0)
                        : socialDrawer.platform === 'g2'
                          ? Array.isArray(entity.g2_ds) && entity.g2_ds.length > 0
                          : socialDrawer.platform === 'trustpilot'
                            ? Array.isArray(entity.trustpilot_ds) && entity.trustpilot_ds.length > 0
                            : socialDrawer.platform === 'instagram'
                              ? Array.isArray(entity.instagram_ds) && entity.instagram_ds.length > 0
                              : socialDrawer.platform === 'facebook'
                                ? Array.isArray(entity.facebook_ds) && entity.facebook_ds.length > 0
                                : socialDrawer.platform === 'youtube'
                                  ? Array.isArray(entity.youtube_ds) && entity.youtube_ds.length > 0
                                  : socialDrawer.platform === 'tiktok'
                                    ? Array.isArray(entity.tiktok_ds) && entity.tiktok_ds.length > 0
                                    : socialDrawer.platform === 'threads'
                                      ? Array.isArray(entity.threads_ds) && entity.threads_ds.length > 0
                                      : socialDrawer.platform === 'x'
                                        ? Array.isArray(entity.x_ds) && entity.x_ds.length > 0
                                        : false;
                  const hasLinkedinFull =
                    (Array.isArray(entity.linkedin_profile_ds) && entity.linkedin_profile_ds.length > 0) ||
                    (Array.isArray(entity.linkedin_posts_ds) && entity.linkedin_posts_ds.length > 0) ||
                    (Array.isArray(entity.linkedin_jobs_ds) && entity.linkedin_jobs_ds.length > 0) ||
                    (Array.isArray(entity.linkedin_employees_ds) && entity.linkedin_employees_ds.length > 0);
                  const cachedLinkedIn = socialDrawer.platform === 'linkedin'
                    ? linkedinCache[socialDrawer.companyName]
                    : null;
                  const hasGlassdoorFull =
                    (Array.isArray(entity.glassdoor_overview_ds) && entity.glassdoor_overview_ds.length > 0) ||
                    (Array.isArray(entity.glassdoor_reviews_ds) && entity.glassdoor_reviews_ds.length > 0) ||
                    (Array.isArray(entity.glassdoor_salaries_ds) && entity.glassdoor_salaries_ds.length > 0) ||
                    (Array.isArray(entity.glassdoor_interviews_ds) && entity.glassdoor_interviews_ds.length > 0) ||
                    (Array.isArray(entity.glassdoor_benefits_ds) && entity.glassdoor_benefits_ds.length > 0);
                  const cachedGlassdoor = socialDrawer.platform === 'glassdoor'
                    ? glassdoorCache[socialDrawer.companyName]
                    : null;
                  const hasG2Full =
                    (Array.isArray(entity.g2_ds) && entity.g2_ds.length > 0);
                  const cachedG2 = socialDrawer.platform === 'g2'
                    ? g2Cache[socialDrawer.companyName]
                    : null;
                  if (url || hasReport) {
                    if (cachedLinkedIn || hasLinkedinFull) {
                      return (
                        <div className="text-sm text-gray-300">
                          LinkedIn data is ready. Click “View Report”.
                        </div>
                      );
                    }
                    if (cachedGlassdoor || hasGlassdoorFull) {
                      return (
                        <div className="text-sm text-gray-300">
                          Glassdoor data is ready. Click “View Report”.
                        </div>
                      );
                    }
                    if (cachedG2 || hasG2Full) {
                      return (
                        <div className="text-sm text-gray-300">
                          G2 data is ready. Click “View Report”.
                        </div>
                      );
                    }
                    if (socialDrawer.platform === 'trustpilot' && (Array.isArray(entity.trustpilot_ds) && entity.trustpilot_ds.length > 0)) {
                      return (
                        <div className="text-sm text-gray-300">
                          Trustpilot data is ready. Click “View Report”.
                        </div>
                      );
                    }
                    return (
                      url ? (
                        <a href={url} target="_blank" rel="noreferrer" className="text-blue-300 hover:text-blue-200 underline">
                          {url}
                        </a>
                      ) : (
                        <div className="text-sm text-gray-300">
                          Report is ready. Click “View Report”.
                        </div>
                      )
                    );
                  }
                  const scraped = entity.socials_last_scraped_at;
                  return (
                    <div className="text-sm text-gray-400">
                      {scraped
                        ? 'Not found on website. Run Find (1cr).'
                        : 'Add a URL or run Find (1cr) to locate the profile.'}
                    </div>
                  );
                })()}
                <div className="company-side-actions">
                  <button
                    onClick={() => openSocialLinkEditor(socialDrawer.companyName, socialDrawer.platform)}
                    className="company-side-btn company-side-btn-ghost"
                  >
                    Edit URL
                  </button>
                </div>
              </div>
              <div className="company-side-actions">
                {socialDrawer.platform === 'linkedin' ? (
                  <>
                    {(() => {
                      const entity = getCompanyEntity(socialDrawer.companyName) || {};
                      const hasLinkedinFull =
                        (Array.isArray(entity.linkedin_profile_ds) && entity.linkedin_profile_ds.length > 0) ||
                        (Array.isArray(entity.linkedin_posts_ds) && entity.linkedin_posts_ds.length > 0) ||
                        (Array.isArray(entity.linkedin_jobs_ds) && entity.linkedin_jobs_ds.length > 0) ||
                        (Array.isArray(entity.linkedin_employees_ds) && entity.linkedin_employees_ds.length > 0);
                      const cachedLinkedIn = linkedinCache[socialDrawer.companyName];
                      const onView = async () => {
                        if (cachedLinkedIn) {
                          setLinkedinDrawer({ companyName: socialDrawer.companyName, data: cachedLinkedIn.data, scrapedAt: cachedLinkedIn.scrapedAt });
                          return;
                        }
                        if (!hasLinkedinFull) return;
                        const full = await fetchLinkedinFull(socialDrawer.companyName);
                        if (full) {
                          const payload = { data: full, scrapedAt: entity?.linkedin_scraped_at };
                          setLinkedinCache((prev) => ({ ...prev, [socialDrawer.companyName]: payload }));
                          setLinkedinDrawer({ companyName: socialDrawer.companyName, data: full, scrapedAt: entity?.linkedin_scraped_at });
                        }
                      };
                      return (cachedLinkedIn || hasLinkedinFull) ? (
                        <button
                          onClick={onView}
                          className="company-side-btn company-side-btn-view"
                        >
                          View Report
                        </button>
                      ) : null;
                    })()}
                    <button
                      onClick={() => {
                        const entity = getCompanyEntity(socialDrawer.companyName) || {};
                        const url = (entity[socialField[socialDrawer.platform]] as string | undefined) ||
                          getFallbackLinkedIn(socialDrawer.companyName);
                        handleLinkedInScrape(socialDrawer.companyName, url);
                      }}
                      disabled={!!linkedinLoading[socialDrawer.companyName]}
                      className="company-side-btn company-side-btn-run"
                    >
                      {linkedinLoading[socialDrawer.companyName] ? 'Scraping...' : 'Scrape (2cr)'}
                    </button>
                    <button
                      onClick={() => handleSocialSearch(socialDrawer.companyName, socialDrawer.platform)}
                      className="company-side-btn company-side-btn-run"
                    >
                      Find (1cr)
                    </button>
                  </>
                ) : socialDrawer.platform === 'glassdoor' ? (
                  <>
                    {(() => {
                      const entity = getCompanyEntity(socialDrawer.companyName) || {};
                      const hasGlassdoorFull =
                        (Array.isArray(entity.glassdoor_overview_ds) && entity.glassdoor_overview_ds.length > 0) ||
                        (Array.isArray(entity.glassdoor_reviews_ds) && entity.glassdoor_reviews_ds.length > 0) ||
                        (Array.isArray(entity.glassdoor_salaries_ds) && entity.glassdoor_salaries_ds.length > 0) ||
                        (Array.isArray(entity.glassdoor_interviews_ds) && entity.glassdoor_interviews_ds.length > 0) ||
                        (Array.isArray(entity.glassdoor_benefits_ds) && entity.glassdoor_benefits_ds.length > 0);
                      const cachedGlassdoor = glassdoorCache[socialDrawer.companyName];
                      const onView = async () => {
                        if (cachedGlassdoor) {
                          setGlassdoorDrawer({ companyName: socialDrawer.companyName, data: cachedGlassdoor.data, scrapedAt: cachedGlassdoor.scrapedAt });
                          return;
                        }
                        if (hasGlassdoorFull) {
                          const full = await fetchGlassdoorFull(socialDrawer.companyName);
                          if (full) {
                            const payload = { data: full, scrapedAt: entity.glassdoor_scraped_at };
                            setGlassdoorCache((prev) => ({ ...prev, [socialDrawer.companyName]: payload }));
                            setGlassdoorDrawer({ companyName: socialDrawer.companyName, data: full, scrapedAt: entity.glassdoor_scraped_at });
                          }
                          return;
                        }
                      };
                      return (cachedGlassdoor || hasGlassdoorFull) ? (
                        <button
                          onClick={onView}
                          className="company-side-btn company-side-btn-view"
                        >
                          View Report
                        </button>
                      ) : null;
                    })()}
                    <button
                      onClick={() => handleGlassdoorScrape(socialDrawer.companyName)}
                      disabled={!!glassdoorLoading[socialDrawer.companyName]}
                      className="company-side-btn company-side-btn-run"
                    >
                      {glassdoorLoading[socialDrawer.companyName] ? 'Scraping...' : 'Scrape (2cr)'}
                    </button>
                    <button
                      onClick={() => handleSocialSearch(socialDrawer.companyName, socialDrawer.platform)}
                      className="company-side-btn company-side-btn-run"
                    >
                      Find (1cr)
                    </button>
                  </>
                ) : socialDrawer.platform === 'g2' ? (
                  <>
                    {(() => {
                      const entity = getCompanyEntity(socialDrawer.companyName) || {};
                      const hasG2Full = Array.isArray(entity.g2_ds) && entity.g2_ds.length > 0;
                      const cachedG2 = g2Cache[socialDrawer.companyName];
                      const onView = async () => {
                        if (cachedG2) {
                          setG2Drawer({ companyName: socialDrawer.companyName, data: cachedG2.data, scrapedAt: cachedG2.scrapedAt });
                          return;
                        }
                        if (!hasG2Full) return;
                        const full = await fetchG2Full(socialDrawer.companyName);
                        if (full) {
                          const payload = { data: full, scrapedAt: entity.g2_scraped_at };
                          setG2Cache((prev) => ({ ...prev, [socialDrawer.companyName]: payload }));
                          setG2Drawer({ companyName: socialDrawer.companyName, data: full, scrapedAt: entity.g2_scraped_at });
                        }
                      };
                      return (cachedG2 || hasG2Full) ? (
                        <button
                          onClick={onView}
                          className="company-side-btn company-side-btn-view"
                        >
                          View Report
                        </button>
                      ) : null;
                    })()}
                    <button
                      onClick={() => handleG2Scrape(socialDrawer.companyName)}
                      disabled={!!g2Loading[socialDrawer.companyName]}
                      className="company-side-btn company-side-btn-run"
                    >
                      {g2Loading[socialDrawer.companyName] ? 'Scraping...' : 'Scrape (1cr)'}
                    </button>
                    <button
                      onClick={() => handleSocialSearch(socialDrawer.companyName, socialDrawer.platform)}
                      className="company-side-btn company-side-btn-run"
                    >
                      Find (1cr)
                    </button>
                  </>
                ) : socialDrawer.platform === 'trustpilot' ? (
                  <>
                    {(() => {
                      const entity = getCompanyEntity(socialDrawer.companyName) || {};
                      const hasTrustpilotFull = Array.isArray(entity.trustpilot_ds) && entity.trustpilot_ds.length > 0;
                      const cachedTrustpilot = trustpilotCache[socialDrawer.companyName];
                      const onView = async () => {
                        if (cachedTrustpilot) {
                          setTrustpilotDrawer({ companyName: socialDrawer.companyName, data: cachedTrustpilot.data, scrapedAt: cachedTrustpilot.scrapedAt });
                          return;
                        }
                        if (!hasTrustpilotFull) return;
                        const full = await fetchTrustpilotFull(socialDrawer.companyName);
                        if (full) {
                          const payload = { data: full, scrapedAt: entity.trustpilot_scraped_at };
                          setTrustpilotCache((prev) => ({ ...prev, [socialDrawer.companyName]: payload }));
                          setTrustpilotDrawer({ companyName: socialDrawer.companyName, data: full, scrapedAt: entity.trustpilot_scraped_at });
                        }
                      };
                      return (cachedTrustpilot || hasTrustpilotFull) ? (
                        <button
                          onClick={onView}
                          className="company-side-btn company-side-btn-view"
                        >
                          View Report
                        </button>
                      ) : null;
                    })()}
                    <button
                      onClick={() => handleTrustpilotScrape(socialDrawer.companyName)}
                      disabled={!!trustpilotLoading[socialDrawer.companyName]}
                      className="company-side-btn company-side-btn-run"
                    >
                      {trustpilotLoading[socialDrawer.companyName] ? 'Scraping...' : 'Scrape (1cr)'}
                    </button>
                    <button
                      onClick={() => handleSocialSearch(socialDrawer.companyName, socialDrawer.platform)}
                      className="company-side-btn company-side-btn-run"
                    >
                      Find (1cr)
                    </button>
                  </>
                ) : socialDrawer.platform === 'instagram' ? (
                  <>
                    {(() => {
                      const entity = getCompanyEntity(socialDrawer.companyName) || {};
                      const hasInstagramFull = Array.isArray(entity.instagram_ds) && entity.instagram_ds.length > 0;
                      const cachedInstagram = instagramCache[socialDrawer.companyName];
                      const onView = async () => {
                        if (cachedInstagram) {
                          setInstagramDrawer({ companyName: socialDrawer.companyName, data: cachedInstagram.data, scrapedAt: cachedInstagram.scrapedAt });
                          return;
                        }
                        if (!hasInstagramFull) return;
                        const full = await fetchInstagramFull(socialDrawer.companyName);
                        if (full) {
                          const payload = { data: full, scrapedAt: entity.instagram_scraped_at };
                          setInstagramCache((prev) => ({ ...prev, [socialDrawer.companyName]: payload }));
                          setInstagramDrawer({ companyName: socialDrawer.companyName, data: full, scrapedAt: entity.instagram_scraped_at });
                        }
                      };
                      return (cachedInstagram || hasInstagramFull) ? (
                        <button
                          onClick={onView}
                          className="company-side-btn company-side-btn-view"
                        >
                          View Report
                        </button>
                      ) : null;
                    })()}
                    <button
                      onClick={() => handleInstagramScrape(socialDrawer.companyName)}
                      disabled={!!instagramLoading[socialDrawer.companyName]}
                      className="company-side-btn company-side-btn-run"
                    >
                      {instagramLoading[socialDrawer.companyName] ? 'Scraping...' : 'Scrape (1cr)'}
                    </button>
                    <button
                      onClick={() => handleSocialSearch(socialDrawer.companyName, socialDrawer.platform)}
                      className="company-side-btn company-side-btn-run"
                    >
                      Find (1cr)
                    </button>
                  </>
                ) : socialDrawer.platform === 'facebook' ? (
                  <>
                    {(() => {
                      const entity = getCompanyEntity(socialDrawer.companyName) || {};
                      const hasFacebookFull = Array.isArray(entity.facebook_ds) && entity.facebook_ds.length > 0;
                      const cachedFacebook = facebookCache[socialDrawer.companyName];
                      const onView = async () => {
                        if (cachedFacebook) {
                          setFacebookDrawer({ companyName: socialDrawer.companyName, data: cachedFacebook.data, scrapedAt: cachedFacebook.scrapedAt });
                          return;
                        }
                        if (!hasFacebookFull) return;
                        const full = await fetchFacebookFull(socialDrawer.companyName);
                        if (full) {
                          const payload = { data: full, scrapedAt: entity.facebook_scraped_at };
                          setFacebookCache((prev) => ({ ...prev, [socialDrawer.companyName]: payload }));
                          setFacebookDrawer({ companyName: socialDrawer.companyName, data: full, scrapedAt: entity.facebook_scraped_at });
                        }
                      };
                      return (cachedFacebook || hasFacebookFull) ? (
                        <button
                          onClick={onView}
                          className="company-side-btn company-side-btn-view"
                        >
                          View Report
                        </button>
                      ) : null;
                    })()}
                    <button
                      onClick={() => handleFacebookScrape(socialDrawer.companyName)}
                      disabled={!!facebookLoading[socialDrawer.companyName]}
                      className="company-side-btn company-side-btn-run"
                    >
                      {facebookLoading[socialDrawer.companyName] ? 'Scraping...' : 'Scrape (1cr)'}
                    </button>
                    <button
                      onClick={() => handleSocialSearch(socialDrawer.companyName, socialDrawer.platform)}
                      className="company-side-btn company-side-btn-run"
                    >
                      Find (1cr)
                    </button>
                  </>
                ) : socialDrawer.platform === 'youtube' ? (
                  <>
                    {(() => {
                      const entity = getCompanyEntity(socialDrawer.companyName) || {};
                      const hasYouTubeFull = Array.isArray(entity.youtube_ds) && entity.youtube_ds.length > 0;
                      const cachedYouTube = youtubeCache[socialDrawer.companyName];
                      const onView = async () => {
                        if (cachedYouTube) {
                          setYoutubeDrawer({ companyName: socialDrawer.companyName, data: cachedYouTube.data, scrapedAt: cachedYouTube.scrapedAt });
                          return;
                        }
                        if (!hasYouTubeFull) return;
                        const full = await fetchYouTubeFull(socialDrawer.companyName);
                        if (full) {
                          const payload = { data: full, scrapedAt: entity.youtube_scraped_at };
                          setYoutubeCache((prev) => ({ ...prev, [socialDrawer.companyName]: payload }));
                          setYoutubeDrawer({ companyName: socialDrawer.companyName, data: full, scrapedAt: entity.youtube_scraped_at });
                        }
                      };
                      return (cachedYouTube || hasYouTubeFull) ? (
                        <button
                          onClick={onView}
                          className="company-side-btn company-side-btn-view"
                        >
                          View Report
                        </button>
                      ) : null;
                    })()}
                    <button
                      onClick={() => handleYouTubeScrape(socialDrawer.companyName)}
                      disabled={!!youtubeLoading[socialDrawer.companyName]}
                      className="company-side-btn company-side-btn-run"
                    >
                      {youtubeLoading[socialDrawer.companyName] ? 'Scraping...' : 'Scrape (1cr)'}
                    </button>
                    <button
                      onClick={() => handleSocialSearch(socialDrawer.companyName, socialDrawer.platform)}
                      className="company-side-btn company-side-btn-run"
                    >
                      Find (1cr)
                    </button>
                  </>
                ) : socialDrawer.platform === 'tiktok' ? (
                  <>
                    {(() => {
                      const entity = getCompanyEntity(socialDrawer.companyName) || {};
                      const hasTikTokFull = Array.isArray(entity.tiktok_ds) && entity.tiktok_ds.length > 0;
                      const cachedTikTok = tiktokCache[socialDrawer.companyName];
                      const onView = async () => {
                        if (cachedTikTok) {
                          setTiktokDrawer({ companyName: socialDrawer.companyName, data: cachedTikTok.data, scrapedAt: cachedTikTok.scrapedAt });
                          return;
                        }
                        if (!hasTikTokFull) return;
                        const full = await fetchTikTokFull(socialDrawer.companyName);
                        if (full) {
                          const payload = { data: full, scrapedAt: entity.tiktok_scraped_at };
                          setTiktokCache((prev) => ({ ...prev, [socialDrawer.companyName]: payload }));
                          setTiktokDrawer({ companyName: socialDrawer.companyName, data: full, scrapedAt: entity.tiktok_scraped_at });
                        }
                      };
                      return (cachedTikTok || hasTikTokFull) ? (
                        <button
                          onClick={onView}
                          className="company-side-btn company-side-btn-view"
                        >
                          View Report
                        </button>
                      ) : null;
                    })()}
                    <button
                      onClick={() => handleTikTokScrape(socialDrawer.companyName)}
                      disabled={!!tiktokLoading[socialDrawer.companyName]}
                      className="company-side-btn company-side-btn-run"
                    >
                      {tiktokLoading[socialDrawer.companyName] ? 'Scraping...' : 'Scrape (1cr)'}
                    </button>
                    <button
                      onClick={() => handleSocialSearch(socialDrawer.companyName, socialDrawer.platform)}
                      className="company-side-btn company-side-btn-run"
                    >
                      Find (1cr)
                    </button>
                  </>
                ) : socialDrawer.platform === 'x' ? (
                  <>
                    {(() => {
                      const entity = getCompanyEntity(socialDrawer.companyName) || {};
                      const hasXFull = Array.isArray(entity.x_ds) && entity.x_ds.length > 0;
                      const cachedX = xCache[socialDrawer.companyName];
                      const onView = async () => {
                        if (cachedX) {
                          setXDrawer({ companyName: socialDrawer.companyName, data: cachedX.data, scrapedAt: cachedX.scrapedAt });
                          return;
                        }
                        if (!hasXFull) return;
                        const full = await fetchXFull(socialDrawer.companyName);
                        if (full) {
                          const payload = { data: full, scrapedAt: entity.x_scraped_at };
                          setXCache((prev) => ({ ...prev, [socialDrawer.companyName]: payload }));
                          setXDrawer({ companyName: socialDrawer.companyName, data: full, scrapedAt: entity.x_scraped_at });
                        }
                      };
                      return (cachedX || hasXFull) ? (
                        <button
                          onClick={onView}
                          className="company-side-btn company-side-btn-view"
                        >
                          View Report
                        </button>
                      ) : null;
                    })()}
                    <button
                      onClick={() => handleXScrape(socialDrawer.companyName)}
                      disabled={!!xLoading[socialDrawer.companyName]}
                      className="company-side-btn company-side-btn-run"
                    >
                      {xLoading[socialDrawer.companyName] ? 'Scraping...' : 'Scrape (1cr)'}
                    </button>
                    <button
                      onClick={() => handleSocialSearch(socialDrawer.companyName, socialDrawer.platform)}
                      className="company-side-btn company-side-btn-run"
                    >
                      Find (1cr)
                    </button>
                  </>
                ) : socialDrawer.platform === 'threads' ? (
                  <>
                    {(() => {
                      const entity = getCompanyEntity(socialDrawer.companyName) || {};
                      const hasThreadsFull = Array.isArray(entity.threads_ds) && entity.threads_ds.length > 0;
                      const cachedThreads = threadsCache[socialDrawer.companyName];
                      const onView = async () => {
                        if (cachedThreads) {
                          setThreadsDrawer({ companyName: socialDrawer.companyName, data: cachedThreads.data, scrapedAt: cachedThreads.scrapedAt });
                          return;
                        }
                        if (!hasThreadsFull) return;
                        const full = await fetchThreadsFull(socialDrawer.companyName);
                        if (full) {
                          const payload = { data: full, scrapedAt: entity.threads_scraped_at };
                          setThreadsCache((prev) => ({ ...prev, [socialDrawer.companyName]: payload }));
                          setThreadsDrawer({ companyName: socialDrawer.companyName, data: full, scrapedAt: entity.threads_scraped_at });
                        }
                      };
                      return (cachedThreads || hasThreadsFull) ? (
                        <button
                          onClick={onView}
                          className="company-side-btn company-side-btn-view"
                        >
                          View Report
                        </button>
                      ) : null;
                    })()}
                    <button
                      onClick={() => handleThreadsScrape(socialDrawer.companyName)}
                      disabled={!!threadsLoading[socialDrawer.companyName]}
                      className="company-side-btn company-side-btn-run"
                    >
                      {threadsLoading[socialDrawer.companyName] ? 'Scraping...' : 'Scrape (1cr)'}
                    </button>
                    <button
                      onClick={() => handleSocialSearch(socialDrawer.companyName, socialDrawer.platform)}
                      className="company-side-btn company-side-btn-run"
                    >
                      Find (1cr)
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleSocialSearch(socialDrawer.companyName, socialDrawer.platform)}
                    className="company-side-btn company-side-btn-run"
                  >
                    Find (1cr)
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}


      {websiteActions && (
        <div className="ss-drawer company-side-drawer fixed inset-0 z-50" data-theme={companyModalTheme}>
          <div className="ss-drawer-overlay" onClick={() => setWebsiteActions(null)} />
          <div className="ss-drawer-panel company-side-panel">
            <div className="ss-drawer-inner">
              <div className="ss-drawer-head">
                <div>
                  <h3 className="ss-drawer-title">{websiteActions.companyName}</h3>
                  <p className="ss-drawer-sub">Website</p>
                </div>
                <button onClick={() => setWebsiteActions(null)} className="ss-drawer-close">×</button>
              </div>
              {(() => {
                const entity = getCompanyEntity(websiteActions.companyName) || {};
                const domain = entity.domain || '';
                const hasWebsiteFull = Array.isArray(entity.website_ds) && entity.website_ds.length > 0;
                const cachedWebsite = websiteCache[websiteActions.companyName];
                const onView = async () => {
                  if (cachedWebsite) {
                    setWebsiteDrawer({ companyName: websiteActions.companyName, data: cachedWebsite.data, scrapedAt: cachedWebsite.scrapedAt });
                    return;
                  }
                  if (!hasWebsiteFull) return;
                  const full = await fetchWebsiteFull(websiteActions.companyName);
                  if (full) {
                    const payload = { data: full, scrapedAt: entity.website_scraped_at };
                    setWebsiteCache((prev) => ({ ...prev, [websiteActions.companyName]: payload }));
                    setWebsiteDrawer({ companyName: websiteActions.companyName, data: full, scrapedAt: entity.website_scraped_at });
                  }
                };
                return (
                  <div className="company-side-card">
                    <div className="text-sm text-gray-300">
                      {domain ? domain : 'No website on file.'}
                    </div>
                    <div className="company-side-actions">
                      {(cachedWebsite || hasWebsiteFull) && (
                        <button
                          onClick={onView}
                          className="company-side-btn company-side-btn-view"
                        >
                          View Report
                        </button>
                      )}
                      {!domain ? (
                        <button
                          onClick={() => handleFindCompanyUrl(websiteActions.companyName)}
                          disabled={!!findingCompanyUrls[websiteActions.companyName]}
                          className="company-side-btn company-side-btn-danger"
                        >
                          {findingCompanyUrls[websiteActions.companyName] ? 'Searching...' : 'Find (1cr)'}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleWebsiteScrape(websiteActions.companyName, domain)}
                          disabled={!!scrapingWebsite[websiteActions.companyName]}
                          className="company-side-btn company-side-btn-run"
                        >
                          {scrapingWebsite[websiteActions.companyName] ? 'Scraping...' : 'Scrape (1cr)'}
                        </button>
                      )}
                      <button
                        onClick={() => {
                          const entity = getCompanyEntity(websiteActions.companyName) || {};
                          const currentDomain = entity.domain || '';
                          setEditCompanyUrl({ companyName: websiteActions.companyName, current: currentDomain });
                          setEditCompanyUrlValue(currentDomain || '');
                          setWebsiteActions(null);
                        }}
                        className="company-side-btn company-side-btn-run"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {linkedinDrawer && (
        <DataDrawer
          isOpen={!!linkedinDrawer}
          onClose={() => setLinkedinDrawer(null)}
          companyName={linkedinDrawer.companyName}
          source="linkedin_360"
          data={linkedinDrawer.data}
          scrapedAt={linkedinDrawer.scrapedAt}
        />
      )}

      {glassdoorDrawer && (
        <DataDrawer
          isOpen={!!glassdoorDrawer}
          onClose={() => setGlassdoorDrawer(null)}
          companyName={glassdoorDrawer.companyName}
          source="glassdoor"
          data={glassdoorDrawer.data}
          scrapedAt={glassdoorDrawer.scrapedAt}
        />
      )}

      {g2Drawer && (
        <DataDrawer
          isOpen={!!g2Drawer}
          onClose={() => setG2Drawer(null)}
          companyName={g2Drawer.companyName}
          source="g2_reviews"
          data={g2Drawer.data}
          scrapedAt={g2Drawer.scrapedAt}
        />
      )}

      {trustpilotDrawer && (
        <DataDrawer
          isOpen={!!trustpilotDrawer}
          onClose={() => setTrustpilotDrawer(null)}
          companyName={trustpilotDrawer.companyName}
          source="trustpilot"
          data={trustpilotDrawer.data}
          scrapedAt={trustpilotDrawer.scrapedAt}
        />
      )}

      {instagramDrawer && (
        <DataDrawer
          isOpen={!!instagramDrawer}
          onClose={() => setInstagramDrawer(null)}
          companyName={instagramDrawer.companyName}
          source="instagram"
          data={instagramDrawer.data}
          scrapedAt={instagramDrawer.scrapedAt}
        />
      )}

      {facebookDrawer && (
        <DataDrawer
          isOpen={!!facebookDrawer}
          onClose={() => setFacebookDrawer(null)}
          companyName={facebookDrawer.companyName}
          source="facebook"
          data={facebookDrawer.data}
          scrapedAt={facebookDrawer.scrapedAt}
        />
      )}

      {youtubeDrawer && (
        <DataDrawer
          isOpen={!!youtubeDrawer}
          onClose={() => setYoutubeDrawer(null)}
          companyName={youtubeDrawer.companyName}
          source="youtube"
          data={youtubeDrawer.data}
          scrapedAt={youtubeDrawer.scrapedAt}
        />
      )}

      {tiktokDrawer && (
        <DataDrawer
          isOpen={!!tiktokDrawer}
          onClose={() => setTiktokDrawer(null)}
          companyName={tiktokDrawer.companyName}
          source="tiktok"
          data={tiktokDrawer.data}
          scrapedAt={tiktokDrawer.scrapedAt}
        />
      )}

      {threadsDrawer && (
        <DataDrawer
          isOpen={!!threadsDrawer}
          onClose={() => setThreadsDrawer(null)}
          companyName={threadsDrawer.companyName}
          source="threads"
          data={threadsDrawer.data}
          scrapedAt={threadsDrawer.scrapedAt}
        />
      )}

      {xDrawer && (
        <DataDrawer
          isOpen={!!xDrawer}
          onClose={() => setXDrawer(null)}
          companyName={xDrawer.companyName}
          source="x"
          data={xDrawer.data}
          scrapedAt={xDrawer.scrapedAt}
        />
      )}

      {websiteDrawer && (
        <DataDrawer
          isOpen={!!websiteDrawer}
          onClose={() => setWebsiteDrawer(null)}
          companyName={websiteDrawer.companyName}
          source="website"
          data={websiteDrawer.data}
          scrapedAt={websiteDrawer.scrapedAt}
        />
      )}

      {/* Crunchbase Drawer */}
      {crunchbaseDrawer && (
        <DataDrawer
          isOpen={!!crunchbaseDrawer}
          onClose={() => setCrunchbaseDrawer(null)}
          companyName={crunchbaseDrawer.companyName}
          source="crunchbase"
          data={crunchbaseDrawer.data}
          scrapedAt={crunchbaseDrawer.scrapedAt}
        />
      )}

      {/* Footer */}
      <footer className="border-t border-gray-800 px-6 py-4 mt-8">
        <div className="flex justify-between items-center text-sm text-gray-500">
          <div>© 2026 Profitibull. All rights reserved.</div>
          <div className="flex space-x-4">
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
